import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import MemberIDCard from "../components/MemberIDCard";
import {
  LayoutDashboard,
  Users,
  Package,
  Wallet,
  LogOut,
  Menu,
  X,
  Plus,
  Trash2,
  IndianRupee,
  FolderOpen,
  Settings,
  Mail,
  CheckCircle,
  Calculator,
  ShoppingCart,
  Receipt,
  CreditCard,
  Clock,
  Ban,
  Phone,
  Calendar,
  Camera,
  Download,
  FileText,
  Eye,
  Building2,
  Upload,
  RefreshCw,
  AlertTriangle,
  Edit,
  Lock,
  Key,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  IdCard,
  XCircle,
  Store,
  Truck,
  Search,
  MapPin,
  TrendingUp,
  DollarSign,
  Layers,
  UserPlus,
  UserCheck,
  MessageCircle
} from "lucide-react";

const NAV_ITEMS = [
  { path: "/admin", label: "Overview", icon: LayoutDashboard },
  { path: "/admin/payments", label: "Payment Approvals", icon: CreditCard },
  { path: "/admin/orders", label: "Orders", icon: Receipt },
  { path: "/admin/billing", label: "Billing", icon: Receipt },
  { path: "/admin/members", label: "Members", icon: Users },  // Secondary can view but not edit
  { path: "/admin/member-gaps", label: "Member Gaps", icon: AlertTriangle, primaryOnly: true },  // Only primary can fill gaps
  { path: "/admin/repurchase", label: "Monthly Repurchase", icon: RefreshCw },
  { path: "/admin/kyc", label: "KYC Management", icon: FileText },
  { path: "/admin/inventory", label: "Inventory", icon: Package },
  { path: "/admin/offline-purchases", label: "Offline Purchases", icon: ShoppingCart },
  { path: "/admin/categories", label: "Categories", icon: FolderOpen },
  { path: "/admin/products", label: "Products", icon: Package },
  { path: "/admin/subscriptions", label: "Subscriptions", icon: IndianRupee },
  { path: "/admin/payouts", label: "Payouts", icon: Wallet },
  { path: "/admin/settings", label: "Settings", icon: Settings },
];

// Grouped navigation for better organization
// Secondary admin has access to all EXCEPT member editing (Member Gaps is primary only)
const NAV_GROUPS = [
  {
    label: "Dashboard",
    items: [
      { path: "/admin", label: "Overview", icon: LayoutDashboard },
    ]
  },
  {
    label: "Sales & Orders",
    items: [
      { path: "/admin/billing", label: "Billing", icon: Receipt },
      { path: "/admin/orders", label: "Orders", icon: Receipt },
      { path: "/admin/registration-requests", label: "Registration Requests", icon: UserPlus },
      { path: "/admin/payments", label: "Payment Approvals", icon: CreditCard },
    ]
  },
  {
    label: "Members",
    items: [
      { path: "/admin/members", label: "All Members", icon: Users },  // Secondary can view
      { path: "/admin/members-by-plan", label: "Members by Plan", icon: Layers },
      { path: "/admin/members-report", label: "Members Report", icon: FileText },  // New report feature
      { path: "/admin/member-gaps", label: "Member Gaps", icon: AlertTriangle, primaryOnly: true },  // Primary only
      { path: "/admin/kyc", label: "KYC Management", icon: FileText },
      { path: "/admin/repurchase", label: "Repurchase Status", icon: RefreshCw },
    ]
  },
  {
    label: "Finance",
    items: [
      { path: "/admin/payouts", label: "Payouts", icon: Wallet },
      { path: "/admin/subscriptions", label: "Subscriptions", icon: IndianRupee },
      { path: "/admin/offline-purchases", label: "Offline Purchases", icon: ShoppingCart },
    ]
  },
  {
    label: "Inventory",
    items: [
      { path: "/admin/products", label: "Products", icon: Package },
      { path: "/admin/categories", label: "Categories", icon: FolderOpen },
      { path: "/admin/inventory", label: "Stock Management", icon: Package },
    ]
  },
  {
    label: "Franchise",
    items: [
      { path: "/admin/franchises", label: "Franchise Management", icon: Store },
      { path: "/admin/franchise-activations", label: "Activation Report", icon: UserCheck },
      { path: "/admin/franchise-billing", label: "Franchise Billing", icon: Receipt },
      { path: "/admin/daily-reports", label: "Daily Reports (9PM)", icon: Calendar },
      { path: "/admin/stock-transfers", label: "Stock Transfers", icon: Truck },
      { path: "/admin/stock-orders", label: "Stock Orders", icon: ShoppingCart },
      { path: "/admin/settlements", label: "Settlements", icon: Wallet },
    ]
  },
  {
    label: "Settings",
    items: [
      { path: "/admin/settings", label: "Settings", icon: Settings },
    ]
  }
];

// Secondary admin only sees items with secondary: true or items without primaryOnly
const SECONDARY_NAV_ITEMS = NAV_ITEMS.filter(item => item.secondary || (!item.primaryOnly && !item.secondary));

function Sidebar({ open, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [expandedGroups, setExpandedGroups] = useState(['Dashboard', 'Sales & Orders', 'Members', 'Finance']);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const toggleGroup = (groupLabel) => {
    setExpandedGroups(prev => 
      prev.includes(groupLabel) 
        ? prev.filter(g => g !== groupLabel)
        : [...prev, groupLabel]
    );
  };

  // Get admin type from user context
  const adminType = user?.admin_type || 'primary';
  
  // Filter nav groups based on admin type
  const filteredGroups = NAV_GROUPS.map(group => ({
    ...group,
    items: group.items.filter(item => {
      if (adminType === 'secondary') {
        return item.secondary || (!item.primaryOnly && !item.secondary);
      }
      return true;
    })
  })).filter(group => group.items.length > 0);

  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}

      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-[#0B1120] border-r border-slate-800 z-50 transform transition-transform lg:translate-x-0
        ${open ? "translate-x-0" : "-translate-x-full"}
      `}>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#B5952F] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <div>
                <span className="font-bold text-white">Achraj</span>
                <p className="text-xs text-slate-500">
                  {adminType === 'secondary' ? 'Staff Panel' : 'Admin Panel'}
                </p>
              </div>
            </Link>
            <button className="lg:hidden text-slate-400" onClick={onClose}>
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <nav className="px-4 space-y-2 overflow-y-auto max-h-[calc(100vh-180px)]">
          {filteredGroups.map((group) => (
            <div key={group.label}>
              <button
                onClick={() => toggleGroup(group.label)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-300"
              >
                <span>{group.label}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${expandedGroups.includes(group.label) ? 'rotate-180' : ''}`} />
              </button>
              
              <div className={`space-y-1 overflow-hidden transition-all duration-200 ${expandedGroups.includes(group.label) ? 'max-h-96' : 'max-h-0'}`}>
                {group.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={onClose}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
                        isActive
                          ? "bg-[#D4AF37]/10 text-[#D4AF37] border-r-2 border-[#D4AF37]"
                          : "text-slate-400 hover:text-white hover:bg-slate-800"
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="text-sm">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800 bg-[#0B1120]">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-500/10"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>
    </>
  );
}

// Overview Component
function Overview() {
  const [stats, setStats] = useState(null);
  const [kycStats, setKycStats] = useState(null);
  const [repurchaseStats, setRepurchaseStats] = useState(null);
  const [todayBillings, setTodayBillings] = useState([]);
  const { token, user } = useAuth();
  const adminType = user?.admin_type || 'primary';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, kycRes, repurchaseRes, billingsRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
          axios.get(`${API}/admin/kyc/pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/repurchase`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { members: [] } })),
          axios.get(`${API}/billings?limit=10`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
        ]);
        setStats(statsRes.data);
        
        // KYC Stats - handle array response
        const kycData = Array.isArray(kycRes.data) ? kycRes.data : (kycRes.data?.members || []);
        setKycStats({ members: kycData.slice(0, 5), total: kycData.length });
        
        // Calculate repurchase stats - handle object with members array
        const repData = repurchaseRes.data?.members || (Array.isArray(repurchaseRes.data) ? repurchaseRes.data : []);
        const repurchased = repData.filter(m => m.repurchased === true).length;
        setRepurchaseStats({ total: repData.length, repurchased, pending: repData.length - repurchased });
        
        // Get today's billings
        const today = new Date().toDateString();
        const todayBills = (billingsRes.data || []).filter(b => new Date(b.created_at).toDateString() === today);
        setTodayBillings(todayBills);
      } catch (err) {
        console.error("Error fetching stats:", err);
      }
    };
    fetchData();
  }, [token]);

  // Secondary Admin Dashboard
  if (adminType === 'secondary') {
    const pendingKyc = kycStats?.members?.filter(m => m.kyc_status === 'pending' || !m.kyc_status).length || 0;
    const todayTotal = todayBillings.reduce((sum, b) => sum + (b.total_amount || 0), 0);

    return (
      <div data-testid="admin-overview">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Staff Dashboard</h1>
          <p className="text-slate-400 text-sm">Welcome back! Here&apos;s your daily summary.</p>
        </div>

        {/* Quick Stats for Secondary Admin */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-400 text-xs font-medium">Today&apos;s Billing</p>
                  <p className="text-2xl font-bold text-white">₹{todayTotal.toLocaleString()}</p>
                  <p className="text-blue-400/70 text-xs">{todayBillings.length} bills</p>
                </div>
                <Receipt className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 border-orange-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-400 text-xs font-medium">Pending KYC</p>
                  <p className="text-2xl font-bold text-white">{pendingKyc}</p>
                  <p className="text-orange-400/70 text-xs">Need review</p>
                </div>
                <FileText className="w-8 h-8 text-orange-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-400 text-xs font-medium">Repurchased</p>
                  <p className="text-2xl font-bold text-white">{repurchaseStats?.repurchased || 0}</p>
                  <p className="text-green-400/70 text-xs">This month</p>
                </div>
                <RefreshCw className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-400 text-xs font-medium">Repurchase Pending</p>
                  <p className="text-2xl font-bold text-white">{repurchaseStats?.pending || 0}</p>
                  <p className="text-purple-400/70 text-xs">Members</p>
                </div>
                <Package className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="border-b border-slate-800 pb-3">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Receipt className="w-5 h-5 text-[#D4AF37]" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-3">
                <Link to="/admin/billing">
                  <Button className="w-full bg-[#E65100] hover:bg-[#D84315] text-white h-16 flex-col gap-1">
                    <Receipt className="w-5 h-5" />
                    <span className="text-xs">New Billing</span>
                  </Button>
                </Link>
                <Link to="/admin/kyc">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white h-16 flex-col gap-1">
                    <FileText className="w-5 h-5" />
                    <span className="text-xs">KYC Review</span>
                  </Button>
                </Link>
                <Link to="/admin/repurchase">
                  <Button className="w-full bg-green-600 hover:bg-green-700 text-white h-16 flex-col gap-1">
                    <RefreshCw className="w-5 h-5" />
                    <span className="text-xs">Repurchase</span>
                  </Button>
                </Link>
                <Link to="/admin/inventory">
                  <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white h-16 flex-col gap-1">
                    <Package className="w-5 h-5" />
                    <span className="text-xs">Inventory</span>
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Recent Billings */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="border-b border-slate-800 pb-3">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#D4AF37]" />
                Today&apos;s Billings
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {todayBillings.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-4">No billings today yet</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {todayBillings.slice(0, 5).map((bill) => (
                    <div key={bill.billing_id} className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                      <div>
                        <p className="text-white text-sm font-mono">{bill.billing_id}</p>
                        <p className="text-slate-400 text-xs">{bill.member_name}</p>
                      </div>
                      <p className="text-green-400 font-bold">₹{bill.total_amount?.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pending KYC List */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="border-b border-slate-800">
            <CardTitle className="text-white flex items-center justify-between">
              <span className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                Pending KYC Reviews
              </span>
              <Link to="/admin/kyc">
                <Button size="sm" variant="outline" className="border-slate-700 text-slate-300">
                  View All
                </Button>
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {!kycStats?.members || kycStats.members.length === 0 ? (
              <p className="text-slate-400 text-center py-4">No pending KYC reviews</p>
            ) : (
              <div className="space-y-2">
                {kycStats.members.filter(m => m.kyc_status !== 'verified').slice(0, 5).map((member) => (
                  <div key={member.member_id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded">
                    <div>
                      <p className="text-white font-medium">{member.name}</p>
                      <p className="text-[#D4AF37] text-sm font-mono">{member.member_id}</p>
                    </div>
                    <Badge className={`${
                      member.kyc_status === 'submitted' ? 'bg-blue-500/20 text-blue-400' :
                      member.kyc_status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                      'bg-orange-500/20 text-orange-400'
                    }`}>
                      {member.kyc_status || 'Pending'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Primary Admin Dashboard (existing)
  const statCards = stats ? [
    { title: "Total Members", value: stats.total_members, icon: Users, color: "text-blue-400" },
    { title: "Active Members", value: stats.active_members, icon: CheckCircle, color: "text-green-400" },
    { title: "Payout Active", value: stats.payout_active_members, icon: Wallet, color: "text-purple-400" },
    { title: "Total Products", value: stats.total_products, icon: Package, color: "text-orange-400" },
    { title: "Total Revenue", value: `₹${stats.total_revenue?.toLocaleString()}`, icon: IndianRupee, color: "text-[#D4AF37]" },
    { title: "Commissions Paid", value: `₹${stats.total_commissions?.toLocaleString()}`, icon: Wallet, color: "text-teal-400" },
  ] : [];

  return (
    <div data-testid="admin-overview">
      <h1 className="text-2xl font-bold text-white mb-6">Dashboard Overview</h1>
      
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat, idx) => (
          <Card key={idx} className="bg-slate-900/50 border-slate-800 hover:border-[#D4AF37]/30 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">{stat.title}</p>
                  <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-xl bg-slate-800 ${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Plan-wise breakdown */}
      {stats?.plan_counts && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Members by Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              {Object.entries(stats.plan_counts).map(([plan, count]) => (
                <div key={plan} className="text-center p-4 bg-slate-800 rounded-xl">
                  <p className="text-3xl font-bold text-[#D4AF37]">{count}</p>
                  <p className="text-slate-400">Plan {plan}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Registration Requests Component (pending payment confirmation -> creates member on approve)
function RegistrationRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [approving, setApproving] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [approvedResult, setApprovedResult] = useState(null);
  const { token } = useAuth();

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/registration-requests?status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(res.data.requests || []);
    } catch (err) {
      toast.error("Failed to load registration requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, [token, statusFilter]);

  const approve = async (req) => {
    setApproving(req.id);
    try {
      const res = await axios.post(`${API}/admin/registration-requests/${req.id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApprovedResult(res.data);
      toast.success(res.data.message || "Approved");
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    } finally {
      setApproving(null);
    }
  };

  const reject = async () => {
    if (!rejectTarget) return;
    try {
      await axios.post(`${API}/admin/registration-requests/${rejectTarget.id}/reject`, { reason: rejectReason }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Request ${rejectTarget.request_no} rejected`);
      setRejectTarget(null);
      setRejectReason("");
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    }
  };

  const planName = (p) => ({ A: "Plan A", B: "Plan B", C: "Plan C", D: "Plan D" }[p] || p);

  return (
    <div data-testid="registration-requests-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-white">Registration Requests</h1>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white" data-testid="reg-req-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchRequests} className="border-slate-600 text-slate-300" data-testid="reg-req-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Card className="bg-[#0B1120] border-slate-800">
        <CardContent className="p-0">
          {loading ? (
            <p className="text-slate-400 p-6">Loading...</p>
          ) : requests.length === 0 ? (
            <p className="text-slate-400 p-6" data-testid="reg-req-empty">No {statusFilter} registration requests.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-slate-400 text-sm">
                    <th className="p-4">Ref No.</th>
                    <th className="p-4">Name</th>
                    <th className="p-4">Phone</th>
                    <th className="p-4">Plan / Amount</th>
                    <th className="p-4">Sponsor</th>
                    <th className="p-4">Status</th>
                    <th className="p-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.id} className="border-b border-slate-800/60 text-slate-200 text-sm" data-testid={`reg-req-row-${r.request_no}`}>
                      <td className="p-4 font-bold text-[#D4AF37]">{r.request_no}</td>
                      <td className="p-4">{r.name}</td>
                      <td className="p-4">
                        <a href={`tel:${r.phone}`} className="hover:underline">{r.phone}</a>
                      </td>
                      <td className="p-4">{planName(r.plan_type)} • ₹{r.amount}</td>
                      <td className="p-4 text-[#E65100]">{r.sponsor_id || "-"}</td>
                      <td className="p-4">
                        {r.status === "pending" && <Badge className="bg-yellow-500/20 text-yellow-400">Pending</Badge>}
                        {r.status === "approved" && <Badge className="bg-green-500/20 text-green-400">Approved → {r.member_id}</Badge>}
                        {r.status === "rejected" && <Badge className="bg-red-500/20 text-red-400">Rejected</Badge>}
                      </td>
                      <td className="p-4">
                        {r.status === "pending" ? (
                          <div className="flex items-center justify-end gap-2">
                            <a
                              href={`https://wa.me/91${r.phone}`}
                              target="_blank" rel="noopener noreferrer"
                              className="p-2 rounded-lg bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30"
                              title="WhatsApp this member"
                              data-testid={`reg-req-wa-${r.request_no}`}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </a>
                            <Button
                              size="sm"
                              onClick={() => approve(r)}
                              disabled={approving === r.id}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`reg-req-approve-${r.request_no}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" /> {approving === r.id ? "..." : "Approve"}
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => setRejectTarget(r)}
                              className="border-red-600/50 text-red-400 hover:bg-red-600/10"
                              data-testid={`reg-req-reject-${r.request_no}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-slate-500 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approved success dialog */}
      <Dialog open={!!approvedResult} onOpenChange={(o) => !o && setApprovedResult(null)}>
        <DialogContent className="bg-[#0B1120] border-slate-800 text-white" data-testid="reg-req-approved-dialog">
          <DialogHeader>
            <DialogTitle className="text-green-400 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" /> Member Created
            </DialogTitle>
          </DialogHeader>
          {approvedResult && (
            <div className="space-y-4">
              <div className="bg-slate-800/60 rounded-xl p-4 space-y-2">
                <div className="flex justify-between"><span className="text-slate-400">Member ID</span><span className="font-bold text-[#D4AF37]">{approvedResult.member_id}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Name</span><span className="font-semibold">{approvedResult.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Password</span><span className="font-semibold text-blue-300">{approvedResult.password}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Credits added</span><span className="font-semibold text-green-400">₹{approvedResult.credits}</span></div>
              </div>
              <p className="text-sm text-slate-400">
                Share the login details and ID card with the member on WhatsApp. Download the ID card from the
                <strong className="text-slate-300"> All Members</strong> page, then attach it in the chat.
              </p>
              <a
                href={`https://wa.me/91${approvedResult.phone}?text=${encodeURIComponent(`Badhai ho! Aapki Achraj registration confirm ho gayi hai.\nMember ID: ${approvedResult.member_id}\nPassword: ${approvedResult.password}\nWallet Credits: ₹${approvedResult.credits}\nLogin: apne Member ID aur password se login karein. Aapka ID card bhej rahe hain.`)}`}
                target="_blank" rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold rounded-lg py-2.5"
                data-testid="reg-req-send-credentials-wa"
              >
                <MessageCircle className="w-4 h-4" /> Send Details on WhatsApp
              </a>
              <Button variant="outline" className="w-full border-slate-600 text-slate-300" onClick={() => setApprovedResult(null)}>
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="bg-[#0B1120] border-slate-800 text-white" data-testid="reg-req-reject-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-400">Reject Request {rejectTarget?.request_no}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">Reject the registration of <strong className="text-white">{rejectTarget?.name}</strong>? This cannot be approved later.</p>
            <div>
              <Label className="text-slate-300">Reason (optional)</Label>
              <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. payment not received" className="bg-slate-800 border-slate-700 text-white" data-testid="reg-req-reject-reason" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 border-slate-600 text-slate-300" onClick={() => setRejectTarget(null)}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={reject} data-testid="reg-req-confirm-reject">Reject</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Franchise Activation Report — members activated by a franchise (created or billed)
function FranchiseActivations() {
  const [data, setData] = useState({ activations: [], count: 0, created_count: 0, billed_count: 0, franchises: [] });
  const [loading, setLoading] = useState(true);
  const [franchiseCode, setFranchiseCode] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const { token } = useAuth();

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (franchiseCode !== "all") params.append("franchise_code", franchiseCode);
      if (typeFilter !== "all") params.append("activation_type", typeFilter);
      const res = await axios.get(`${API}/admin/franchise-activations?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load activation report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [token, franchiseCode, typeFilter]);

  const fmtDate = (s) => {
    if (!s) return "-";
    try { return new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return s; }
  };

  return (
    <div data-testid="franchise-activations-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Franchise Activation Report</h1>
          <p className="text-slate-400 text-sm">Members created or activated through a franchise</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={franchiseCode} onValueChange={setFranchiseCode}>
            <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white" data-testid="fa-franchise-filter">
              <SelectValue placeholder="All Franchises" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Franchises</SelectItem>
              {data.franchises?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white" data-testid="fa-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="created">Created</SelectItem>
              <SelectItem value="billed">Activated by Bill</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData} className="border-slate-600 text-slate-300" data-testid="fa-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-[#0B1120] border-slate-800"><CardContent className="p-4"><p className="text-slate-400 text-sm">Total Activations</p><p className="text-2xl font-bold text-white" data-testid="fa-total">{data.count}</p></CardContent></Card>
        <Card className="bg-[#0B1120] border-slate-800"><CardContent className="p-4"><p className="text-slate-400 text-sm">Created by Franchise</p><p className="text-2xl font-bold text-purple-400">{data.created_count}</p></CardContent></Card>
        <Card className="bg-[#0B1120] border-slate-800"><CardContent className="p-4"><p className="text-slate-400 text-sm">Activated via Bill</p><p className="text-2xl font-bold text-green-400">{data.billed_count}</p></CardContent></Card>
      </div>

      <Card className="bg-[#0B1120] border-slate-800">
        <CardContent className="p-0">
          {loading ? (
            <p className="text-slate-400 p-6">Loading...</p>
          ) : data.activations.length === 0 ? (
            <p className="text-slate-400 p-6" data-testid="fa-empty">No franchise activations found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-slate-400 text-sm">
                    <th className="p-4">Member ID</th>
                    <th className="p-4">Name</th>
                    <th className="p-4">Phone</th>
                    <th className="p-4">Plan</th>
                    <th className="p-4">Franchise</th>
                    <th className="p-4">Type</th>
                    <th className="p-4">Activated On</th>
                  </tr>
                </thead>
                <tbody>
                  {data.activations.map((a) => (
                    <tr key={a.member_id} className="border-b border-slate-800/60 text-slate-200 text-sm" data-testid={`fa-row-${a.member_id}`}>
                      <td className="p-4 font-bold text-[#D4AF37]">{a.member_id}</td>
                      <td className="p-4">{a.name}</td>
                      <td className="p-4"><a href={`tel:${a.phone}`} className="hover:underline">{a.phone}</a></td>
                      <td className="p-4">Plan {a.plan_type}</td>
                      <td className="p-4">
                        <span className="text-[#E65100] font-medium">{a.franchise_code}</span>
                        {a.franchise_name && <span className="text-slate-500 text-xs block">{a.franchise_name}</span>}
                      </td>
                      <td className="p-4">
                        {a.activation_type === "created"
                          ? <Badge className="bg-purple-500/20 text-purple-400">Created</Badge>
                          : <Badge className="bg-green-500/20 text-green-400">Billed</Badge>}
                      </td>
                      <td className="p-4 text-slate-400">{fmtDate(a.activated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}



// Payment Approvals Component
function PaymentApprovals() {
  const [pendingMembers, setPendingMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    payment_mode: "cash",
    payment_reference: "",
    payment_amount: "",
    notes: ""
  });
  const [viewMode, setViewMode] = useState("pending"); // pending, approved, rejected
  const { token } = useAuth();

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API}/admin/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const members = res.data;
      setAllMembers(members);
      setPendingMembers(members.filter(m => !m.is_active && m.payment_status !== "rejected"));
    } catch (err) {
      console.error("Error fetching members:", err);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [token]);

  const approvePayment = async () => {
    if (!selectedMember) return;
    
    try {
      // Update payment details first
      await axios.put(`${API}/admin/members/${selectedMember.member_id}`, {
        payment_status: "approved",
        payment_mode: paymentDetails.payment_mode,
        payment_reference: paymentDetails.payment_reference,
        payment_amount: parseFloat(paymentDetails.payment_amount) || 0,
        payment_date: new Date().toISOString(),
        payment_notes: paymentDetails.notes,
        is_active: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then activate the member (this also sets subscription_status)
      try {
        await axios.post(`${API}/admin/members/${selectedMember.member_id}/activate`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (activateErr) {
        // Member might already be active, that's okay
        console.log("Activate note:", activateErr.response?.data?.detail || "Already active");
      }
      
      toast.success(`Payment approved for ${selectedMember.member_id}. Account activated!`);
      setPaymentDialogOpen(false);
      setSelectedMember(null);
      setPaymentDetails({ payment_mode: "cash", payment_reference: "", payment_amount: "", notes: "" });
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve payment");
    }
  };

  const rejectPayment = async () => {
    if (!selectedMember) return;
    
    try {
      await axios.put(`${API}/admin/members/${selectedMember.member_id}`, {
        payment_status: "rejected",
        is_active: false,
        rejection_reason: paymentDetails.notes,
        rejection_date: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Payment rejected for ${selectedMember.member_id}`);
      setRejectDialogOpen(false);
      setSelectedMember(null);
      setPaymentDetails({ payment_mode: "cash", payment_reference: "", payment_amount: "", notes: "" });
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject payment");
    }
  };

  const getPlanAmount = (plan) => {
    const amounts = { "D": 500, "C": 1000, "B": 2000, "A": 5000 };
    return amounts[plan] || 500;
  };

  const filteredMembers = () => {
    switch (viewMode) {
      case "pending":
        return allMembers.filter(m => !m.is_active && m.payment_status !== "rejected");
      case "approved":
        return allMembers.filter(m => m.is_active || m.payment_status === "approved");
      case "rejected":
        return allMembers.filter(m => m.payment_status === "rejected");
      default:
        return allMembers;
    }
  };

  const pendingCount = allMembers.filter(m => !m.is_active && m.payment_status !== "rejected").length;
  const approvedCount = allMembers.filter(m => m.is_active || m.payment_status === "approved").length;
  const rejectedCount = allMembers.filter(m => m.payment_status === "rejected").length;

  return (
    <div data-testid="admin-payments">
      <h1 className="text-2xl font-bold text-white mb-6">Payment Approvals</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card 
          className={`cursor-pointer transition-all ${viewMode === "pending" ? "bg-yellow-500/20 border-yellow-500" : "bg-slate-900/50 border-slate-800 hover:border-yellow-500/50"}`}
          onClick={() => setViewMode("pending")}
        >
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-yellow-400">{pendingCount}</p>
            <p className="text-sm text-slate-400">Pending Approval</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all ${viewMode === "approved" ? "bg-green-500/20 border-green-500" : "bg-slate-900/50 border-slate-800 hover:border-green-500/50"}`}
          onClick={() => setViewMode("approved")}
        >
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-green-400">{approvedCount}</p>
            <p className="text-sm text-slate-400">Approved</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all ${viewMode === "rejected" ? "bg-red-500/20 border-red-500" : "bg-slate-900/50 border-slate-800 hover:border-red-500/50"}`}
          onClick={() => setViewMode("rejected")}
        >
          <CardContent className="p-4 text-center">
            <Ban className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-red-400">{rejectedCount}</p>
            <p className="text-sm text-slate-400">Rejected</p>
          </CardContent>
        </Card>
      </div>

      {/* Members Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            {viewMode === "pending" && <><Clock className="w-5 h-5 text-yellow-400" /> Pending Payments</>}
            {viewMode === "approved" && <><CheckCircle className="w-5 h-5 text-green-400" /> Approved Payments</>}
            {viewMode === "rejected" && <><Ban className="w-5 h-5 text-red-400" /> Rejected Payments</>}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800">
                <TableHead className="text-slate-400">Member ID</TableHead>
                <TableHead className="text-slate-400">Name</TableHead>
                <TableHead className="text-slate-400">Phone</TableHead>
                <TableHead className="text-slate-400">Sponsor</TableHead>
                <TableHead className="text-slate-400">Plan</TableHead>
                <TableHead className="text-slate-400">Amount</TableHead>
                <TableHead className="text-slate-400">Registered</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMembers().length > 0 ? filteredMembers().map((member) => (
                <TableRow key={member.id} className="border-slate-800">
                  <TableCell className="text-[#D4AF37] font-bold font-mono">{member.member_id}</TableCell>
                  <TableCell className="text-white">{member.name}</TableCell>
                  <TableCell className="text-slate-300">
                    <a href={`tel:${member.phone}`} className="flex items-center gap-1 hover:text-[#D4AF37]">
                      <Phone className="w-3 h-3" /> {member.phone}
                    </a>
                  </TableCell>
                  <TableCell className="text-slate-300">{member.sponsor_id || "-"}</TableCell>
                  <TableCell>
                    <Badge className="bg-[#E65100]/20 text-[#E65100]">Plan {member.plan}</Badge>
                  </TableCell>
                  <TableCell className="text-green-400 font-bold">₹{getPlanAmount(member.plan).toLocaleString()}</TableCell>
                  <TableCell className="text-slate-400 text-sm">
                    {new Date(member.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {member.is_active || member.payment_status === "approved" ? (
                      <Badge className="bg-green-500/20 text-green-400">✓ Active</Badge>
                    ) : member.payment_status === "rejected" ? (
                      <Badge className="bg-red-500/20 text-red-400">✗ Rejected</Badge>
                    ) : (
                      <Badge className="bg-yellow-500/20 text-yellow-400">⏳ Pending</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {viewMode === "pending" && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => { setSelectedMember(member); setPaymentDetails({...paymentDetails, payment_amount: getPlanAmount(member.plan).toString()}); setPaymentDialogOpen(true); }}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`approve-payment-${member.member_id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedMember(member); setRejectDialogOpen(true); }}
                          className="text-red-400 border-red-400/30 hover:bg-red-500/10"
                        >
                          <Ban className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    {viewMode === "approved" && member.payment_date && (
                      <span className="text-xs text-slate-400">
                        {member.payment_mode?.toUpperCase()} • {new Date(member.payment_date).toLocaleDateString()}
                      </span>
                    )}
                    {viewMode === "rejected" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedMember(member); setPaymentDetails({...paymentDetails, payment_amount: getPlanAmount(member.plan).toString()}); setPaymentDialogOpen(true); }}
                        className="text-green-400 border-green-400/30 hover:bg-green-500/10"
                      >
                        Re-approve
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-slate-400 py-8">
                    {viewMode === "pending" && "No pending payments"}
                    {viewMode === "approved" && "No approved members yet"}
                    {viewMode === "rejected" && "No rejected payments"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Approve Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" /> Approve Payment
            </DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">Member ID</p>
                    <p className="text-[#D4AF37] font-bold">{selectedMember.member_id}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Name</p>
                    <p className="text-white">{selectedMember.name}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Phone</p>
                    <p className="text-white">{selectedMember.phone}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Plan</p>
                    <p className="text-[#E65100] font-semibold">Plan {selectedMember.plan} (₹{getPlanAmount(selectedMember.plan)})</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-slate-300">Payment Mode *</Label>
                  <Select value={paymentDetails.payment_mode} onValueChange={(v) => setPaymentDetails({...paymentDetails, payment_mode: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="bank">Bank Transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="online">Online Payment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Amount Received (₹) *</Label>
                  <Input
                    type="number"
                    value={paymentDetails.payment_amount}
                    onChange={(e) => setPaymentDetails({...paymentDetails, payment_amount: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="Enter amount"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Reference/Transaction ID</Label>
                  <Input
                    value={paymentDetails.payment_reference}
                    onChange={(e) => setPaymentDetails({...paymentDetails, payment_reference: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="UPI ID, Cheque No., etc."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Notes</Label>
                  <Textarea
                    value={paymentDetails.notes}
                    onChange={(e) => setPaymentDetails({...paymentDetails, notes: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>

              <Button onClick={approvePayment} className="w-full bg-green-600 hover:bg-green-700 text-white">
                <CheckCircle className="w-4 h-4 mr-2" /> Approve & Activate Account
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Payment Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-400" /> Reject Payment
            </DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-red-400 font-semibold">Are you sure you want to reject this payment?</p>
                <p className="text-slate-400 text-sm mt-1">
                  Member: <span className="text-white">{selectedMember.member_id} - {selectedMember.name}</span>
                </p>
              </div>

              <div>
                <Label className="text-slate-300">Rejection Reason</Label>
                <Textarea
                  value={paymentDetails.notes}
                  onChange={(e) => setPaymentDetails({...paymentDetails, notes: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="Reason for rejection..."
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setRejectDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={rejectPayment} className="flex-1 bg-red-600 hover:bg-red-700 text-white">
                  <Ban className="w-4 h-4 mr-2" /> Reject Payment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Orders Management
function OrdersManagement() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const { token } = useAuth();

  const fetchOrders = async () => {
    try {
      const query = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const [ordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/orders${query}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/orders/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Error:", err);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [token, statusFilter]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;
    try {
      await axios.delete(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Order deleted");
      fetchOrders();
    } catch (err) {
      toast.error("Failed to delete order");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-500/20 text-yellow-400",
      confirmed: "bg-blue-500/20 text-blue-400",
      processing: "bg-purple-500/20 text-purple-400",
      shipped: "bg-cyan-500/20 text-cyan-400",
      delivered: "bg-green-500/20 text-green-400",
      cancelled: "bg-red-500/20 text-red-400"
    };
    return colors[status] || "bg-slate-500/20 text-slate-400";
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="text-2xl font-bold text-white mb-6">Orders Management</h1>

      {/* Order Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-white">{stats.total_orders}</p>
              <p className="text-xs text-slate-400">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-400">{stats.pending_orders}</p>
              <p className="text-xs text-slate-400">Pending</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-400">{stats.confirmed_orders}</p>
              <p className="text-xs text-slate-400">Confirmed</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-500/10 border-purple-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-400">{stats.processing_orders}</p>
              <p className="text-xs text-slate-400">Processing</p>
            </CardContent>
          </Card>
          <Card className="bg-cyan-500/10 border-cyan-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-cyan-400">{stats.shipped_orders}</p>
              <p className="text-xs text-slate-400">Shipped</p>
            </CardContent>
          </Card>
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-400">{stats.delivered_orders}</p>
              <p className="text-xs text-slate-400">Delivered</p>
            </CardContent>
          </Card>
          <Card className="bg-[#D4AF37]/10 border-[#D4AF37]/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#D4AF37]">₹{stats.total_revenue?.toLocaleString()}</p>
              <p className="text-xs text-slate-400">Revenue</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-4 mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Orders</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800">
                <TableHead className="text-slate-400">Order ID</TableHead>
                <TableHead className="text-slate-400">Customer</TableHead>
                <TableHead className="text-slate-400">Phone</TableHead>
                <TableHead className="text-slate-400">Items</TableHead>
                <TableHead className="text-slate-400">Amount</TableHead>
                <TableHead className="text-slate-400">Payment</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Date</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length > 0 ? orders.map((order) => (
                <TableRow key={order.id} className="border-slate-800">
                  <TableCell className="text-[#D4AF37] font-mono font-bold">{order.order_id || "—"}</TableCell>
                  <TableCell className="text-white">{order.customer_name || order.member_name || "—"}{order.member_id ? ` (${order.member_id})` : ""}</TableCell>
                  <TableCell className="text-slate-300">{order.customer_phone || "—"}</TableCell>
                  <TableCell className="text-slate-300">{order.total_items || (order.items?.length || 0)} items</TableCell>
                  <TableCell className="text-green-400 font-bold">₹{order.total_amount?.toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge className="bg-slate-700 text-slate-300">{order.payment_mode?.toUpperCase()}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(order.status)}>{order.status}</Badge>
                  </TableCell>
                  <TableCell className="text-slate-400 text-sm">
                    {new Date(order.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedOrder(order); setDetailsOpen(true); }}
                        className="text-blue-400 border-blue-400/30 hover:bg-blue-500/10"
                      >
                        View
                      </Button>
                      <Select
                        value={order.status}
                        onValueChange={(val) => updateStatus(order.order_id, val)}
                      >
                        <SelectTrigger className="w-28 h-8 text-xs bg-slate-800 border-slate-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirm</SelectItem>
                          <SelectItem value="processing">Process</SelectItem>
                          <SelectItem value="shipped">Ship</SelectItem>
                          <SelectItem value="delivered">Deliver</SelectItem>
                          <SelectItem value="cancelled">Cancel</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteOrder(order.order_id)}
                        className="text-red-400 border-red-400/30 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-slate-400 py-8">
                    No orders found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Order Details - {selectedOrder?.order_id}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="text-[#D4AF37] font-semibold mb-3">Customer Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">Name</p>
                    <p className="text-white font-medium">{selectedOrder.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Phone</p>
                    <p className="text-white font-medium">{selectedOrder.customer_phone}</p>
                  </div>
                  {selectedOrder.customer_email && (
                    <div>
                      <p className="text-slate-400">Email</p>
                      <p className="text-white font-medium">{selectedOrder.customer_email}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-slate-400">Payment Mode</p>
                    <p className="text-white font-medium">{selectedOrder.payment_mode?.toUpperCase()}</p>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="text-[#D4AF37] font-semibold mb-3">Shipping Address</h4>
                <p className="text-white">{selectedOrder.customer_address}</p>
                <p className="text-slate-300">
                  {selectedOrder.city}, {selectedOrder.state} - {selectedOrder.pincode}
                </p>
              </div>

              {/* Order Items */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="text-[#D4AF37] font-semibold mb-3">Order Items</h4>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 bg-slate-700/50 rounded-lg p-3">
                      {item.image_url && (
                        <img src={item.image_url} alt={item.product_name} className="w-16 h-16 object-cover rounded" />
                      )}
                      <div className="flex-1">
                        <p className="text-white font-medium">{item.product_name}</p>
                        <p className="text-slate-400 text-sm">Qty: {item.quantity} × ₹{item.price}</p>
                      </div>
                      <p className="text-green-400 font-bold">₹{(item.quantity * item.price).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-slate-700 mt-4 pt-4 flex justify-between">
                  <p className="text-white font-semibold">Total Amount</p>
                  <p className="text-[#D4AF37] text-xl font-bold">₹{selectedOrder.total_amount?.toLocaleString()}</p>
                </div>
              </div>

              {/* Notes */}
              {selectedOrder.notes && (
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-[#D4AF37] font-semibold mb-2">Order Notes</h4>
                  <p className="text-slate-300">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Status & Date */}
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="text-slate-400">Status: </span>
                  <Badge className={getStatusColor(selectedOrder.status)}>{selectedOrder.status}</Badge>
                </div>
                <div className="text-slate-400">
                  Ordered: {new Date(selectedOrder.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Members Report - Subscription Tracking Report
function MembersReport() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSubscription, setFilterSubscription] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [exporting, setExporting] = useState(false);
  const [updating, setUpdating] = useState(null);
  const { token } = useAuth();

  const fetchMembers = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/members-report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(res.data || []);
    } catch (err) {
      console.error("Error fetching report:", err);
      toast.error("Failed to load members report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [token]);

  // Get member number from ID
  const getMemberNumber = (mid) => {
    try {
      return parseInt(mid.replace('ACJ', '').replace(/^0+/, '') || '0');
    } catch {
      return 0;
    }
  };

  // Determine join info based on ID ranges
  // ID 1-321: November 2025
  // ID 322-561: December 2025
  // ID 562-922: January 2026
  // ID 923+: February 2026
  const getJoinInfo = (member) => {
    const num = getMemberNumber(member.member_id);
    if (num >= 1 && num <= 321) {
      return { month: 'November', year: 2025, key: '2025-11', batch: 'nov' };
    }
    if (num >= 322 && num <= 561) {
      return { month: 'December', year: 2025, key: '2025-12', batch: 'dec' };
    }
    if (num >= 562 && num <= 922) {
      return { month: 'January', year: 2026, key: '2026-01', batch: 'jan' };
    }
    // ID 923+ or from database
    if (member.created_at) {
      try {
        const date = new Date(member.created_at);
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        return { 
          month: monthNames[date.getMonth()], 
          year: date.getFullYear(),
          key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
          batch: 'feb'
        };
      } catch {
        return { month: 'February', year: 2026, key: '2026-02', batch: 'feb' };
      }
    }
    return { month: 'February', year: 2026, key: '2026-02', batch: 'feb' };
  };

  // Get due months based on batch (only for active members)
  // November batch (ID 1-321): Nov, Dec, Jan due
  // December batch (ID 322-561): Dec, Jan, Feb due
  // January batch (ID 562-922): Jan, Feb due
  // February batch (ID 923+): Feb due
  const getDueMonths = (member) => {
    const joinInfo = getJoinInfo(member);
    const dueMonths = [];
    
    // Only active members have due months
    if (!member.is_active) {
      return { dueMonths: [], batch: joinInfo.batch, joinInfo };
    }
    
    if (joinInfo.batch === 'nov') {
      // November batch: Nov 2025, Dec 2025, Jan 2026
      dueMonths.push({ key: '2025-11', label: 'Nov 2025' });
      dueMonths.push({ key: '2025-12', label: 'Dec 2025' });
      dueMonths.push({ key: '2026-01', label: 'Jan 2026' });
    } else if (joinInfo.batch === 'dec') {
      // December batch: Dec 2025, Jan 2026, Feb 2026
      dueMonths.push({ key: '2025-12', label: 'Dec 2025' });
      dueMonths.push({ key: '2026-01', label: 'Jan 2026' });
      dueMonths.push({ key: '2026-02', label: 'Feb 2026' });
    } else if (joinInfo.batch === 'jan') {
      // January batch: Jan 2026, Feb 2026
      dueMonths.push({ key: '2026-01', label: 'Jan 2026' });
      dueMonths.push({ key: '2026-02', label: 'Feb 2026' });
    } else {
      // February batch: Feb 2026
      dueMonths.push({ key: '2026-02', label: 'Feb 2026' });
    }
    
    return { dueMonths, batch: joinInfo.batch, joinInfo };
  };

  // Get subscription data for member
  const getSubscriptionData = (member) => {
    const { dueMonths, joinInfo } = getDueMonths(member);
    const paidMonths = [];
    const pendingMonths = [];
    
    // Check repurchase_history (format: {'2026-02': True})
    const repurchaseHistory = member.repurchase_history || {};
    
    dueMonths.forEach(m => {
      if (repurchaseHistory[m.key] === true || repurchaseHistory[m.key] === 'true') {
        paidMonths.push(m);
      } else {
        pendingMonths.push(m);
      }
    });
    
    return {
      joinMonth: `${joinInfo.month} ${joinInfo.year}`,
      dueMonths,
      paidMonths,
      pendingMonths,
      totalDue: dueMonths.length,
      totalPaid: paidMonths.length,
      totalPending: pendingMonths.length
    };
  };

  // Update repurchase status for a member
  const updateRepurchaseStatus = async (memberId, monthKey, status) => {
    setUpdating(`${memberId}-${monthKey}`);
    try {
      await axios.put(`${API}/admin/members/${memberId}/repurchase`, {
        month_key: monthKey,
        status: status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Updated ${monthKey} status for ${memberId}`);
      fetchMembers(); // Refresh data
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdating(null);
    }
  };

  // Filter members
  const filteredMembers = members.filter(m => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!m.member_id?.toLowerCase().includes(query) &&
          !m.name?.toLowerCase().includes(query) &&
          !m.phone?.includes(query)) {
        return false;
      }
    }
    
    if (filterStatus === 'active' && !m.is_active) return false;
    if (filterStatus === 'inactive' && m.is_active) return false;
    
    const subData = getSubscriptionData(m);
    if (filterSubscription === 'paid' && subData.totalPending > 0) return false;
    if (filterSubscription === 'pending' && subData.totalPending === 0) return false;
    
    const joinMonth = getJoinInfo(m).month.toLowerCase();
    if (filterMonth === 'november' && joinMonth !== 'november') return false;
    if (filterMonth === 'december' && joinMonth !== 'december') return false;
    if (filterMonth === 'january' && joinMonth !== 'january') return false;
    if (filterMonth === 'february' && joinMonth !== 'february') return false;
    
    return true;
  });

  // Sort by member number
  const sortedMembers = [...filteredMembers].sort((a, b) => 
    getMemberNumber(a.member_id) - getMemberNumber(b.member_id)
  );

  // Export to CSV
  const exportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/members/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 120000
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Achraj_Subscription_Report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Report exported successfully!");
    } catch (err) {
      toast.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  // Stats
  const stats = {
    total: members.length,
    active: members.filter(m => m.is_active).length,
    inactive: members.filter(m => !m.is_active).length,
    fullPaid: members.filter(m => m.is_active && getSubscriptionData(m).totalPending === 0).length,
    hasPending: members.filter(m => m.is_active && getSubscriptionData(m).totalPending > 0).length,
    november: members.filter(m => getJoinInfo(m).batch === 'nov').length,
    december: members.filter(m => getJoinInfo(m).batch === 'dec').length,
    january: members.filter(m => getJoinInfo(m).batch === 'jan').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E65100]"></div>
      </div>
    );
  }

  return (
    <div data-testid="members-report">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Subscription Report</h1>
          <p className="text-slate-400 text-sm">Monthly Subscription Status by Member</p>
        </div>
        <Button 
          onClick={exportCSV}
          disabled={exporting}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="export-report-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          {exporting ? 'Exporting...' : 'Download CSV Report'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-xs text-slate-400">Total</p>
          </CardContent>
        </Card>
        <Card className="bg-green-900/30 border-green-700/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-400">{stats.active}</p>
            <p className="text-xs text-green-400/70">Active</p>
          </CardContent>
        </Card>
        <Card className="bg-red-900/30 border-red-700/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-400">{stats.inactive}</p>
            <p className="text-xs text-red-400/70">Inactive</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-900/30 border-blue-700/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-400">{stats.fullPaid}</p>
            <p className="text-xs text-blue-400/70">All Paid</p>
          </CardContent>
        </Card>
        <Card className="bg-orange-900/30 border-orange-700/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-400">{stats.hasPending}</p>
            <p className="text-xs text-orange-400/70">Has Pending</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-900/30 border-purple-700/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-400">{stats.november}</p>
            <p className="text-xs text-purple-400/70">ID 1-321</p>
          </CardContent>
        </Card>
        <Card className="bg-pink-900/30 border-pink-700/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-pink-400">{stats.december}</p>
            <p className="text-xs text-pink-400/70">ID 322-561</p>
          </CardContent>
        </Card>
        <Card className="bg-cyan-900/30 border-cyan-700/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-cyan-400">{stats.january}</p>
            <p className="text-xs text-cyan-400/70">ID 562-922</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search by ID, name, phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px] bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="inactive">Inactive Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterSubscription} onValueChange={setFilterSubscription}>
              <SelectTrigger className="w-[180px] bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Subscription" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subscriptions</SelectItem>
                <SelectItem value="paid">All Months Paid</SelectItem>
                <SelectItem value="pending">Has Pending</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger className="w-[150px] bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Join Batch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Batches</SelectItem>
                <SelectItem value="november">Nov (1-321)</SelectItem>
                <SelectItem value="december">Dec (322-561)</SelectItem>
                <SelectItem value="january">Jan (562-922)</SelectItem>
                <SelectItem value="february">Feb (923+)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-sm text-slate-400 mt-3">
            Showing {sortedMembers.length} of {members.length} members | <span className="text-yellow-400">Click on pending months to mark as paid</span>
          </p>
        </CardContent>
      </Card>

      {/* Members Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700 bg-slate-900/50">
                  <th className="text-left p-4 text-slate-400 font-medium text-sm">Sr.</th>
                  <th className="text-left p-4 text-slate-400 font-medium text-sm">Member ID</th>
                  <th className="text-left p-4 text-slate-400 font-medium text-sm">Name</th>
                  <th className="text-left p-4 text-slate-400 font-medium text-sm">Phone</th>
                  <th className="text-left p-4 text-slate-400 font-medium text-sm">Plan</th>
                  <th className="text-center p-4 text-slate-400 font-medium text-sm">Status</th>
                  <th className="text-left p-4 text-slate-400 font-medium text-sm">Join Batch</th>
                  <th className="text-left p-4 text-slate-400 font-medium text-sm">Subscribed Months</th>
                  <th className="text-center p-4 text-slate-400 font-medium text-sm">Paid</th>
                  <th className="text-left p-4 text-slate-400 font-medium text-sm">Pending Months (Click to Update)</th>
                  <th className="text-center p-4 text-slate-400 font-medium text-sm">Pending</th>
                </tr>
              </thead>
              <tbody>
                {sortedMembers.length > 0 ? sortedMembers.map((member, idx) => {
                  const subData = getSubscriptionData(member);
                  
                  return (
                    <tr key={member.member_id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="p-4 text-slate-400 text-sm">{idx + 1}</td>
                      <td className="p-4">
                        <span className="text-[#E65100] font-mono font-semibold">{member.member_id}</span>
                      </td>
                      <td className="p-4 text-white">{member.name}</td>
                      <td className="p-4 text-slate-300">{member.phone}</td>
                      <td className="p-4">
                        <Badge className={
                          member.plan_type === 'A' ? 'bg-yellow-500/20 text-yellow-400' :
                          member.plan_type === 'B' ? 'bg-purple-500/20 text-purple-400' :
                          member.plan_type === 'C' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-slate-500/20 text-slate-400'
                        }>
                          {member.plan_type || 'D'}
                        </Badge>
                      </td>
                      <td className="p-4 text-center">
                        {member.is_active ? (
                          <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                            Inactive
                          </Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-purple-400 font-medium">{subData.joinMonth}</span>
                      </td>
                      <td className="p-4">
                        {subData.paidMonths.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {subData.paidMonths.map((m, i) => (
                              <Badge 
                                key={i} 
                                className="bg-green-500/20 text-green-400 text-xs cursor-pointer hover:bg-red-500/20 hover:text-red-400"
                                onClick={() => updateRepurchaseStatus(member.member_id, m.key, false)}
                                title={`Click to mark ${m.label} as unpaid`}
                              >
                                {updating === `${member.member_id}-${m.key}` ? '...' : m.label} ✓
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-500 text-sm">{member.is_active ? 'None' : 'N/A'}</span>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        <span className="text-green-400 font-bold text-lg">{subData.totalPaid}</span>
                      </td>
                      <td className="p-4">
                        {!member.is_active ? (
                          <span className="text-slate-500 text-sm">Activate member first</span>
                        ) : subData.pendingMonths.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {subData.pendingMonths.map((m, i) => (
                              <Badge 
                                key={i} 
                                className="bg-red-500/20 text-red-400 text-xs cursor-pointer hover:bg-green-500/20 hover:text-green-400 transition-colors"
                                onClick={() => updateRepurchaseStatus(member.member_id, m.key, true)}
                                title={`Click to mark ${m.label} as paid`}
                              >
                                {updating === `${member.member_id}-${m.key}` ? '...' : m.label}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-green-400 text-sm">All Paid ✓</span>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        {subData.totalPending > 0 ? (
                          <span className="text-red-400 font-bold text-lg">{subData.totalPending}</span>
                        ) : (
                          <span className="text-green-400 font-bold text-lg">0</span>
                        )}
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan={11} className="p-8 text-center text-slate-400">
                      No members found matching your filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Members Management
function MembersManagement() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMember, setNewMember] = useState({
    name: "", email: "", phone: "", plan_type: "D", sponsor_id: "", address: "", member_id: "", photo_url: "", is_management: false
  });
  const [editMember, setEditMember] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [resetPasswordMember, setResetPasswordMember] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [showIdCard, setShowIdCard] = useState(false);
  const [registeredMember, setRegisteredMember] = useState(null);
  const { token, user } = useAuth();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const pageSize = 50; // Members per page
  
  // Sponsored members modal state
  const [showSponsoredModal, setShowSponsoredModal] = useState(false);
  const [sponsoredData, setSponsoredData] = useState(null);
  const [loadingSponsored, setLoadingSponsored] = useState(false);

  const openResetPasswordDialog = (member) => {
    setResetPasswordMember(member);
    setNewPassword(member.phone?.slice(-4) || '');
    setResetPasswordDialog(true);
  };

  // Fetch sponsored members for a specific member
  const fetchSponsoredMembers = async (memberId) => {
    setLoadingSponsored(true);
    try {
      const res = await axios.get(`${API}/admin/members/${memberId}/sponsored`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSponsoredData(res.data);
      setShowSponsoredModal(true);
    } catch (err) {
      toast.error("Failed to load sponsored members");
    } finally {
      setLoadingSponsored(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordMember || !newPassword) return;
    
    if (newPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }

    setResettingPassword(true);
    try {
      await axios.put(`${API}/admin/members/${resetPasswordMember.member_id}/reset-password`, {
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Password reset for ${resetPasswordMember.member_id}. New password: ${newPassword}`);
      setResetPasswordDialog(false);
      setResetPasswordMember(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  const fetchMembers = async (page = 1, search = "") => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const skip = (page - 1) * pageSize;
      let url = `${API}/admin/members?limit=${pageSize}&skip=${skip}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000 // 30 seconds timeout
      });
      console.log("Members fetched:", res.data);
      
      // Handle new paginated response format
      if (res.data.members) {
        setMembers(res.data.members);
        setTotalMembers(res.data.total);
      } else {
        // Fallback for old format
        setMembers(res.data || []);
        setTotalMembers(res.data?.length || 0);
      }
    } catch (err) {
      console.error("Error fetching members:", err);
      const errorMessage = err.response?.data?.detail || err.message || "Failed to load members";
      setError(errorMessage);
      toast.error("Failed to load members: " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchMembers(currentPage, searchQuery);
    }
  }, [token, currentPage]);
  
  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (token) {
        setCurrentPage(1);
        fetchMembers(1, searchQuery);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  const totalPages = Math.ceil(totalMembers / pageSize);
  
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newMember };
      // Use admin endpoint if custom member_id is provided
      const endpoint = newMember.member_id ? `${API}/admin/members/create` : `${API}/auth/register`;
      
      const res = await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Store registered member data for ID card
      const memberData = {
        member_id: res.data.member_id,
        name: newMember.name,
        email: newMember.email,
        phone: newMember.phone,
        plan_type: newMember.plan_type,
        sponsor_id: newMember.sponsor_id,
        photo_url: newMember.photo_url,
        password: res.data.password
      };
      
      // Close dialog first
      setDialogOpen(false);
      
      // Clear form
      setNewMember({ name: "", email: "", phone: "", plan_type: "D", sponsor_id: "", address: "", member_id: "", photo_url: "", is_management: false });
      
      // Refresh members list
      fetchMembers(currentPage, searchQuery);
      
      // Show success toast
      toast.success(`Member registered! ID: ${res.data.member_id}, Password: ${res.data.password}`);
      
      // Show ID card after a short delay to ensure dialog is closed
      setTimeout(() => {
        console.log("Showing ID card for:", memberData);
        setRegisteredMember(memberData);
        setShowIdCard(true);
      }, 500);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to register");
    }
  };

  const handleEditMember = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/members/${editMember.original_member_id}`, {
        name: editMember.name,
        email: editMember.email,
        phone: editMember.phone,
        plan_type: editMember.plan_type,
        member_id: editMember.member_id,
        sponsor_id: editMember.sponsor_id,
        photo_url: editMember.photo_url,
        is_management: editMember.is_management
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Member updated successfully");
      setEditDialogOpen(false);
      setEditMember(null);
      fetchMembers(currentPage, searchQuery);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const activateMember = async (memberId) => {
    try {
      await axios.post(`${API}/admin/members/${memberId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Member activated");
      fetchMembers(currentPage, searchQuery);
    } catch (err) {
      toast.error("Failed to activate");
    }
  };

  const emailCredentials = async (memberId) => {
    try {
      const res = await axios.post(`${API}/admin/members/${memberId}/send-credentials`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.credentials) {
        toast.success(`Credentials: ID: ${res.data.credentials.member_id}, Password: ${res.data.credentials.password}`);
      } else {
        toast.success(`Email sent to ${res.data.sent_to}`);
      }
    } catch (err) {
      toast.error("Failed to send credentials");
    }
  };

  const deleteMember = async (memberId) => {
    if (!window.confirm(`Are you sure you want to delete member ${memberId}? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Member deleted successfully");
      fetchMembers(currentPage, searchQuery);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete member");
    }
  };

  const openEditDialog = (member) => {
    setEditMember({ ...member, original_member_id: member.member_id });
    setEditDialogOpen(true);
  };

  const exportMembers = async () => {
    try {
      toast.info("Preparing export... This may take a moment for large datasets.", { duration: 5000 });
      
      const response = await axios.get(`${API}/admin/members/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 180000, // 3 minutes for large exports
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            if (percent % 25 === 0) {
              console.log(`Export progress: ${percent}%`);
            }
          }
        }
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Achraj_Member_Report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Members exported successfully!");
    } catch (err) {
      console.error("Export error:", err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error("Export timed out. Your member list may be too large. Please contact support.", { duration: 8000 });
      } else if (err.code === 'ERR_NETWORK') {
        toast.error("Network error during export. Please check your connection and try again.");
      } else if (err.response?.status === 500) {
        toast.error("Server error during export. Please try again later.");
      } else {
        toast.error("Failed to export members. Please try again.");
      }
    }
  };

  return (
    <div data-testid="admin-members">
      {/* ID Card Modal */}
      {showIdCard && registeredMember && (
        <MemberIDCard 
          member={registeredMember} 
          onClose={() => {
            setShowIdCard(false);
            setRegisteredMember(null);
          }} 
        />
      )}
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Members Management</h1>
          <p className="text-slate-400 text-sm">Total: {totalMembers} members</p>
        </div>
        <div className="flex flex-wrap gap-3 w-full sm:w-auto">
          <Input
            placeholder="Search by ID, name, email, phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-slate-800 border-slate-700 text-white w-full sm:w-64"
          />
          <Button 
            onClick={exportMembers}
            variant="outline"
            className="text-green-400 border-green-400/30 hover:bg-green-500/10 whitespace-nowrap"
            data-testid="export-members-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          {/* Add Member - Primary admin only */}
          {user?.admin_type !== 'secondary' && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-member-btn" className="bg-[#E65100] hover:bg-[#D84315] text-white whitespace-nowrap">
                <Plus className="w-4 h-4 mr-2" /> Add Member
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Register New Member</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="bg-slate-800 p-3 rounded-lg">
                <Label className="text-[#FF8F00] text-sm">Custom Member ID (Optional - for Management Level)</Label>
                <Input
                  value={newMember.member_id}
                  onChange={(e) => setNewMember({ ...newMember, member_id: e.target.value.toUpperCase() })}
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                  placeholder="ACJ00001 (leave empty for auto)"
                />
                <p className="text-xs text-slate-400 mt-1">Management Level: ACJ00001 - ACJ00031</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_management"
                  checked={newMember.is_management}
                  onChange={(e) => setNewMember({ ...newMember, is_management: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is_management" className="text-slate-300 text-sm">Management Level (Initial Investor)</Label>
              </div>
              <div>
                <Label className="text-slate-300">Sponsor ID</Label>
                <Input
                  value={newMember.sponsor_id}
                  onChange={(e) => setNewMember({ ...newMember, sponsor_id: e.target.value.toUpperCase() })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="ACJ00001"
                />
              </div>
              <div>
                <Label className="text-slate-300">Full Name</Label>
                <Input
                  value={newMember.name}
                  onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300">Phone (10 digits)</Label>
                <Input
                  value={newMember.phone}
                  onChange={(e) => setNewMember({ ...newMember, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                  minLength={10}
                />
              </div>
              <div>
                <Label className="text-slate-300">Plan</Label>
                <Select value={newMember.plan_type} onValueChange={(v) => setNewMember({ ...newMember, plan_type: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="D">Plan D - ₹500</SelectItem>
                    <SelectItem value="C">Plan C - ₹1,000</SelectItem>
                    <SelectItem value="B">Plan B - ₹2,000</SelectItem>
                    <SelectItem value="A">Plan A - ₹4,000</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Photo URL</Label>
                <Input
                  value={newMember.photo_url}
                  onChange={(e) => setNewMember({ ...newMember, photo_url: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="https://example.com/photo.jpg"
                />
              </div>
              <Button type="submit" className="w-full bg-[#E65100] hover:bg-[#D84315] text-white">
                Register Member
              </Button>
            </form>
          </DialogContent>
        </Dialog>
          )}
        </div>
      </div>

      {/* Edit Member Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Member - Full Admin Control</DialogTitle>
          </DialogHeader>
          {editMember && (
            <form onSubmit={handleEditMember} className="space-y-4">
              <div className="bg-slate-800 p-3 rounded-lg">
                <Label className="text-[#FF8F00] text-sm">Member ID</Label>
                <Input
                  value={editMember.member_id}
                  onChange={(e) => setEditMember({ ...editMember, member_id: e.target.value.toUpperCase() })}
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                />
                <p className="text-xs text-slate-400 mt-1">Warning: Changing ID will affect all references</p>
              </div>
              <div className="bg-slate-800 p-3 rounded-lg">
                <Label className="text-[#FF8F00] text-sm">Sponsor ID</Label>
                <Input
                  value={editMember.sponsor_id || ""}
                  onChange={(e) => setEditMember({ ...editMember, sponsor_id: e.target.value.toUpperCase() })}
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                  placeholder="ACJ00001"
                />
                <p className="text-xs text-slate-400 mt-1">Change sponsor if member joined under wrong ID</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit_is_management"
                  checked={editMember.is_management || false}
                  onChange={(e) => setEditMember({ ...editMember, is_management: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="edit_is_management" className="text-slate-300 text-sm">Management Level</Label>
              </div>
              <div>
                <Label className="text-slate-300">Full Name</Label>
                <Input
                  value={editMember.name}
                  onChange={(e) => setEditMember({ ...editMember, name: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input
                  type="email"
                  value={editMember.email}
                  onChange={(e) => setEditMember({ ...editMember, email: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300">Phone</Label>
                <Input
                  value={editMember.phone}
                  onChange={(e) => setEditMember({ ...editMember, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300">Plan</Label>
                <Select value={editMember.plan_type} onValueChange={(v) => setEditMember({ ...editMember, plan_type: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="D">Plan D - ₹500</SelectItem>
                    <SelectItem value="C">Plan C - ₹1,000</SelectItem>
                    <SelectItem value="B">Plan B - ₹2,000</SelectItem>
                    <SelectItem value="A">Plan A - ₹4,000</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Photo URL</Label>
                <Input
                  value={editMember.photo_url || ""}
                  onChange={(e) => setEditMember({ ...editMember, photo_url: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="https://example.com/photo.jpg"
                />
                {editMember.photo_url && (
                  <img src={editMember.photo_url} alt="Member" className="w-16 h-16 rounded-full mt-2 object-cover" />
                )}
              </div>
              <Button type="submit" className="w-full bg-[#E65100] hover:bg-[#D84315] text-white">
                Update Member
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog} onOpenChange={setResetPasswordDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-cyan-400" />
              Reset Member Password
            </DialogTitle>
          </DialogHeader>
          {resetPasswordMember && (
            <div className="space-y-4">
              <div className="bg-slate-800 rounded-lg p-4">
                <p className="text-[#D4AF37] font-bold">{resetPasswordMember.member_id}</p>
                <p className="text-white">{resetPasswordMember.name}</p>
                <p className="text-slate-400 text-sm">{resetPasswordMember.phone}</p>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-blue-400 text-sm">
                  Default password is the last 4 digits of phone number: <strong>{resetPasswordMember.phone?.slice(-4)}</strong>
                </p>
              </div>

              <div>
                <Label className="text-slate-400">New Password</Label>
                <Input
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  placeholder="Enter new password (min 4 characters)"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleResetPassword}
                  disabled={resettingPassword}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                >
                  {resettingPassword ? "Resetting..." : "Reset Password"}
                </Button>
                <Button
                  onClick={() => setResetPasswordDialog(false)}
                  variant="outline"
                  className="flex-1 border-slate-700"
                >
                  Cancel
                </Button>
              </div>

              <p className="text-slate-500 text-xs text-center">
                After resetting, please inform the member of their new password.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-slate-400">Loading members...</div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-red-400 mb-4">{error}</div>
              <Button onClick={fetchMembers} variant="outline" className="text-white">
                Retry
              </Button>
            </div>
          ) : members.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-slate-400">No members found. Click &quot;Add Member&quot; to create one.</div>
            </div>
          ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-slate-800/50">
                <TableHead className="text-slate-400">Photo</TableHead>
                <TableHead className="text-slate-400">Member ID</TableHead>
                <TableHead className="text-slate-400">Name</TableHead>
                <TableHead className="text-slate-400">Phone</TableHead>
                <TableHead className="text-slate-400">Plan</TableHead>
                <TableHead className="text-slate-400">Type</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Direct</TableHead>
                <TableHead className="text-slate-400">Sponsors</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.id || member.member_id} className="border-slate-800 hover:bg-slate-800/50">
                  <TableCell>
                    {member.photo_url ? (
                      <img src={member.photo_url} alt={member.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold">
                        {member.name?.charAt(0)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-[#E65100] font-mono font-bold">{member.member_id}</TableCell>
                  <TableCell className="text-white">{member.name}</TableCell>
                  <TableCell className="text-slate-300">{member.phone}</TableCell>
                  <TableCell>
                    <Badge className="bg-[#2E86AB]/20 text-[#2E86AB]">{member.plan_type}</Badge>
                  </TableCell>
                  <TableCell>
                    {member.is_management ? (
                      <Badge className="bg-purple-500/20 text-purple-400">Management</Badge>
                    ) : (
                      <Badge className="bg-slate-500/20 text-slate-400">Member</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={member.is_active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                      {member.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-300">{member.sponsored_count ?? member.direct_joinings ?? 0}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => fetchSponsoredMembers(member.member_id)}
                      className="text-cyan-400 border-cyan-400/30 hover:bg-cyan-500/10"
                      title="View Sponsored Members"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {/* Edit button - Primary admin only */}
                      {user?.admin_type !== 'secondary' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(member)}
                          className="text-[#FF8F00] border-[#FF8F00]/30 hover:bg-[#FF8F00]/10"
                        >
                          Edit
                        </Button>
                      )}
                      {/* ID Card - Available to all */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setRegisteredMember(member);
                          setShowIdCard(true);
                        }}
                        className="text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"
                        title="View ID Card"
                      >
                        <IdCard className="w-4 h-4" />
                      </Button>
                      {/* Reset Password - Primary admin only */}
                      {user?.admin_type !== 'secondary' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openResetPasswordDialog(member)}
                          className="text-cyan-400 border-cyan-400/30 hover:bg-cyan-500/10"
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Activate - Primary admin only */}
                      {!member.is_active && user?.admin_type !== 'secondary' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => activateMember(member.member_id)}
                          className="text-green-400 border-green-400/30 hover:bg-green-500/10"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Email - Available to all */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => emailCredentials(member.member_id)}
                        className="text-blue-400 border-blue-400/30 hover:bg-blue-500/10"
                      >
                        <Mail className="w-4 h-4" />
                      </Button>
                      {/* Delete - Primary admin only */}
                      {user?.admin_type !== 'secondary' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteMember(member.member_id)}
                          className="text-red-400 border-red-400/30 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      {/* View only badge for secondary admin */}
                      {user?.admin_type === 'secondary' && (
                        <Badge className="bg-slate-500/20 text-slate-400 text-xs">View Only</Badge>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <div className="text-slate-400 text-sm">
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalMembers)} of {totalMembers} members
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              className="text-white border-slate-700 hover:bg-slate-800 disabled:opacity-50"
            >
              First
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="text-white border-slate-700 hover:bg-slate-800 disabled:opacity-50"
            >
              Previous
            </Button>
            <div className="flex items-center gap-1 px-2">
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    size="sm"
                    variant={currentPage === pageNum ? "default" : "outline"}
                    onClick={() => goToPage(pageNum)}
                    className={currentPage === pageNum ? "bg-[#E65100] text-white" : "text-white border-slate-700 hover:bg-slate-800"}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="text-white border-slate-700 hover:bg-slate-800 disabled:opacity-50"
            >
              Next
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
              className="text-white border-slate-700 hover:bg-slate-800 disabled:opacity-50"
            >
              Last
            </Button>
          </div>
        </div>
      )}

      {/* Sponsored Members Modal */}
      <Dialog open={showSponsoredModal} onOpenChange={setShowSponsoredModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Sponsored Members by {sponsoredData?.sponsor_id}
            </DialogTitle>
          </DialogHeader>
          
          {loadingSponsored ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
            </div>
          ) : sponsoredData ? (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-800 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-white">{sponsoredData.total_sponsored}</p>
                  <p className="text-slate-400 text-sm">Total Sponsored</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-400">{sponsoredData.active_count}</p>
                  <p className="text-green-400/70 text-sm">Active</p>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-400">{sponsoredData.inactive_count}</p>
                  <p className="text-red-400/70 text-sm">Inactive</p>
                </div>
              </div>

              {/* Members List */}
              {sponsoredData.members.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No sponsored members found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-slate-400 text-sm font-medium">Sponsored Members List:</p>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Photo</TableHead>
                        <TableHead className="text-slate-400">Member ID</TableHead>
                        <TableHead className="text-slate-400">Name</TableHead>
                        <TableHead className="text-slate-400">Phone</TableHead>
                        <TableHead className="text-slate-400">Plan</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                        <TableHead className="text-slate-400">Direct</TableHead>
                        <TableHead className="text-slate-400">Joined</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sponsoredData.members.map((m) => (
                        <TableRow key={m.member_id} className="border-slate-700 hover:bg-slate-800/50">
                          <TableCell>
                            {m.photo_url ? (
                              <img src={m.photo_url} alt={m.name} className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-white text-sm font-bold">
                                {m.name?.charAt(0)}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-[#E65100] font-mono font-bold">{m.member_id}</TableCell>
                          <TableCell className="text-white">{m.name}</TableCell>
                          <TableCell className="text-slate-300">{m.phone}</TableCell>
                          <TableCell>
                            <Badge className="bg-[#2E86AB]/20 text-[#2E86AB]">{m.plan_type}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={m.is_active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                              {m.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-300">{m.direct_joinings || 0}</TableCell>
                          <TableCell className="text-slate-400 text-sm">
                            {m.created_at ? new Date(m.created_at).toLocaleDateString() : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Monthly Repurchase Management
function RepurchaseManagement() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));
  const [searchQuery, setSearchQuery] = useState("");
  const { token } = useAuth();

  const fetchRepurchaseData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/repurchase?month=${currentMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(res.data.members || []);
    } catch (err) {
      console.error("Error:", err);
      toast.error("Failed to load repurchase data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRepurchaseData();
  }, [currentMonth]);

  const toggleRepurchase = async (memberId, currentStatus) => {
    try {
      await axios.post(`${API}/admin/repurchase/${memberId}?repurchased=${!currentStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Repurchase ${!currentStatus ? 'marked' : 'unmarked'} for ${memberId}`);
      fetchRepurchaseData();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const getPlanAmount = (planType) => {
    const amounts = { D: 500, C: 1000, B: 2000, A: 4000 };
    return amounts[planType] || 500;
  };

  const filteredMembers = members.filter(m =>
    !searchQuery ||
    m.member_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.phone?.includes(searchQuery)
  );

  const totalRepurchased = members.filter(m => m.repurchased).length;
  const totalPending = members.filter(m => !m.repurchased).length;

  return (
    <div data-testid="admin-repurchase">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Monthly Repurchase</h1>
          <p className="text-slate-400 text-sm">Track member subscription renewals</p>
        </div>
        <div className="flex gap-3 items-center">
          <Input
            type="month"
            value={currentMonth}
            onChange={(e) => setCurrentMonth(e.target.value)}
            className="bg-slate-800 border-slate-700 text-white w-40"
          />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-slate-800 border-slate-700 text-white w-48"
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-white">{members.length}</p>
            <p className="text-slate-400 text-sm">Total Active</p>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-400">{totalRepurchased}</p>
            <p className="text-green-400/70 text-sm">Repurchased</p>
          </CardContent>
        </Card>
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-400">{totalPending}</p>
            <p className="text-red-400/70 text-sm">Pending</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-slate-400">Loading...</div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400">Member ID</TableHead>
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Phone</TableHead>
                  <TableHead className="text-slate-400">Plan</TableHead>
                  <TableHead className="text-slate-400">Amount</TableHead>
                  <TableHead className="text-slate-400">Direct</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMembers.map((member) => (
                  <TableRow key={member.member_id} className="border-slate-800">
                    <TableCell className="text-[#E65100] font-mono font-bold">{member.member_id}</TableCell>
                    <TableCell className="text-white">{member.name}</TableCell>
                    <TableCell className="text-slate-300">{member.phone}</TableCell>
                    <TableCell>
                      <Badge className="bg-[#D4AF37]/20 text-[#D4AF37]">{member.plan_type}</Badge>
                    </TableCell>
                    <TableCell className="text-white">₹{getPlanAmount(member.plan_type).toLocaleString()}</TableCell>
                    <TableCell className="text-slate-300">{member.direct_joinings || 0}</TableCell>
                    <TableCell>
                      {member.repurchased ? (
                        <Badge className="bg-green-500/20 text-green-400">DONE</Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-400">PENDING</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        onClick={() => toggleRepurchase(member.member_id, member.repurchased)}
                        className={member.repurchased 
                          ? "bg-red-500/20 text-red-400 hover:bg-red-500/30" 
                          : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        }
                      >
                        {member.repurchased ? "Unmark" : "Mark Done"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// KYC Management for Admin
function KYCManagement() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [kycDialogOpen, setKycDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [kycData, setKycData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const pageSize = 25;
  const { token } = useAuth();

  const fetchMembers = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const skip = (page - 1) * pageSize;
      let url = `${API}/admin/members?limit=${pageSize}&skip=${skip}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      
      if (res.data.members) {
        setMembers(res.data.members);
        setTotalMembers(res.data.total || 0);
      } else {
        setMembers(res.data || []);
        setTotalMembers(res.data?.length || 0);
      }
    } catch (err) {
      console.error("Error fetching members:", err);
      toast.error("Failed to load members. Try refreshing.");
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchMembers(currentPage, searchQuery);
    }
  }, [token, currentPage]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (token) {
        setCurrentPage(1);
        fetchMembers(1, searchQuery);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const totalPages = Math.ceil(totalMembers / pageSize);

  const openKYCDialog = async (member) => {
    setSelectedMember(member);
    try {
      const res = await axios.get(`${API}/admin/members/${member.member_id}/kyc`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      setKycData(res.data || {});
    } catch (err) {
      console.error("Error loading KYC:", err);
      setKycData({});
    }
    setKycDialogOpen(true);
  };

  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be less than 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setKycData(prev => ({ ...prev, [fieldName]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveKYC = async () => {
    if (!selectedMember) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/members/${selectedMember.member_id}/kyc`, kycData, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      toast.success(`KYC updated for ${selectedMember.member_id}. Fields: ${res.data.updated_fields?.join(', ') || 'all'}`);
      setKycDialogOpen(false);
      fetchMembers(currentPage, searchQuery);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update KYC");
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400",
      submitted: "bg-blue-500/20 text-blue-400",
      verified: "bg-green-500/20 text-green-400",
      rejected: "bg-red-500/20 text-red-400"
    };
    return styles[status] || styles.pending;
  };

  // Export KYC data to CSV
  const exportKYCDataCSV = async () => {
    try {
      toast.info("Fetching all KYC data...");
      
      // Fetch all members with KYC details
      const res = await axios.get(`${API}/admin/kyc-export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allMembers = res.data || [];
      
      if (allMembers.length === 0) {
        toast.error("No KYC data to export");
        return;
      }

      // CSV headers
      const headers = [
        'S.No',
        'Member ID',
        'Name',
        'Phone',
        'Email',
        'Plan Type',
        'KYC Status',
        'Account Holder Name',
        'Bank Name',
        'Account Number',
        'IFSC Code',
        'UPI ID',
        'Aadhaar Number',
        'PAN Number',
        'Address',
        'Nominee Name',
        'Nominee Relation',
        'KYC Updated At',
        'Joined At'
      ];

      // CSV rows - prefix account/aadhaar numbers with tab to prevent Excel from converting to scientific notation
      const rows = allMembers.map((m, idx) => [
        idx + 1,
        m.member_id || '',
        m.name || '',
        `'${m.phone || ''}`,
        m.email || '',
        m.plan_type || '',
        m.kyc_status || 'pending',
        m.account_holder_name || '',
        m.bank_name || '',
        m.account_number ? `'${m.account_number}` : '',
        m.ifsc_code || '',
        m.upi_id || '',
        m.aadhaar_number ? `'${m.aadhaar_number}` : '',
        m.pan_number || '',
        m.address || '',
        m.nominee_name || '',
        m.nominee_relation || '',
        m.kyc_updated_at ? new Date(m.kyc_updated_at).toLocaleDateString('en-IN') : '',
        m.created_at ? new Date(m.created_at).toLocaleDateString('en-IN') : ''
      ]);

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `kyc_data_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${allMembers.length} members' KYC data to CSV`);
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export KYC data");
    }
  };

  return (
    <div data-testid="admin-kyc">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">KYC Management</h1>
          <p className="text-slate-400 text-sm">Total: {totalMembers} members | Page {currentPage} of {totalPages || 1}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            placeholder="Search by ID, name, phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-slate-800 border-slate-700 text-white w-full sm:w-64"
          />
          <Button 
            onClick={exportKYCDataCSV} 
            variant="outline" 
            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-slate-400">Loading members...</div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400 w-16">S.No</TableHead>
                  <TableHead className="text-slate-400">Member ID</TableHead>
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Phone</TableHead>
                  <TableHead className="text-slate-400">Bank</TableHead>
                  <TableHead className="text-slate-400">UPI</TableHead>
                  <TableHead className="text-slate-400">Documents</TableHead>
                  <TableHead className="text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member, index) => (
                  <TableRow key={member.id || member.member_id} className="border-slate-800">
                    <TableCell className="text-slate-500 font-mono text-sm">{((currentPage - 1) * pageSize) + index + 1}</TableCell>
                    <TableCell className="text-[#E65100] font-mono font-bold">{member.member_id}</TableCell>
                    <TableCell className="text-white">{member.name}</TableCell>
                    <TableCell className="text-slate-300">{member.phone}</TableCell>
                    <TableCell>
                      {member.bank_name ? (
                        <Badge className="bg-green-500/20 text-green-400">Added</Badge>
                      ) : (
                        <Badge className="bg-slate-500/20 text-slate-400">-</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {member.upi_id ? (
                        <Badge className="bg-green-500/20 text-green-400">Added</Badge>
                      ) : (
                        <Badge className="bg-slate-500/20 text-slate-400">-</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadge(member.kyc_status)}>
                        {member.kyc_status?.toUpperCase() || 'PENDING'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        onClick={() => openKYCDialog(member)}
                        className="bg-[#E65100] hover:bg-[#D84315] text-white"
                      >
                        <Eye className="w-4 h-4 mr-1" /> View/Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 bg-slate-900/50 border border-slate-800 rounded-lg p-3">
          <span className="text-slate-400 text-sm">
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalMembers)} of {totalMembers}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="text-white border-slate-700 hover:bg-slate-800 disabled:opacity-50"
            >
              First
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="text-white border-slate-700 hover:bg-slate-800 disabled:opacity-50"
            >
              Prev
            </Button>
            <span className="text-white px-3">Page {currentPage} of {totalPages}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="text-white border-slate-700 hover:bg-slate-800 disabled:opacity-50"
            >
              Next
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="text-white border-slate-700 hover:bg-slate-800 disabled:opacity-50"
            >
              Last
            </Button>
          </div>
        </div>
      )}

      {/* KYC Edit Dialog */}
      <Dialog open={kycDialogOpen} onOpenChange={setKycDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
              KYC Details - {selectedMember?.member_id} ({selectedMember?.name})
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* KYC Status */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400">KYC Status</Label>
                  <select
                    value={kycData.kyc_status || 'pending'}
                    onChange={(e) => setKycData({...kycData, kyc_status: e.target.value})}
                    className="w-full mt-1 p-2 bg-slate-700 border border-slate-600 rounded text-white"
                  >
                    <option value="pending">Pending</option>
                    <option value="submitted">Submitted</option>
                    <option value="verified">Verified</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div>
                  <Label className="text-slate-400">Admin Remarks</Label>
                  <Input
                    value={kycData.kyc_remarks || ''}
                    onChange={(e) => setKycData({...kycData, kyc_remarks: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                    placeholder="Add remarks for member"
                  />
                </div>
              </div>
            </div>

            {/* Bank Details */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h3 className="text-[#D4AF37] font-semibold mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Bank Details
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-400 text-sm">Bank Name</Label>
                  <Input
                    value={kycData.bank_name || ''}
                    onChange={(e) => setKycData({...kycData, bank_name: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-sm">Account Holder</Label>
                  <Input
                    value={kycData.account_holder_name || ''}
                    onChange={(e) => setKycData({...kycData, account_holder_name: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-sm">Account Number</Label>
                  <Input
                    value={kycData.account_number || ''}
                    onChange={(e) => setKycData({...kycData, account_number: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-sm">IFSC Code</Label>
                  <Input
                    value={kycData.ifsc_code || ''}
                    onChange={(e) => setKycData({...kycData, ifsc_code: e.target.value.toUpperCase()})}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-slate-400 text-sm">Branch Name</Label>
                  <Input
                    value={kycData.branch_name || ''}
                    onChange={(e) => setKycData({...kycData, branch_name: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
              </div>
            </div>

            {/* UPI Details */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h3 className="text-[#D4AF37] font-semibold mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> UPI Details
              </h3>
              <div>
                <Label className="text-slate-400 text-sm">UPI ID</Label>
                <Input
                  value={kycData.upi_id || ''}
                  onChange={(e) => setKycData({...kycData, upi_id: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                  placeholder="e.g., name@upi"
                />
              </div>
            </div>

            {/* Documents */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h3 className="text-[#D4AF37] font-semibold mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Identity Documents
              </h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {/* Aadhaar Front */}
                <div>
                  <Label className="text-slate-400 text-sm">Aadhaar Front</Label>
                  <div className="border border-slate-700 rounded-lg p-2 mt-1">
                    {kycData.aadhaar_front_url ? (
                      <img src={kycData.aadhaar_front_url} alt="Aadhaar Front" className="w-full h-24 object-contain rounded" />
                    ) : (
                      <div className="h-24 flex items-center justify-center text-slate-500 text-sm">Not uploaded</div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'aadhaar_front_url')}
                      className="w-full mt-2 text-xs text-slate-400"
                    />
                  </div>
                </div>
                {/* Aadhaar Back */}
                <div>
                  <Label className="text-slate-400 text-sm">Aadhaar Back</Label>
                  <div className="border border-slate-700 rounded-lg p-2 mt-1">
                    {kycData.aadhaar_back_url ? (
                      <img src={kycData.aadhaar_back_url} alt="Aadhaar Back" className="w-full h-24 object-contain rounded" />
                    ) : (
                      <div className="h-24 flex items-center justify-center text-slate-500 text-sm">Not uploaded</div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'aadhaar_back_url')}
                      className="w-full mt-2 text-xs text-slate-400"
                    />
                  </div>
                </div>
                {/* PAN Card */}
                <div>
                  <Label className="text-slate-400 text-sm">PAN Card</Label>
                  <div className="border border-slate-700 rounded-lg p-2 mt-1">
                    {kycData.pan_card_url ? (
                      <img src={kycData.pan_card_url} alt="PAN Card" className="w-full h-24 object-contain rounded" />
                    ) : (
                      <div className="h-24 flex items-center justify-center text-slate-500 text-sm">Not uploaded</div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'pan_card_url')}
                      className="w-full mt-2 text-xs text-slate-400"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Nominee Details */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h3 className="text-[#D4AF37] font-semibold mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" /> Nominee Details
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-400 text-sm">Nominee Name</Label>
                  <Input
                    value={kycData.nominee_name || ''}
                    onChange={(e) => setKycData({...kycData, nominee_name: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-sm">Relationship</Label>
                  <Input
                    value={kycData.nominee_relation || ''}
                    onChange={(e) => setKycData({...kycData, nominee_relation: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-sm">Nominee Aadhaar</Label>
                  <Input
                    value={kycData.nominee_aadhaar || ''}
                    onChange={(e) => setKycData({...kycData, nominee_aadhaar: e.target.value.replace(/\D/g, '').slice(0, 12)})}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                    maxLength={12}
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-sm">Nominee Phone</Label>
                  <Input
                    value={kycData.nominee_phone || ''}
                    onChange={(e) => setKycData({...kycData, nominee_phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                    maxLength={10}
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <Button 
              onClick={handleSaveKYC}
              disabled={saving}
              className="w-full bg-gradient-to-r from-[#E65100] to-[#FF8F00] hover:from-[#D84315] hover:to-[#E65100] text-white"
            >
              {saving ? "Saving..." : "Save KYC Details"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Categories Management
function CategoriesManagement() {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: "", description: "" });
  const [editCategory, setEditCategory] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { token } = useAuth();

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (err) {
      console.error("Error:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/categories`, newCategory, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Category created");
      setNewCategory({ name: "", description: "" });
      setDialogOpen(false);
      fetchCategories();
    } catch (err) {
      toast.error("Failed to create category");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/categories/${editCategory.id}`, {
        name: editCategory.name,
        description: editCategory.description
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Category updated");
      setEditDialogOpen(false);
      setEditCategory(null);
      fetchCategories();
    } catch (err) {
      toast.error("Failed to update category");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try {
      await axios.delete(`${API}/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Category deleted");
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete category");
    }
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Categories</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] hover:bg-[#B5952F] text-black">
              <Plus className="w-4 h-4 mr-2" /> Add Category
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader>
              <DialogTitle className="text-white">Add Category</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-slate-300">Name</Label>
                <Input
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300">Description</Label>
                <Textarea
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <Button type="submit" className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black">
                Create Category
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((cat) => (
          <Card key={cat.id} className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-white font-semibold">{cat.name}</h3>
                  <p className="text-slate-400 text-sm mt-1">{cat.description || "No description"}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setEditCategory(cat); setEditDialogOpen(true); }}
                    className="text-blue-400 hover:bg-blue-500/10"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(cat.id)}
                    className="text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Category Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Category</DialogTitle>
          </DialogHeader>
          {editCategory && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <Label className="text-slate-300">Name</Label>
                <Input
                  value={editCategory.name}
                  onChange={(e) => setEditCategory({ ...editCategory, name: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300">Description</Label>
                <Textarea
                  value={editCategory.description || ""}
                  onChange={(e) => setEditCategory({ ...editCategory, description: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <Button type="submit" className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black">
                Update Category
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Products Management with Multiple Images, Color Variants, and Image Slider
function ProductsManagement() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newProduct, setNewProduct] = useState({
    name: "", description: "", price: "", category_id: "", image_url: "", 
    images: [], stock: "", unit: "pcs", product_type: "single",
    variants: [], is_holi_special: false, is_internal: false, point_value: ""
  });
  const [editProduct, setEditProduct] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filterHoli, setFilterHoli] = useState(false);
  const [filterInternal, setFilterInternal] = useState("all"); // all, public, internal
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [previewProduct, setPreviewProduct] = useState(null);
  const { token } = useAuth();

  const UNIT_OPTIONS = [
    { value: "pcs", label: "Pieces" },
    { value: "kg", label: "Kilogram" },
    { value: "gram", label: "Gram" },
    { value: "ltr", label: "Litre" },
    { value: "ml", label: "Millilitre" },
    { value: "pack", label: "Pack" },
    { value: "box", label: "Box" },
    { value: "set", label: "Set" },
    { value: "combo", label: "Combo" }
  ];

  const PRODUCT_TYPES = [
    { value: "single", label: "Single Product" },
    { value: "combo", label: "Combo Pack" },
    { value: "kit", label: "Kit / Set" }
  ];

  const COLOR_OPTIONS = [
    { value: "red", label: "Red", hex: "#EF4444" },
    { value: "blue", label: "Blue", hex: "#3B82F6" },
    { value: "green", label: "Green", hex: "#22C55E" },
    { value: "yellow", label: "Yellow", hex: "#EAB308" },
    { value: "pink", label: "Pink", hex: "#EC4899" },
    { value: "purple", label: "Purple", hex: "#A855F7" },
    { value: "orange", label: "Orange", hex: "#F97316" },
    { value: "white", label: "White", hex: "#FFFFFF" },
    { value: "black", label: "Black", hex: "#1F2937" },
    { value: "multicolor", label: "Multicolor", hex: "linear-gradient(90deg, #EF4444, #EAB308, #22C55E, #3B82F6)" }
  ];

  const SIZE_OPTIONS = [
    { value: "XS", label: "XS (Extra Small)" },
    { value: "S", label: "S (Small)" },
    { value: "M", label: "M (Medium)" },
    { value: "L", label: "L (Large)" },
    { value: "XL", label: "XL (Extra Large)" },
    { value: "XXL", label: "XXL (2XL)" },
    { value: "XXXL", label: "XXXL (3XL)" },
    { value: "Free", label: "Free Size" }
  ];

  const CATEGORY_TYPES = {
    clothing: ['Men Clothing', 'Women Clothing', 'Kids Clothing', 'Clothing', 'Fashion', 'Apparel'],
    general: ['Grocery', 'Spices', 'Electronics', 'Home', 'Beauty', 'Health']
  };

  // Check if category is clothing type
  const isClothingCategory = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return false;
    return CATEGORY_TYPES.clothing.some(type => 
      category.name?.toLowerCase().includes(type.toLowerCase())
    );
  };

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products?include_internal=true`),  // Include internal products for admin
        axios.get(`${API}/categories`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (err) {
      console.error("Error:", err);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Upload single image
  const uploadImage = async (file) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return null;
    }
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const res = await axios.post(`${API}/upload-base64`, { image: reader.result }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          resolve(`${API}${res.data.url.replace('/api', '')}`);
        } catch { resolve(null); }
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  };

  // Handle multiple images with colors and sizes
  const handleMultiImageUpload = async (e, color, size, isEdit = false) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    setUploading(true);
    for (const file of files) {
      const url = await uploadImage(file);
      if (url) {
        const imageObj = { 
          url, 
          color: color || 'default',
          size: size || 'default'
        };
        if (isEdit) {
          setEditProduct(prev => ({ ...prev, images: [...(prev.images || []), imageObj] }));
        } else {
          setNewProduct(prev => ({ ...prev, images: [...(prev.images || []), imageObj] }));
        }
      }
    }
    setUploading(false);
    toast.success("Images uploaded!");
  };

  // Remove image
  const removeImage = (index, isEdit = false) => {
    if (isEdit) {
      setEditProduct(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
    } else {
      setNewProduct(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
    }
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Set first image as main image_url if not set
      const mainImage = newProduct.image_url || (newProduct.images[0]?.url || newProduct.images[0]);
      await axios.post(`${API}/products`, {
        ...newProduct,
        image_url: mainImage,
        price: parseFloat(newProduct.price),
        point_value: parseFloat(newProduct.point_value) || 0,
        stock: parseInt(newProduct.stock) || 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newProduct.is_internal ? "Internal product created!" : "Product created!");
      setNewProduct({ name: "", description: "", price: "", category_id: "", image_url: "", images: [], stock: "", unit: "pcs", product_type: "single", variants: [], is_holi_special: false, is_internal: false, point_value: "" });
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to create product");
    }
  };

  // Handle update
  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const mainImage = editProduct.image_url || (editProduct.images?.[0]?.url || editProduct.images?.[0]);
      await axios.put(`${API}/products/${editProduct.id}`, {
        ...editProduct,
        image_url: mainImage,
        price: parseFloat(editProduct.price) || 0,
        point_value: parseFloat(editProduct.point_value) || 0,
        stock: parseInt(editProduct.stock) || 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Product updated!");
      setEditDialogOpen(false);
      setEditProduct(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const deleteProduct = async (id) => {
    if (!confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const filteredProducts = products.filter(p => {
    // Filter by Holi special
    if (filterHoli && !p.is_holi_special) return false;
    // Filter by internal/public
    if (filterInternal === "internal" && !p.is_internal) return false;
    if (filterInternal === "public" && p.is_internal) return false;
    return true;
  });

  // Image Slider Component
  const ImageSlider = ({ images, className = "" }) => {
    const [current, setCurrent] = useState(0);
    const allImages = images?.length > 0 ? images : [{ url: null }];
    
    return (
      <div className={`relative ${className}`}>
        <div className="w-full h-48 bg-slate-700 rounded-lg overflow-hidden">
          {allImages[current]?.url || allImages[current] ? (
            <img 
              src={allImages[current]?.url || allImages[current]} 
              alt="Product" 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="w-16 h-16 text-slate-500" />
            </div>
          )}
        </div>
        {allImages.length > 1 && (
          <>
            <button 
              onClick={() => setCurrent(c => c > 0 ? c - 1 : allImages.length - 1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button 
              onClick={() => setCurrent(c => c < allImages.length - 1 ? c + 1 : 0)}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {allImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-2 h-2 rounded-full transition-colors ${i === current ? 'bg-white' : 'bg-white/50'}`}
                  style={img?.color && img.color !== 'default' ? { 
                    backgroundColor: COLOR_OPTIONS.find(c => c.value === img.color)?.hex || '#fff' 
                  } : {}}
                />
              ))}
            </div>
            {allImages[current]?.color && allImages[current].color !== 'default' && (
              <Badge className="absolute top-2 left-2 bg-black/70 text-white capitalize">
                {allImages[current].color}
              </Badge>
            )}
          </>
        )}
      </div>
    );
  };

  // Check if product is new (created within last 7 days)
  const isNewProduct = (createdAt) => {
    if (!createdAt) return false;
    const created = new Date(createdAt);
    const now = new Date();
    const diffDays = (now - created) / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  };

  // Sort products by created_at (newest first)
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateB - dateA;
  });

  // Product Form with Light Theme
  const ProductForm = ({ product, setProduct, onSubmit, isEdit }) => {
    const [selectedColor, setSelectedColor] = useState('default');
    const [selectedSize, setSelectedSize] = useState('default');
    const [availableSizes, setAvailableSizes] = useState(product.sizes || []);
    const showSizeOptions = isClothingCategory(product.category_id);
    
    return (
      <form onSubmit={onSubmit} className="space-y-4 max-h-[75vh] overflow-y-auto pr-2 bg-white rounded-lg p-4">
        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-gray-700 font-semibold">Product Name *</Label>
            <Input value={product.name} onChange={e => setProduct({...product, name: e.target.value})} required className="bg-gray-50 border-gray-300 text-gray-900" placeholder="Enter product name" />
          </div>
          <div className="col-span-2">
            <Label className="text-gray-700 font-semibold">Description</Label>
            <textarea value={product.description} onChange={e => setProduct({...product, description: e.target.value})} className="w-full p-2 bg-gray-50 border border-gray-300 rounded text-gray-900 text-sm" rows={2} placeholder="Product description..." />
          </div>
          <div>
            <Label className="text-gray-700 font-semibold">Price (₹) *</Label>
            <Input type="number" value={product.price} onChange={e => setProduct({...product, price: e.target.value})} required className="bg-gray-50 border-gray-300 text-gray-900" placeholder="0" />
          </div>
          <div>
            <Label className="text-gray-700 font-semibold">Point Value (PV)</Label>
            <Input type="number" step="0.01" value={product.point_value ?? ""} onChange={e => setProduct({...product, point_value: e.target.value})} className="bg-gray-50 border-gray-300 text-gray-900" placeholder="0" data-testid="product-pv-input" />
            <p className="text-xs text-gray-400 mt-1">1 PV = ₹5 (e.g. ₹40 margin = 8 PV)</p>
          </div>
          <div>
            <Label className="text-gray-700 font-semibold">Stock</Label>
            <Input type="number" value={product.stock} onChange={e => setProduct({...product, stock: e.target.value})} className="bg-gray-50 border-gray-300 text-gray-900" placeholder="0" />
          </div>
          <div>
            <Label className="text-gray-700 font-semibold">Category *</Label>
            <Select value={product.category_id} onValueChange={v => setProduct({...product, category_id: v})}>
              <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-700 font-semibold">Unit</Label>
            <Select value={product.unit} onValueChange={v => setProduct({...product, unit: v})}>
              <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900"><SelectValue /></SelectTrigger>
              <SelectContent>
                {UNIT_OPTIONS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-700 font-semibold">Product Type</Label>
            <Select value={product.product_type || 'single'} onValueChange={v => setProduct({...product, product_type: v})}>
              <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRODUCT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pt-5">
            <input type="checkbox" id={`holi-${isEdit?'edit':'new'}`} checked={product.is_holi_special || false} onChange={e => setProduct({...product, is_holi_special: e.target.checked})} className="w-5 h-5 accent-pink-500" />
            <Label htmlFor={`holi-${isEdit?'edit':'new'}`} className="text-pink-600 font-semibold cursor-pointer">🎨 Holi Special</Label>
          </div>
          <div className="flex items-center gap-2 pt-5">
            <input type="checkbox" id={`internal-${isEdit?'edit':'new'}`} checked={product.is_internal || false} onChange={e => setProduct({...product, is_internal: e.target.checked})} className="w-5 h-5 accent-amber-500" />
            <Label htmlFor={`internal-${isEdit?'edit':'new'}`} className="text-amber-600 font-semibold cursor-pointer">🔒 Internal Only (Not on Website)</Label>
          </div>
        </div>

        {/* Available Sizes (for Clothing) */}
        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
          <Label className="text-base font-semibold block mb-2 text-gray-800">👕 Available Sizes (for Clothing)</Label>
          <p className="text-xs text-gray-500 mb-2">Select all sizes available for this product:</p>
          <div className="flex flex-wrap gap-2">
            {SIZE_OPTIONS.map(s => {
              const isSelected = availableSizes.includes(s.value);
              return (
                <button 
                  key={s.value} 
                  type="button" 
                  onClick={() => {
                    const newSizes = isSelected 
                      ? availableSizes.filter(sz => sz !== s.value)
                      : [...availableSizes, s.value];
                    setAvailableSizes(newSizes);
                    setProduct({...product, sizes: newSizes});
                  }}
                  className={`px-3 py-2 rounded text-sm font-medium transition-colors ${isSelected ? 'bg-blue-600 text-white ring-2 ring-blue-400' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'}`}
                >
                  {s.value}
                </button>
              );
            })}
          </div>
          {availableSizes.length > 0 && (
            <p className="text-xs text-green-600 mt-2 font-medium">Selected: {availableSizes.join(', ')}</p>
          )}
        </div>

        {/* Multiple Images with Colors & Sizes */}
        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
          <Label className="text-base font-semibold block mb-2 text-gray-800">📸 Product Images (Color & Size Variants)</Label>
          
          {/* Color Selector */}
          <div className="mb-3">
            <Label className="text-xs text-gray-600 mb-1 block font-medium">Select color for images:</Label>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setSelectedColor('default')} className={`px-3 py-1.5 rounded text-xs font-medium ${selectedColor === 'default' ? 'bg-gray-700 text-white ring-2 ring-gray-400' : 'bg-white border border-gray-300 text-gray-700'}`}>
                No Color
              </button>
              {COLOR_OPTIONS.map(c => (
                <button 
                  key={c.value} 
                  type="button" 
                  onClick={() => setSelectedColor(c.value)}
                  className={`px-3 py-1.5 rounded text-xs font-medium border-2 ${selectedColor === c.value ? 'ring-2 ring-offset-1 ring-gray-400' : 'border-transparent'}`}
                  style={{ backgroundColor: c.hex.includes('gradient') ? undefined : c.hex, background: c.hex.includes('gradient') ? c.hex : undefined, color: ['white', 'yellow'].includes(c.value) ? '#000' : '#fff', borderColor: ['white'].includes(c.value) ? '#ccc' : 'transparent' }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Size Selector for Images */}
          {availableSizes.length > 0 && (
            <div className="mb-3">
              <Label className="text-xs text-gray-600 mb-1 block font-medium">Select size for images (optional):</Label>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setSelectedSize('default')} className={`px-3 py-1.5 rounded text-xs font-medium ${selectedSize === 'default' ? 'bg-gray-700 text-white ring-2 ring-gray-400' : 'bg-white border border-gray-300 text-gray-700'}`}>
                  All Sizes
                </button>
                {availableSizes.map(size => (
                  <button 
                    key={size} 
                    type="button" 
                    onClick={() => setSelectedSize(size)}
                    className={`px-3 py-1.5 rounded text-xs font-medium ${selectedSize === size ? 'bg-blue-600 text-white ring-2 ring-blue-400' : 'bg-white border border-gray-300 text-gray-700'}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Image Preview Grid with Slider */}
          {(product.images?.length > 0) && (
            <div className="mb-3">
              <ImageSlider images={product.images} className="mb-2" />
              <div className="flex flex-wrap gap-2">
                {product.images.map((img, idx) => (
                  <div key={idx} className="relative group">
                    <img src={img?.url || img} alt="" className="w-14 h-14 object-cover rounded border-2" style={{ borderColor: img?.color ? (COLOR_OPTIONS.find(c => c.value === img.color)?.hex || '#d1d5db') : '#d1d5db' }} />
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-0.5">
                      {img?.color && img.color !== 'default' && (
                        <span className="text-[7px] bg-gray-800 text-white px-1 rounded capitalize">{img.color}</span>
                      )}
                      {img?.size && img.size !== 'default' && (
                        <span className="text-[7px] bg-blue-600 text-white px-1 rounded">{img.size}</span>
                      )}
                    </div>
                    <button type="button" onClick={() => removeImage(idx, isEdit)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs opacity-0 group-hover:opacity-100">×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          <div className="flex gap-2 items-center">
            <input type="file" accept="image/*" multiple onChange={e => handleMultiImageUpload(e, selectedColor, selectedSize, isEdit)} className="hidden" id={`img-upload-${isEdit?'edit':'new'}`} />
            <label htmlFor={`img-upload-${isEdit?'edit':'new'}`} className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2">
              <Camera className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload Images'}
            </label>
            {(selectedColor !== 'default' || selectedSize !== 'default') && (
              <span className="text-xs text-gray-600 font-medium">
                Uploading for: <span className="text-blue-600">{selectedColor !== 'default' ? selectedColor : ''} {selectedSize !== 'default' ? `(${selectedSize})` : ''}</span>
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">Select color/size first, then upload images. Images will be tagged automatically.</p>
        </div>

        <Button type="submit" className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold py-3" disabled={uploading}>
          {isEdit ? '✓ Update Product' : '+ Create Product'}
        </Button>
      </form>
    );
  };

  return (
    <div data-testid="admin-products">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Products</h1>
          <p className="text-slate-400 text-sm">Manage products with multiple images & color variants</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={filterInternal} onValueChange={setFilterInternal}>
            <SelectTrigger className="w-[140px] bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="Filter products" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-white">All Products</SelectItem>
              <SelectItem value="public" className="text-white">Public Only</SelectItem>
              <SelectItem value="internal" className="text-white">Internal Only</SelectItem>
            </SelectContent>
          </Select>
          <Button variant={filterHoli ? "default" : "outline"} onClick={() => setFilterHoli(!filterHoli)} className={filterHoli ? "bg-pink-600" : "border-pink-500 text-pink-400"}>
            🎨 {filterHoli ? "Holi Special" : "Filter Holi"}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"><Plus className="w-4 h-4 mr-2" /> Add Product</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 max-w-2xl">
              <DialogHeader><DialogTitle className="text-gray-900 text-xl font-bold">Add New Product</DialogTitle></DialogHeader>
              <ProductForm product={newProduct} setProduct={setNewProduct} onSubmit={handleSubmit} isEdit={false} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Products Grid with Image Sliders */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {sortedProducts.map(p => (
          <Card key={p.id} className={`bg-slate-800 border-slate-700 overflow-hidden ${p.is_holi_special ? 'ring-2 ring-pink-500' : ''} ${p.is_internal ? 'ring-2 ring-amber-500' : ''}`}>
            <div className="relative">
              <ImageSlider images={p.images?.length > 0 ? p.images : (p.image_url ? [{ url: p.image_url }] : [])} />
              {/* New Product Badge */}
              {isNewProduct(p.created_at) && (
                <Badge className="absolute top-2 left-2 bg-green-500 text-white animate-pulse">✨ NEW</Badge>
              )}
              {p.is_internal && <Badge className="absolute top-2 right-12 bg-amber-600 text-white">🔒 Internal</Badge>}
              {p.is_holi_special && <Badge className="absolute top-2 right-2 bg-pink-500">🎨 Holi</Badge>}
              {p.product_type !== 'single' && !isNewProduct(p.created_at) && !p.is_internal && <Badge className="absolute top-2 left-2 bg-purple-500 capitalize">{p.product_type}</Badge>}
            </div>
            <CardContent className="p-4">
              <h3 className="text-white font-semibold truncate">{p.name}</h3>
              <p className="text-slate-400 text-sm truncate">{p.description}</p>
              <div className="flex justify-between mt-2">
                <span className="text-[#D4AF37] font-bold">₹{p.price}<span className="ml-2 text-purple-400 text-xs font-semibold">{p.point_value || 0} PV</span></span>
                <span className="text-slate-400 text-sm">{p.stock} {p.unit}</span>
              </div>
              {p.category_name && <Badge className="mt-2 bg-slate-700">{p.category_name}</Badge>}
              {/* Sizes */}
              {p.sizes?.length > 0 && (
                <div className="flex gap-1 mt-2 flex-wrap">
                  {p.sizes.map(size => (
                    <Badge key={size} className="bg-blue-600/30 text-blue-300 text-xs">{size}</Badge>
                  ))}
                </div>
              )}
              {/* Color dots */}
              {p.images?.some(img => img?.color && img.color !== 'default') && (
                <div className="flex gap-1 mt-2">
                  {[...new Set(p.images.filter(img => img?.color && img.color !== 'default').map(img => img.color))].map(color => (
                    <span key={color} className="w-4 h-4 rounded-full border border-slate-600" style={{ backgroundColor: COLOR_OPTIONS.find(c => c.value === color)?.hex }} title={color} />
                  ))}
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="flex-1 border-slate-600" onClick={() => { setEditProduct({...p}); setEditDialogOpen(true); }}>
                  <Edit className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="destructive" onClick={() => deleteProduct(p.id)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {sortedProducts.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          {filterHoli ? "No Holi Special products. Add some!" : "No products found. Add your first product!"}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl">
          <DialogHeader><DialogTitle className="text-gray-900 text-xl font-bold">Edit Product</DialogTitle></DialogHeader>
          {editProduct && <ProductForm product={editProduct} setProduct={setEditProduct} onSubmit={handleUpdate} isEdit={true} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Offline Purchases Management
function OfflinePurchasesManagement() {
  const [purchases, setPurchases] = useState([]);
  const [members, setMembers] = useState([]);
  const [newPurchase, setNewPurchase] = useState({
    member_id: "", amount: "", description: "", payment_mode: "cash"
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const { token } = useAuth();

  const fetchPurchases = async () => {
    try {
      const res = await axios.get(`${API}/admin/offline-purchases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchases(res.data);
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API}/admin/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(res.data);
    } catch (err) {
      console.error("Error:", err);
    }
  };

  useEffect(() => {
    fetchPurchases();
    fetchMembers();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/offline-purchases`, {
        ...newPurchase,
        amount: parseFloat(newPurchase.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      if (res.data.carry_forward > 0) {
        toast.info(`₹${res.data.carry_forward} will be carried forward to next month`);
      }
      setNewPurchase({ member_id: "", amount: "", description: "", payment_mode: "cash" });
      setDialogOpen(false);
      fetchPurchases();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add purchase");
    }
  };

  const deletePurchase = async (id) => {
    if (!window.confirm("Delete this purchase record?")) return;
    try {
      await axios.delete(`${API}/admin/offline-purchases/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Purchase deleted");
      fetchPurchases();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const adjustCarryForward = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/offline-purchases/${id}/adjust`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchPurchases();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to adjust");
    }
  };

  const getStatusBadge = (status, carryForward) => {
    switch (status) {
      case "completed": return <Badge className="bg-green-500/20 text-green-400">Completed</Badge>;
      case "excess": return <Badge className="bg-blue-500/20 text-blue-400">Excess ₹{carryForward}</Badge>;
      case "adjusted": return <Badge className="bg-purple-500/20 text-purple-400">Adjusted</Badge>;
      default: return <Badge className="bg-yellow-500/20 text-yellow-400">Pending</Badge>;
    }
  };

  return (
    <div data-testid="admin-offline-purchases">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Offline Purchases</h1>
          <p className="text-slate-400 text-sm">Track purchases made through Office/Franchise</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#E65100] hover:bg-[#D84315] text-white">
              <Plus className="w-4 h-4 mr-2" /> Add Purchase
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Add Offline Purchase</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-slate-300">Member ID</Label>
                <Select value={newPurchase.member_id} onValueChange={(v) => setNewPurchase({ ...newPurchase, member_id: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Select member" />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((m) => (
                      <SelectItem key={m.id} value={m.member_id}>
                        {m.member_id} - {m.name} (Plan {m.plan_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Amount (₹)</Label>
                <Input
                  type="number"
                  value={newPurchase.amount}
                  onChange={(e) => setNewPurchase({ ...newPurchase, amount: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="Enter amount"
                  required
                />
                <p className="text-xs text-slate-400 mt-1">If amount exceeds plan price, excess will be carried forward</p>
              </div>
              <div>
                <Label className="text-slate-300">Payment Mode</Label>
                <Select value={newPurchase.payment_mode} onValueChange={(v) => setNewPurchase({ ...newPurchase, payment_mode: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="bank">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Description (Optional)</Label>
                <Input
                  value={newPurchase.description}
                  onChange={(e) => setNewPurchase({ ...newPurchase, description: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="e.g., Office purchase, Franchise order"
                />
              </div>
              <Button type="submit" className="w-full bg-[#E65100] hover:bg-[#D84315] text-white">
                Add Purchase
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Total Purchases</p>
            <p className="text-2xl font-bold text-white">{purchases.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Total Amount</p>
            <p className="text-2xl font-bold text-[#E65100]">₹{purchases.reduce((sum, p) => sum + p.amount, 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Pending Adjustments</p>
            <p className="text-2xl font-bold text-blue-400">
              ₹{purchases.filter(p => p.status === "excess").reduce((sum, p) => sum + p.carry_forward, 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800">
                <TableHead className="text-slate-400">Member</TableHead>
                <TableHead className="text-slate-400">Plan</TableHead>
                <TableHead className="text-slate-400">Amount</TableHead>
                <TableHead className="text-slate-400">Plan Price</TableHead>
                <TableHead className="text-slate-400">Mode</TableHead>
                <TableHead className="text-slate-400">Month</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {purchases.map((p) => (
                <TableRow key={p.id} className="border-slate-800 hover:bg-slate-800/50">
                  <TableCell>
                    <div>
                      <p className="text-[#E65100] font-mono font-bold">{p.member_id}</p>
                      <p className="text-slate-400 text-xs">{p.member_name}</p>
                    </div>
                  </TableCell>
                  <TableCell><Badge className="bg-[#2E86AB]/20 text-[#2E86AB]">{p.plan_type}</Badge></TableCell>
                  <TableCell className="text-white font-bold">₹{p.amount}</TableCell>
                  <TableCell className="text-slate-300">₹{p.plan_price}</TableCell>
                  <TableCell className="text-slate-300 capitalize">{p.payment_mode}</TableCell>
                  <TableCell className="text-slate-300">{p.month}/{p.year}</TableCell>
                  <TableCell>{getStatusBadge(p.status, p.carry_forward)}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {p.status === "excess" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => adjustCarryForward(p.id)}
                          className="text-blue-400 border-blue-400/30 hover:bg-blue-500/10"
                          title="Adjust to next month"
                        >
                          <Receipt className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deletePurchase(p.id)}
                        className="text-red-400 border-red-400/30 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {purchases.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-400 py-8">
                    No offline purchases recorded yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// Subscriptions Management
function SubscriptionsManagement() {
  const [subscriptions, setSubscriptions] = useState([]);
  const { token } = useAuth();

  const fetchSubscriptions = async () => {
    try {
      const res = await axios.get(`${API}/subscriptions`, { headers: { Authorization: `Bearer ${token}` } });
      setSubscriptions(res.data);
    } catch (err) {
      console.error("Error:", err);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [token]);

  const confirmSubscription = async (id) => {
    try {
      await axios.put(`${API}/subscriptions/${id}/confirm`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Subscription confirmed");
      fetchSubscriptions();
    } catch (err) {
      toast.error("Failed to confirm");
    }
  };

  return (
    <div data-testid="admin-subscriptions">
      <h1 className="text-2xl font-bold text-white mb-6">Subscriptions</h1>
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800">
                <TableHead className="text-slate-400">Member ID</TableHead>
                <TableHead className="text-slate-400">Month/Year</TableHead>
                <TableHead className="text-slate-400">Plan</TableHead>
                <TableHead className="text-slate-400">Amount</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.map((sub) => (
                <TableRow key={sub.id} className="border-slate-800">
                  <TableCell className="text-[#D4AF37] font-mono">{sub.member_id}</TableCell>
                  <TableCell className="text-white">{sub.month}/{sub.year}</TableCell>
                  <TableCell><Badge>{sub.plan_type}</Badge></TableCell>
                  <TableCell className="text-[#D4AF37]">₹{sub.amount}</TableCell>
                  <TableCell>
                    <Badge className={sub.status === "paid" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                      {sub.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {sub.status === "pending" && (
                      <Button size="sm" onClick={() => confirmSubscription(sub.id)} className="bg-green-600 hover:bg-green-700">
                        Confirm Payment
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// Payouts Management
function PayoutsManagement() {
  const [payouts, setPayouts] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [closingPosition, setClosingPosition] = useState('');  // e.g., 922 for ACJ00922
  const [recalculate, setRecalculate] = useState(false);
  const [skipRepurchaseCheck, setSkipRepurchaseCheck] = useState(false);  // Skip repurchase requirement
  const [calculating, setCalculating] = useState(false);
  const [summary, setSummary] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);
  const { token } = useAuth();

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const fetchPayouts = async () => {
    try {
      // Fetch payouts filtered by selected month/year
      const monthKey = `${year}-${String(month).padStart(2, '0')}`;
      const res = await axios.get(`${API}/payouts?month_key=${monthKey}`, { headers: { Authorization: `Bearer ${token}` } });
      setPayouts(res.data);
    } catch (err) {
      console.error("Error:", err);
    }
  };

  useEffect(() => {
    fetchPayouts();
  }, [token, month, year]);

  const calculatePayouts = async () => {
    setCalculating(true);
    setJobProgress("Starting...");
    setSummary(null);
    try {
      let url = `${API}/admin/calculate-payouts?month=${month}&year=${year}`;
      if (closingPosition) {
        url += `&closing_position=${closingPosition}`;
      }
      if (recalculate) {
        url += `&recalculate=true`;
      }
      if (skipRepurchaseCheck) {
        url += `&skip_repurchase_check=true`;
      }
      
      const res = await axios.post(url, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      
      if (res.data.warning) {
        toast.warning(res.data.message);
        setCalculating(false);
        setJobProgress(null);
        return;
      }
      
      const jobId = res.data.job_id;
      if (!jobId) {
        setSummary(res.data);
        toast.success("Payouts calculated!");
        fetchPayouts();
        setCalculating(false);
        setJobProgress(null);
        return;
      }
      
      toast.info("Payout calculation started in background...");
      
      // Poll for job completion
      const pollInterval = setInterval(async () => {
        try {
          const jobRes = await axios.get(`${API}/admin/payout-job/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const job = jobRes.data;
          setJobProgress(job.progress || "Processing...");
          
          if (job.status === "completed") {
            clearInterval(pollInterval);
            setSummary(job);
            setJobProgress(null);
            setCalculating(false);
            toast.success(`${job.payouts_created} payouts created! Total: ₹${job.total_payout_amount?.toLocaleString()}`);
            fetchPayouts();
          } else if (job.status === "failed") {
            clearInterval(pollInterval);
            setJobProgress(null);
            setCalculating(false);
            toast.error(`Calculation failed: ${job.error || "Unknown error"}`);
          }
        } catch (pollErr) {
          console.error("Poll error:", pollErr);
        }
      }, 3000);
      
      // Safety: stop polling after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (calculating) {
          setCalculating(false);
          setJobProgress(null);
          toast.warning("Polling stopped after 5 minutes. Check results manually.");
          fetchPayouts();
        }
      }, 300000);
      
    } catch (err) {
      console.error("Payout calculation error:", err);
      toast.error(err.response?.data?.detail || "Failed to start calculation");
      setCalculating(false);
      setJobProgress(null);
    }
  };

  const markPaid = async (id) => {
    try {
      await axios.put(`${API}/admin/payouts/${id}/pay`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Marked as paid");
      fetchPayouts();
    } catch (err) {
      toast.error("Failed");
    }
  };

  // Export payouts to CSV
  const exportPayoutsCSV = () => {
    if (payouts.length === 0) {
      toast.error("No payouts to export");
      return;
    }

    // CSV headers
    const headers = [
      'S.No',
      'Member ID',
      'Member Name',
      'Phone',
      'Period',
      'Plan Type',
      'AI Team Count',
      'Gross Amount (₹)',
      'Deduction (%)',
      'Net Amount (₹)',
      'Status',
      'Account Holder Name',
      'Bank Name',
      'Account Number',
      'IFSC Code',
      'UPI ID',
      'Created At'
    ];

    // CSV rows
    // CSV rows - prefix account numbers with apostrophe to prevent Excel conversion
    const rows = payouts.map((p, idx) => [
      idx + 1,
      p.member_id || '',
      p.member_name || '',
      `'${p.phone || ''}`,
      `${p.month}/${p.year}`,
      p.plan_type || '',
      p.ai_team_count || p.total_ai_team || 0,
      p.gross_amount?.toFixed(2) || '0.00',
      p.deduction_percent || 10,
      p.net_amount?.toFixed(2) || '0.00',
      p.status || 'pending',
      p.account_holder_name || '',
      p.bank_name || '',
      p.account_number ? `'${p.account_number}` : '',
      p.ifsc_code || '',
      p.upi_id || '',
      p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : ''
    ]);

    // Add total row
    const totalGross = payouts.reduce((sum, p) => sum + (p.gross_amount || 0), 0);
    const totalNet = payouts.reduce((sum, p) => sum + (p.net_amount || 0), 0);
    rows.push(['', '', '', '', '', '', '', totalGross.toFixed(2), '', totalNet.toFixed(2), 'TOTAL', '', '', '', '', '', '']);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payouts_${month}_${year}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${payouts.length} payouts to CSV`);
  };

  // Export only pending payouts
  const exportPendingPayoutsCSV = () => {
    const pendingPayouts = payouts.filter(p => p.status === 'pending');
    if (pendingPayouts.length === 0) {
      toast.error("No pending payouts to export");
      return;
    }

    // CSV headers for bank transfer
    const headers = [
      'S.No',
      'Member ID',
      'Member Name',
      'Net Amount (₹)',
      'Account Holder Name',
      'Bank Name',
      'Account Number',
      'IFSC Code',
      'UPI ID',
      'Phone',
      'KYC Status'
    ];

    // CSV rows - prefix account numbers with apostrophe to prevent Excel conversion
    const rows = pendingPayouts.map((p, idx) => {
      const hasKYC = p.bank_name || p.account_number || p.upi_id;
      return [
        idx + 1,
        p.member_id || '',
        p.member_name || '',
        p.net_amount?.toFixed(2) || '0.00',
        p.account_holder_name || '',
        p.bank_name || '',
        p.account_number ? `'${p.account_number}` : '',
        p.ifsc_code || '',
        p.upi_id || '',
        `'${p.phone || ''}`,
        hasKYC ? 'Complete' : 'Pending'
      ];
    });

    // Calculate total
    const total = pendingPayouts.reduce((sum, p) => sum + (p.net_amount || 0), 0);
    rows.push(['', '', 'TOTAL', total.toFixed(2), '', '', '', '', '', '', '']);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `pending_payouts_${month}_${year}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${pendingPayouts.length} pending payouts to CSV`);
  };

  // Calculate totals
  const totalPending = payouts.filter(p => p.status === 'pending').reduce((sum, p) => sum + (p.net_amount || 0), 0);
  const totalPaid = payouts.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.net_amount || 0), 0);
  const pendingCount = payouts.filter(p => p.status === 'pending').length;

  return (
    <div data-testid="admin-payouts">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Monthly Payouts</h1>
          <p className="text-slate-400 text-sm">Calculate payouts at end of month (30th/31st). Closing extends to 5th.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-2">
            <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger className="w-24 bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => <SelectItem key={m} value={m.toString()}>{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m-1]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="w-20 bg-slate-800 border-slate-700 text-white" />
          </div>
          <Button onClick={calculatePayouts} disabled={calculating} className="bg-[#D4AF37] hover:bg-[#B5952F] text-black" data-testid="calculate-payouts-btn">
            <Calculator className="w-4 h-4 mr-2" /> {calculating ? "Processing..." : "Calculate Payouts"}
          </Button>
        </div>
        {jobProgress && (
          <div className="mt-2 flex items-center gap-2 text-sm text-cyan-400">
            <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
            {jobProgress}
          </div>
        )}
      </div>

      {/* Closing Position Setting */}
      <Card className="bg-slate-900/50 border-slate-800 mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Calculator className="w-4 h-4 text-[#D4AF37]" />
            Monthly Closing Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label className="text-slate-400 text-xs">Closing Member ID (Last ID for this month)</Label>
              <div className="flex gap-2 mt-1">
                <span className="text-slate-500 px-2 py-2 bg-slate-800 rounded-l border border-slate-700">ACJ</span>
                <Input
                  type="number"
                  value={closingPosition}
                  onChange={(e) => setClosingPosition(e.target.value)}
                  placeholder="e.g., 922"
                  className="bg-slate-800 border-slate-700 text-white rounded-l-none"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Leave empty to include all members</p>
            </div>
            <div className="flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={recalculate}
                  onChange={(e) => setRecalculate(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-800 text-[#D4AF37]"
                />
                <span className="text-slate-300 text-sm">Recalculate (delete existing)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={skipRepurchaseCheck}
                  onChange={(e) => setSkipRepurchaseCheck(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-800 text-[#D4AF37]"
                />
                <span className="text-slate-300 text-sm">Skip Repurchase Check</span>
              </label>
            </div>
            {summary && (
              <div className="bg-slate-800/50 p-3 rounded lg:col-span-2">
                <p className="text-xs text-slate-400">Last Calculation Result</p>
                <p className="text-sm text-white">{summary.message}</p>
                <div className="flex flex-wrap gap-4 mt-1 text-xs">
                  <span className="text-green-400">Created: {summary.payouts_created}</span>
                  <span className="text-yellow-400">No Direct: {summary.skipped_no_direct}</span>
                  <span className="text-red-400">Skipped No Repurchase: {summary.skipped_no_repurchase}</span>
                </div>
                {summary.repurchase_stats && (
                  <div className="flex flex-wrap gap-4 mt-2 text-xs border-t border-slate-700 pt-2">
                    <span className="text-green-400">✓ With Repurchase: {summary.repurchase_stats.with_repurchase}</span>
                    <span className="text-orange-400">⚠ Without Repurchase: {summary.repurchase_stats.without_repurchase}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Members Without Repurchase Section */}
      {summary?.repurchase_stats?.without_repurchase_details?.length > 0 && (
        <Card className="bg-orange-500/10 border-orange-500/30 mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-orange-400 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Members Without Repurchase ({summary.repurchase_stats.without_repurchase})
              </CardTitle>
              <Button 
                size="sm" 
                variant="outline" 
                className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                onClick={() => {
                  const data = summary.repurchase_stats.without_repurchase_details;
                  const headers = ['S.No', 'Member ID', 'Name', 'Phone', 'Direct Sponsors'];
                  const rows = data.map((m, i) => [i + 1, m.member_id, m.name, m.phone, m.direct_joinings]);
                  const csvContent = [
                    `Members Without Repurchase - ${month}/${year}`,
                    headers.join(','),
                    ...rows.map(r => r.join(','))
                  ].join('\n');
                  const blob = new Blob([csvContent], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `without_repurchase_${month}_${year}.csv`;
                  a.click();
                  toast.success(`Exported ${data.length} members without repurchase`);
                }}
              >
                <Download className="w-4 h-4 mr-1" /> Export CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-60 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-orange-500/20">
                    <TableHead className="text-orange-400 w-12">S.No</TableHead>
                    <TableHead className="text-orange-400">Member ID</TableHead>
                    <TableHead className="text-orange-400">Name</TableHead>
                    <TableHead className="text-orange-400">Phone</TableHead>
                    <TableHead className="text-orange-400">Direct Sponsors</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.repurchase_stats.without_repurchase_details.map((m, idx) => (
                    <TableRow key={m.member_id} className="border-orange-500/20">
                      <TableCell className="text-slate-400">{idx + 1}</TableCell>
                      <TableCell className="text-orange-400 font-mono">{m.member_id}</TableCell>
                      <TableCell className="text-white">{m.name}</TableCell>
                      <TableCell className="text-slate-300">{m.phone}</TableCell>
                      <TableCell className="text-slate-300">{m.direct_joinings}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Button onClick={exportPayoutsCSV} variant="outline" className="border-green-500/50 text-green-400 hover:bg-green-500/10">
          <Download className="w-4 h-4 mr-2" /> Export All ({payouts.length})
        </Button>
        <Button onClick={exportPendingPayoutsCSV} variant="outline" className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10">
          <Download className="w-4 h-4 mr-2" /> Export Pending ({pendingCount})
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-slate-400 text-xs">{months[month-1]} {year} Payouts</p>
            <p className="text-2xl font-bold text-white">{payouts.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-yellow-400 text-xs">Pending</p>
            <p className="text-2xl font-bold text-yellow-400">₹{totalPending.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-green-400 text-xs">Paid</p>
            <p className="text-2xl font-bold text-green-400">₹{totalPaid.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#D4AF37]/10 border-[#D4AF37]/30">
          <CardContent className="p-4 text-center">
            <p className="text-[#D4AF37] text-xs">Grand Total</p>
            <p className="text-2xl font-bold text-[#D4AF37]">₹{(totalPending + totalPaid).toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Last Calculation Summary */}
      {summary && (
        <Card className="bg-blue-500/10 border-blue-500/30 mb-6">
          <CardContent className="p-4">
            <p className="text-blue-400 font-semibold">Last Calculation Result:</p>
            <p className="text-white text-sm">
              {summary.payouts_created} payouts created | Total: ₹{summary.total_payout_amount?.toLocaleString()} | 
              Skipped (No Repurchase): {summary.skipped_no_repurchase} | Skipped (No Direct): {summary.skipped_no_direct}
            </p>
          </CardContent>
        </Card>
      )}

      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800">
                <TableHead className="text-slate-400 w-12">S.No</TableHead>
                <TableHead className="text-slate-400">Member ID</TableHead>
                <TableHead className="text-slate-400">Period</TableHead>
                <TableHead className="text-slate-400">AI Team</TableHead>
                <TableHead className="text-slate-400">Gross</TableHead>
                <TableHead className="text-slate-400">Deduction</TableHead>
                <TableHead className="text-slate-400">Net</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payouts.map((p, index) => (
                <TableRow key={p.id} className="border-slate-800">
                  <TableCell className="text-slate-500 font-mono text-sm">{index + 1}</TableCell>
                  <TableCell className="text-[#D4AF37] font-mono">{p.member_id}</TableCell>
                  <TableCell className="text-white">{p.month}/{p.year}</TableCell>
                  <TableCell className="text-slate-300">{p.ai_team_count}</TableCell>
                  <TableCell className="text-white">₹{p.gross_amount?.toFixed(2)}</TableCell>
                  <TableCell className="text-red-400">{p.deduction_percent}%</TableCell>
                  <TableCell className="text-[#D4AF37] font-bold">₹{p.net_amount?.toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge className={p.status === "paid" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                      {p.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {p.status === "pending" && (
                      <Button size="sm" onClick={() => markPaid(p.id)} className="bg-green-600 hover:bg-green-700">Pay</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// Inventory Management
function InventoryManagement() {
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [entryData, setEntryData] = useState({ quantity: 1, entry_type: 'in', notes: '' });
  const { token } = useAuth();

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(res.data || []);
    } catch (err) {
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (productId = null) => {
    try {
      let url = `${API}/inventory/history?limit=50`;
      if (productId) url += `&product_id=${productId}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(res.data || []);
    } catch (err) {
      console.error("Error:", err);
    }
  };

  useEffect(() => {
    fetchInventory();
  }, []);

  const openEntryDialog = (product, type) => {
    setSelectedProduct(product);
    setEntryData({ quantity: 1, entry_type: type, notes: '' });
    setEntryDialogOpen(true);
  };

  const handleEntry = async () => {
    try {
      await axios.post(`${API}/inventory/entry`, {
        product_id: selectedProduct.id,
        product_name: selectedProduct.name,
        quantity: parseInt(entryData.quantity),
        entry_type: entryData.entry_type,
        reference_type: 'adjustment',
        notes: entryData.notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Stock ${entryData.entry_type === 'in' ? 'added' : 'reduced'} successfully`);
      setEntryDialogOpen(false);
      fetchInventory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update stock");
    }
  };

  const totalStock = products.reduce((sum, p) => sum + (p.stock || 0), 0);
  const lowStockProducts = products.filter(p => (p.stock || 0) < 10);

  return (
    <div data-testid="admin-inventory">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Stock Inventory</h1>
          <p className="text-slate-400 text-sm">Track products in and out</p>
        </div>
        <Button
          onClick={() => { setShowHistory(!showHistory); if (!showHistory) fetchHistory(); }}
          variant="outline"
          className="text-white border-slate-700"
        >
          {showHistory ? "Hide History" : "View History"}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-slate-400 text-xs">Total Products</p>
            <p className="text-2xl font-bold text-white">{products.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-green-400 text-xs">Total Stock</p>
            <p className="text-2xl font-bold text-green-400">{totalStock}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-red-400 text-xs">Low Stock (&lt;10)</p>
            <p className="text-2xl font-bold text-red-400">{lowStockProducts.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Inventory History */}
      {showHistory && (
        <Card className="bg-slate-900/50 border-slate-800 mb-6">
          <CardContent className="p-4">
            <h3 className="text-white font-semibold mb-3">Recent Transactions</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between text-sm p-2 bg-slate-800/50 rounded">
                  <div>
                    <span className={h.entry_type === 'in' ? 'text-green-400' : 'text-red-400'}>
                      {h.entry_type === 'in' ? '+' : '-'}{h.quantity}
                    </span>
                    <span className="text-white ml-2">{h.product_name}</span>
                  </div>
                  <div className="text-slate-400 text-xs">
                    {h.reference_type} | {new Date(h.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400">Product</TableHead>
                  <TableHead className="text-slate-400">Current Stock</TableHead>
                  <TableHead className="text-slate-400">Total In</TableHead>
                  <TableHead className="text-slate-400">Total Out</TableHead>
                  <TableHead className="text-slate-400">Price</TableHead>
                  <TableHead className="text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} className="border-slate-800">
                    <TableCell className="text-white font-medium">{p.name}</TableCell>
                    <TableCell>
                      <span className={`font-bold ${p.stock < 10 ? 'text-red-400' : 'text-green-400'}`}>
                        {p.stock || 0}
                      </span>
                      {p.unit && <span className="text-slate-500 ml-1">{p.unit}</span>}
                    </TableCell>
                    <TableCell className="text-green-400">+{p.total_in || 0}</TableCell>
                    <TableCell className="text-red-400">-{p.total_out || 0}</TableCell>
                    <TableCell className="text-[#D4AF37]">₹{p.price}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => openEntryDialog(p, 'in')} className="bg-green-600 hover:bg-green-700 text-white">
                          + Add
                        </Button>
                        <Button size="sm" onClick={() => openEntryDialog(p, 'out')} className="bg-red-600 hover:bg-red-700 text-white">
                          - Remove
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Entry Dialog */}
      <Dialog open={entryDialogOpen} onOpenChange={setEntryDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">
              {entryData.entry_type === 'in' ? 'Add Stock' : 'Remove Stock'} - {selectedProduct?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-400">Quantity</Label>
              <Input
                type="number"
                min="1"
                value={entryData.quantity}
                onChange={(e) => setEntryData({...entryData, quantity: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-400">Notes (optional)</Label>
              <Input
                value={entryData.notes}
                onChange={(e) => setEntryData({...entryData, notes: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                placeholder="e.g., New stock received"
              />
            </div>
            <Button onClick={handleEntry} className={`w-full ${entryData.entry_type === 'in' ? 'bg-green-600' : 'bg-red-600'}`}>
              {entryData.entry_type === 'in' ? 'Add Stock' : 'Remove Stock'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Billing Management
function BillingManagement() {
  const { token } = useAuth();
  const [billType, setBillType] = useState("member"); // member | franchise
  const [products, setProducts] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [billings, setBillings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productSearch, setProductSearch] = useState("");
  const [searchMember, setSearchMember] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedFranchise, setSelectedFranchise] = useState("");
  const [cart, setCart] = useState([]);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastBill, setLastBill] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom] = useState({ name: "", price: "", pv: "", qty: 1 });
  const [viewBill, setViewBill] = useState(null);
  const [billDate, setBillDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [dateEdit, setDateEdit] = useState(null); // { billing_id, bill_date }

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products?include_internal=true`);
      setProducts(res.data || []);
    } catch (err) { console.error(err); }
  };
  const fetchFranchises = async () => {
    try {
      const res = await axios.get(`${API}/admin/franchises`, { headers: { Authorization: `Bearer ${token}` } });
      setFranchises(res.data || []);
    } catch (err) { console.error(err); }
  };
  const fetchBillings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/billings?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      setBillings(res.data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { fetchProducts(); fetchFranchises(); fetchBillings(); }, []);

  const searchMemberById = async () => {
    if (!searchMember.trim()) return;
    try {
      const res = await axios.get(`${API}/admin/members?search=${searchMember.trim()}&limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const members = res.data.members || res.data || [];
      if (members.length > 0) { setSelectedMember(members[0]); toast.success(`Found: ${members[0].name}`); }
      else toast.error("Member not found");
    } catch (err) { toast.error("Failed to search member"); }
  };

  const addToCart = (p) => {
    const existing = cart.find(c => c.product_id === p.id);
    if (existing) setCart(cart.map(c => c.product_id === p.id ? { ...c, quantity: c.quantity + 1 } : c));
    else setCart([...cart, { product_id: p.id, product_name: p.name, quantity: 1, unit_price: p.price, point_value: p.point_value || 0 }]);
  };
  const updateQty = (id, qty) => {
    if (qty <= 0) setCart(cart.filter(c => c.product_id !== id));
    else setCart(cart.map(c => c.product_id === id ? { ...c, quantity: qty } : c));
  };
  const addCustomItem = () => {
    if (!custom.name.trim() || !custom.price) { toast.error("Enter name and price"); return; }
    const qty = Math.max(1, parseInt(custom.qty) || 1);
    setCart([...cart, {
      product_id: `custom-${Date.now()}`,
      product_name: custom.name.trim(),
      quantity: qty,
      unit_price: parseFloat(custom.price) || 0,
      point_value: parseFloat(custom.pv) || 0,
      is_custom: true,
    }]);
    setCustom({ name: "", price: "", pv: "", qty: 1 });
    setCustomOpen(false);
    toast.success("Custom item added");
  };
  const toPayloadItems = () => cart.map(c => c.is_custom
    ? { is_custom: true, product_name: c.product_name, unit_price: c.unit_price, point_value: c.point_value, quantity: c.quantity }
    : { product_id: c.product_id, quantity: c.quantity, unit_price: c.unit_price, point_value: c.point_value });

  const saveBillDate = async () => {
    if (!dateEdit?.bill_date) { toast.error("Pick a date"); return; }
    try {
      await axios.put(`${API}/admin/billings/${dateEdit.billing_id}/date`, { bill_date: dateEdit.bill_date }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Bill ${dateEdit.billing_id} date updated`);
      setDateEdit(null);
      fetchBillings();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update date"); }
  };

  const cartTotal = cart.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const cartPV = cart.reduce((s, i) => s + i.quantity * (Number(i.point_value) || 0), 0);
  const selFranchiseObj = franchises.find(f => f.id === selectedFranchise);
  const franchiseCommPct = selFranchiseObj ? (selFranchiseObj.commission_percent ?? selFranchiseObj.member_sales_commission ?? 10) : 0;
  const businessVolume = Math.round(cartPV * 10 * 100) / 100;
  const estCommission = billType === "franchise" ? Math.round(businessVolume * franchiseCommPct / 100 * 100) / 100 : 0;
  const estPayable = Math.round((cartTotal - estCommission) * 100) / 100;

  const resetForm = () => { setCart([]); setSelectedMember(null); setSearchMember(""); setSelectedFranchise(""); setNotes(""); };

  const createBill = async () => {
    if (cart.length === 0) { toast.error("Add at least one product"); return; }
    setSubmitting(true);
    try {
      if (billType === "member") {
        if (!selectedMember) { toast.error("Select a member"); setSubmitting(false); return; }
        const res = await axios.post(`${API}/billing`, {
          member_id: selectedMember.member_id, items: toPayloadItems(), payment_mode: paymentMode, billing_type: "office", bill_date: billDate
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Bill ${res.data.billing_id} created — ₹${res.data.total_amount}`);
        if (res.data.repurchase_updated) toast.success("Monthly repurchase marked done!");
        setLastBill({
          kind: "member", billing_id: res.data.billing_id, name: selectedMember.name,
          phone: selectedMember.phone, to_id: selectedMember.member_id, items: cart,
          total: res.data.total_amount, total_pv: res.data.total_pv ?? cartPV, commission: 0, payable: res.data.total_amount,
        });
      } else {
        if (!selectedFranchise) { toast.error("Select a franchise"); setSubmitting(false); return; }
        const res = await axios.post(`${API}/admin/franchise-bill`, {
          franchise_id: selectedFranchise, items: toPayloadItems(),
          payment_mode: paymentMode, notes, bill_date: billDate
        }, { headers: { Authorization: `Bearer ${token}` } });
        const d = res.data;
        toast.success(`Franchise bill ${d.billing_id} created. Stock transfer sent (pending).`);
        setLastBill({
          kind: "franchise", billing_id: d.billing_id, name: d.franchise_name,
          phone: d.franchise_phone, to_id: selFranchiseObj?.franchise_code, items: cart,
          total: d.subtotal, commission: d.commission_amount, commission_percent: d.commission_percent,
          total_pv: d.total_pv, business_volume: d.business_volume, payable: d.franchise_payable,
        });
      }
      resetForm();
      fetchBillings();
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create bill"); }
    finally { setSubmitting(false); }
  };

  const buildBillText = (b) => {
    const lines = (b.items || []).map(i => `- ${i.product_name} x${i.quantity} (PV ${(Number(i.point_value) || 0) * i.quantity}) = Rs ${(i.quantity * i.unit_price).toLocaleString()}`).join("\n");
    let msg = `*Achraj Services Pvt. Ltd.*\nBill No: ${b.billing_id}\n\n${lines}\n\nTotal: Rs ${(b.total || 0).toLocaleString()}\nTotal PV: ${b.total_pv || 0} PV`;
    if (b.kind === "franchise") {
      msg += `\nBusiness Volume (PV x 10): Rs ${(b.business_volume ?? (b.total_pv || 0) * 10).toLocaleString()}`;
      msg += `\nCommission (${b.commission_percent}% on BV): -Rs ${(b.commission || 0).toLocaleString()}`;
      msg += `\n*Amount Payable: Rs ${(b.payable || 0).toLocaleString()}*`;
      msg += `\n\nStock transfer created — please confirm receipt in your dashboard.`;
    }
    msg += `\n\nThank you!`;
    return msg;
  };

  const sendWhatsApp = (b) => {
    if (!b.phone) { toast.error("No phone number on file"); return; }
    const num = String(b.phone).replace(/\D/g, "");
    window.open(`https://wa.me/91${num}?text=${encodeURIComponent(buildBillText(b))}`, "_blank");
  };

  const printInvoice = (b) => {
    const rows = (b.items || []).map(i => `<tr>
      <td style="padding:8px;border:1px solid #ddd;">${i.product_name}</td>
      <td style="padding:8px;border:1px solid #ddd;text-align:center;">${i.quantity}</td>
      <td style="padding:8px;border:1px solid #ddd;text-align:right;">Rs ${(i.unit_price).toLocaleString()}</td>
      <td style="padding:8px;border:1px solid #ddd;text-align:center;">${(Number(i.point_value) || 0) * i.quantity}</td>
      <td style="padding:8px;border:1px solid #ddd;text-align:right;">Rs ${(i.quantity * i.unit_price).toLocaleString()}</td></tr>`).join("");
    const extra = b.kind === "franchise" ? `
      <div><span>Business Volume (PV x 10)</span><span>Rs ${((b.business_volume ?? (b.total_pv || 0) * 10)).toLocaleString()}</span></div>
      <div><span>Commission (${b.commission_percent}% on BV)</span><span>-Rs ${(b.commission || 0).toLocaleString()}</span></div>
      <div class="grand"><span>Amount Payable</span><span>Rs ${(b.payable || 0).toLocaleString()}</span></div>
      <p class="muted" style="margin-top:8px;">Commission is prepaid (deducted here). No future settlement. A stock transfer has been created (pending franchise confirmation).</p>` : "";
    const html = `<!DOCTYPE html><html><head><title>Bill ${b.billing_id}</title><meta charset="utf-8"/>
      <style>body{font-family:Arial,sans-serif;color:#1a1a1a;padding:32px;max-width:720px;margin:0 auto;}
      h1{margin:0;font-size:22px;color:#B5952F;} .muted{color:#666;font-size:13px;}
      table{width:100%;border-collapse:collapse;margin-top:16px;font-size:13px;}
      th{background:#f4f4f4;padding:8px;border:1px solid #ddd;text-align:left;}
      .totals{margin-top:16px;font-size:14px;} .totals div{display:flex;justify-content:space-between;padding:3px 0;}
      .grand{font-weight:bold;font-size:16px;border-top:2px solid #333;padding-top:8px;margin-top:6px;}
      .head{display:flex;justify-content:space-between;border-bottom:2px solid #B5952F;padding-bottom:12px;}</style></head>
      <body><div class="head"><div><h1>Achraj Services Private Limited</h1><p class="muted">${b.kind === "franchise" ? "Franchise Supply Invoice" : "Member Purchase Invoice"}</p></div>
      <div style="text-align:right;"><p class="muted">Bill No.<br/><strong>${b.billing_id}</strong></p><p class="muted">${new Date().toLocaleDateString()}</p></div></div>
      <div style="margin-top:16px;font-size:13px;"><p><strong>${b.kind === "franchise" ? "Franchise" : "Member"}:</strong> ${b.name || "-"} (${b.to_id || ""})</p></div>
      <table><thead><tr><th>Product</th><th style="text-align:center;">Qty</th><th style="text-align:right;">Unit</th><th style="text-align:center;">PV</th><th style="text-align:right;">Amount</th></tr></thead><tbody>${rows}</tbody></table>
      <div class="totals"><div><span>Total</span><span>Rs ${(b.total || 0).toLocaleString()}</span></div><div><span>Total PV</span><span>${b.total_pv || 0} PV</span></div>${extra}</div>
      <p class="muted" style="margin-top:32px;text-align:center;">Thank you! • Achraj Services Private Limited</p>
      <script>window.onload=function(){window.print();}</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print the invoice"); return; }
    w.document.write(html); w.document.close();
  };

  // Normalise a stored billing doc into the shape used by WhatsApp/print/view
  const toBillObj = (bill) => ({
    kind: bill.billing_type === "franchise" ? "franchise" : "member",
    billing_id: bill.billing_id,
    name: bill.franchise_name || bill.member_name || "-",
    to_id: bill.franchise_code || bill.member_id || "",
    phone: bill.franchise_phone || bill.member_phone,
    items: bill.items || [],
    total: bill.total_amount ?? bill.subtotal ?? 0,
    total_pv: bill.total_pv ?? (bill.items || []).reduce((s, i) => s + (Number(i.point_value) || 0) * i.quantity, 0),
    business_volume: bill.business_volume,
    commission: bill.commission_amount || 0,
    commission_percent: bill.commission_percent,
    payable: bill.franchise_payable ?? bill.total_amount ?? 0,
  });

  const filteredProducts = products.filter(p => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="admin-billing">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Receipt className="w-7 h-7 text-[#D4AF37]" /> Billing</h1>
          <p className="text-slate-400 text-sm mt-1">Bill a member or a franchise. Franchise bills auto-create a stock transfer &amp; deduct prepaid commission.</p>
        </div>
      </div>

      {/* Bill Type Toggle */}
      <div className="flex gap-2">
        <Button onClick={() => { setBillType("member"); setCart([]); }} variant={billType === "member" ? "default" : "outline"}
          className={billType === "member" ? "bg-[#D4AF37] text-black" : "border-slate-600 text-white"} data-testid="billtype-member">
          <Users className="w-4 h-4 mr-1" /> Bill a Member
        </Button>
        <Button onClick={() => { setBillType("franchise"); setCart([]); }} variant={billType === "franchise" ? "default" : "outline"}
          className={billType === "franchise" ? "bg-[#D4AF37] text-black" : "border-slate-600 text-white"} data-testid="billtype-franchise">
          <Store className="w-4 h-4 mr-1" /> Bill a Franchise
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: recipient + products */}
        <Card className="bg-slate-900/60 border-slate-700/50">
          <CardContent className="p-4 space-y-4">
            {billType === "member" ? (
              <div>
                <Label className="text-slate-400 text-xs">Member ID / Name</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={searchMember} onChange={e => setSearchMember(e.target.value)} placeholder="ACJ00001"
                    onKeyDown={e => e.key === "Enter" && searchMemberById()} className="bg-slate-800 border-slate-600 text-white" data-testid="member-search-input" />
                  <Button onClick={searchMemberById} className="bg-slate-700 text-white" data-testid="member-search-btn"><Search className="w-4 h-4" /></Button>
                </div>
                {selectedMember && (
                  <div className="mt-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-sm text-emerald-300" data-testid="selected-member">
                    {selectedMember.name} ({selectedMember.member_id}) • {selectedMember.phone}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <Label className="text-slate-400 text-xs">Franchise</Label>
                <select value={selectedFranchise} onChange={e => setSelectedFranchise(e.target.value)}
                  className="w-full mt-1 bg-slate-800 border border-slate-600 text-white rounded-md px-3 py-2 text-sm" data-testid="franchise-select">
                  <option value="">Select Franchise</option>
                  {franchises.map(f => <option key={f.id} value={f.id}>{f.name} ({f.franchise_code})</option>)}
                </select>
                {selFranchiseObj && (
                  <p className="text-xs text-slate-500 mt-1">Commission: {franchiseCommPct}% on PV • Phone: {selFranchiseObj.phone || "—"}</p>
                )}
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-400 text-xs">Add Products</Label>
                <Button size="sm" variant="outline" className="h-7 border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10" onClick={() => setCustomOpen(true)} data-testid="add-custom-product-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Custom Product
                </Button>
              </div>
              <Input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Search products..."
                className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="product-search" />
              <div className="mt-2 max-h-64 overflow-y-auto space-y-1 pr-1">
                {filteredProducts.map(p => (
                  <div key={p.id} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2">
                    <div>
                      <p className="text-sm text-white">{p.name}</p>
                      <p className="text-xs text-slate-500">₹{p.price} • Stock: {p.stock ?? 0} • PV: {p.point_value || 0}</p>
                    </div>
                    <Button size="sm" onClick={() => addToCart(p)} className="bg-[#D4AF37] text-black h-8" data-testid={`add-product-${p.id}`}><Plus className="w-4 h-4" /></Button>
                  </div>
                ))}
                {filteredProducts.length === 0 && <p className="text-xs text-slate-500 py-4 text-center">No products found</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* RIGHT: cart + summary */}
        <Card className="bg-slate-900/60 border-slate-700/50">
          <CardContent className="p-4 space-y-3">
            <h3 className="text-white font-semibold flex items-center gap-2"><Receipt className="w-4 h-4 text-[#D4AF37]" /> Bill Items</h3>
            {cart.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">No items added yet</p>
            ) : (
              <div className="space-y-2">
                {cart.map(c => (
                  <div key={c.product_id} className="flex items-center justify-between gap-2 bg-slate-800/60 rounded-lg px-3 py-2">
                    <div className="flex-1"><p className="text-sm text-white">{c.product_name}{c.is_custom && <span className="ml-1 text-[10px] text-amber-400">(custom)</span>}</p><p className="text-xs text-slate-500">₹{c.unit_price} each • PV {(Number(c.point_value) || 0)}</p></div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0 border-slate-600 text-white" onClick={() => updateQty(c.product_id, c.quantity - 1)}>-</Button>
                      <span className="text-white w-8 text-center text-sm">{c.quantity}</span>
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0 border-slate-600 text-white" onClick={() => updateQty(c.product_id, c.quantity + 1)}>+</Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => updateQty(c.product_id, 0)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    <p className="text-sm text-[#D4AF37] font-semibold w-20 text-right">₹{(c.quantity * c.unit_price).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-slate-700 pt-3 space-y-1 text-sm">
              <div className="flex justify-between text-slate-300"><span>Total</span><span className="font-semibold" data-testid="cart-total">₹{cartTotal.toLocaleString()}</span></div>
              <div className="flex justify-between text-cyan-400"><span>Total PV</span><span data-testid="cart-pv">{cartPV} PV</span></div>
              {billType === "franchise" && (
                <>
                  <div className="flex justify-between text-slate-400"><span>Business Volume (PV × 10)</span><span data-testid="business-volume">₹{businessVolume.toLocaleString()}</span></div>
                  <div className="flex justify-between text-orange-400"><span>Commission ({franchiseCommPct}% on BV, prepaid)</span><span data-testid="est-commission">-₹{estCommission.toLocaleString()}</span></div>
                  <div className="flex justify-between text-green-400 text-base font-bold"><span>Franchise Payable</span><span data-testid="est-payable">₹{estPayable.toLocaleString()}</span></div>
                </>
              )}
            </div>

            <div>
              <Label className="text-slate-400 text-xs">Bill Date</Label>
              <Input type="date" value={billDate} onChange={e => setBillDate(e.target.value)}
                className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="bill-date-input" />
            </div>

            <div>
              <Label className="text-slate-400 text-xs">Payment Mode</Label>
              <select value={paymentMode} onChange={e => setPaymentMode(e.target.value)}
                className="w-full mt-1 bg-slate-800 border border-slate-600 text-white rounded-md px-3 py-2 text-sm" data-testid="payment-mode">
                <option value="cash">Cash</option><option value="upi">UPI</option><option value="bank">Bank</option><option value="credit">Credit</option>
              </select>
            </div>
            {billType === "franchise" && (
              <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" className="bg-slate-800 border-slate-600 text-white" data-testid="bill-notes" />
            )}

            <Button onClick={createBill} disabled={submitting} className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold h-11" data-testid="create-bill-submit">
              {submitting ? "Creating..." : billType === "franchise" ? "Create Bill & Stock Transfer" : "Create Bill"}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Success card with WhatsApp + Print */}
      {lastBill && (
        <Card className="bg-emerald-500/10 border-emerald-500/30" data-testid="bill-success">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
              <div>
                <p className="text-emerald-300 font-semibold">Bill {lastBill.billing_id} created for {lastBill.name}</p>
                <p className="text-emerald-200/70 text-sm">
                  Total ₹{(lastBill.total || 0).toLocaleString()}
                  {lastBill.kind === "franchise" && ` • Commission -₹${(lastBill.commission || 0).toLocaleString()} • Payable ₹${(lastBill.payable || 0).toLocaleString()}`}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => sendWhatsApp(lastBill)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="send-whatsapp-btn">
                <MessageCircle className="w-4 h-4 mr-1" /> Send on WhatsApp
              </Button>
              <Button onClick={() => printInvoice(lastBill)} variant="outline" className="border-slate-500 text-white" data-testid="print-invoice-btn">
                <FileText className="w-4 h-4 mr-1" /> Print / Download
              </Button>
              <Button onClick={() => setLastBill(null)} variant="ghost" className="text-slate-400">Dismiss</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent bills */}
      <Card className="bg-slate-900/60 border-slate-700/50">
        <CardContent className="p-4">
          <h3 className="text-white font-semibold mb-3">Recent Bills</h3>
          {loading ? <p className="text-slate-500 text-sm">Loading...</p> : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {billings.map(b => (
                <div key={b.id} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-3 py-2 gap-2" data-testid={`bill-row-${b.billing_id}`}>
                  <div className="min-w-0">
                    <span className="font-mono text-sm text-[#D4AF37]">{b.billing_id}</span>
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${b.billing_type === "franchise" ? "bg-purple-500/20 text-purple-300" : "bg-cyan-500/20 text-cyan-300"}`}>{b.billing_type === "franchise" ? "Franchise" : "Member"}</span>
                    <p className="text-xs text-slate-400 mt-0.5 truncate">{b.franchise_name || b.member_name || "-"} • {b.bill_date || (b.created_at || "").split("T")[0]} • {b.total_pv || 0} PV</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <p className="text-green-400 font-bold">₹{(b.franchise_payable ?? b.total_amount ?? 0).toLocaleString()}</p>
                      {b.billing_type === "franchise" && <p className="text-xs text-orange-400">Comm -₹{(b.commission_amount || 0).toLocaleString()}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-8 w-8 p-0 border-slate-600 text-amber-300" title="Edit bill date" onClick={() => setDateEdit({ billing_id: b.billing_id, bill_date: b.bill_date || (b.created_at || "").split("T")[0] })} data-testid={`editdate-bill-${b.billing_id}`}><Calendar className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="h-8 w-8 p-0 border-slate-600 text-cyan-300" title="View" onClick={() => setViewBill(toBillObj(b))} data-testid={`view-bill-${b.billing_id}`}><Eye className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="h-8 w-8 p-0 border-slate-600 text-white" title="Print / Download" onClick={() => printInvoice(toBillObj(b))} data-testid={`print-bill-${b.billing_id}`}><FileText className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="h-8 w-8 p-0 border-green-600 text-green-400" title="Send on WhatsApp" onClick={() => sendWhatsApp(toBillObj(b))} data-testid={`whatsapp-bill-${b.billing_id}`}><MessageCircle className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              ))}
              {billings.length === 0 && <p className="text-slate-500 text-sm text-center py-6">No bills yet</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Custom Product Dialog */}
      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm" data-testid="custom-product-dialog">
          <DialogHeader><DialogTitle className="text-[#D4AF37] flex items-center gap-2"><Plus className="w-5 h-5" /> Custom Product</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-slate-400 text-xs">Product Name *</Label>
              <Input value={custom.name} onChange={e => setCustom({ ...custom, name: e.target.value })} placeholder="e.g. Gift Hamper" className="bg-slate-800 border-slate-600 text-white" data-testid="custom-name" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-slate-400 text-xs">Price ₹ *</Label>
                <Input type="number" value={custom.price} onChange={e => setCustom({ ...custom, price: e.target.value })} className="bg-slate-800 border-slate-600 text-white" data-testid="custom-price" /></div>
              <div><Label className="text-slate-400 text-xs">PV</Label>
                <Input type="number" value={custom.pv} onChange={e => setCustom({ ...custom, pv: e.target.value })} className="bg-slate-800 border-slate-600 text-white" data-testid="custom-pv" /></div>
              <div><Label className="text-slate-400 text-xs">Qty</Label>
                <Input type="number" value={custom.qty} onChange={e => setCustom({ ...custom, qty: e.target.value })} className="bg-slate-800 border-slate-600 text-white" data-testid="custom-qty" /></div>
            </div>
            <Button onClick={addCustomItem} className="w-full bg-[#D4AF37] text-black" data-testid="custom-add-btn">Add to Bill</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Bill Dialog */}
      <Dialog open={!!viewBill} onOpenChange={(o) => !o && setViewBill(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md" data-testid="view-bill-dialog">
          <DialogHeader><DialogTitle className="text-[#D4AF37]">Bill {viewBill?.billing_id}</DialogTitle></DialogHeader>
          {viewBill && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-400">{viewBill.kind === "franchise" ? "Franchise" : "Member"}: <span className="text-white">{viewBill.name} ({viewBill.to_id})</span></p>
              <div className="space-y-1 border-t border-slate-700 pt-2">
                {viewBill.items.map((i, idx) => (
                  <div key={idx} className="flex justify-between text-slate-300">
                    <span>{i.product_name} x{i.quantity} <span className="text-cyan-400 text-xs">({(Number(i.point_value) || 0) * i.quantity} PV)</span></span>
                    <span>₹{(i.quantity * i.unit_price).toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-700 pt-2 space-y-1">
                <div className="flex justify-between text-slate-300"><span>Total</span><span>₹{(viewBill.total || 0).toLocaleString()}</span></div>
                <div className="flex justify-between text-cyan-400"><span>Total PV</span><span>{viewBill.total_pv || 0} PV</span></div>
                {viewBill.kind === "franchise" && <>
                  <div className="flex justify-between text-slate-400"><span>Business Volume (PV×10)</span><span>₹{(viewBill.business_volume ?? (viewBill.total_pv || 0) * 10).toLocaleString()}</span></div>
                  <div className="flex justify-between text-orange-400"><span>Commission ({viewBill.commission_percent}%)</span><span>-₹{(viewBill.commission || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between text-green-400 font-bold"><span>Payable</span><span>₹{(viewBill.payable || 0).toLocaleString()}</span></div>
                </>}
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={() => sendWhatsApp(viewBill)} className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid="view-whatsapp-btn"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</Button>
                <Button onClick={() => printInvoice(viewBill)} variant="outline" className="flex-1 border-slate-500 text-white" data-testid="view-print-btn"><FileText className="w-4 h-4 mr-1" /> Print</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Edit Bill Date Dialog */}
      <Dialog open={!!dateEdit} onOpenChange={(o) => !o && setDateEdit(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-xs" data-testid="edit-date-dialog">
          <DialogHeader><DialogTitle className="text-amber-300 flex items-center gap-2"><Calendar className="w-5 h-5" /> Update Bill Date</DialogTitle></DialogHeader>
          {dateEdit && (
            <div className="space-y-3">
              <p className="text-xs text-slate-400">Bill <span className="font-mono text-[#D4AF37]">{dateEdit.billing_id}</span></p>
              <Input type="date" value={dateEdit.bill_date} onChange={e => setDateEdit({ ...dateEdit, bill_date: e.target.value })} className="bg-slate-800 border-slate-600 text-white" data-testid="edit-date-input" />
              <Button onClick={saveBillDate} className="w-full bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-bill-date-btn">Save Date</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Member Gaps Report Page
function MemberGapsReport() {
  const { token, user } = useAuth();
  const [gapsData, setGapsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fillDialogOpen, setFillDialogOpen] = useState(false);
  const [selectedGap, setSelectedGap] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', plan_type: 'C', sponsor_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [fixing, setFixing] = useState(false);

  const runFixTreePositions = async () => {
    if (!window.confirm("Rebuild Matrix Positions for ALL members? This re-ranks every member into a dense, gap-free matrix (1,2,3...) so every level fills top-down. It runs safely in the background. Safe to run.")) return;
    setFixing(true);
    try {
      const res = await axios.post(`${API}/admin/fix-tree-positions`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data;
      if (d.status === "running") {
        toast.info(`A rebuild is already running (${d.processed || 0} done). Waiting for it to finish...`, { duration: 6000 });
      } else {
        toast.info(`Rebuild started for ${d.total} members. This runs in the background — tracking progress...`, { duration: 6000 });
      }

      // Poll status until done/error (background task, immune to gateway timeout)
      const poll = async () => {
        for (let i = 0; i < 120; i++) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const s = await axios.get(`${API}/admin/rebuild-status`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const st = s.data;
            if (st.status === "done") {
              toast.success(`Matrix rebuilt: ${st.total} members re-ranked densely. Duplicate IDs: ${st.duplicate_count ?? 0}.`, { duration: 9000 });
              fetchGaps();
              return;
            }
            if (st.status === "error") {
              toast.error(`Rebuild failed: ${st.error || "unknown error"}`, { duration: 9000 });
              return;
            }
            // still running - optional progress
          } catch (e) { /* keep polling */ }
        }
        toast.info("Rebuild is taking a while. Refresh the page shortly to see updated positions.", { duration: 8000 });
      };
      await poll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start matrix rebuild");
    } finally {
      setFixing(false);
    }
  };

  const fetchGaps = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/member-gaps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGapsData(res.data || { gaps: [], inactive_members: [], gaps_count: 0, inactive_count: 0 });
    } catch (err) {
      console.error("Error fetching gaps:", err);
      toast.error("Failed to fetch gaps report");
      setGapsData({ gaps: [], inactive_members: [], gaps_count: 0, inactive_count: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchGaps();
    }
  }, [token]);

  const openFillDialog = (gap) => {
    setSelectedGap(gap);
    setFormData({ name: '', email: '', phone: '', plan_type: 'C', sponsor_id: '' });
    setFillDialogOpen(true);
  };

  const fillGap = async () => {
    if (!selectedGap) return;
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/fill-member-gap`, {
        gap_position: selectedGap.position,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        plan_type: formData.plan_type,
        sponsor_id: formData.sponsor_id || null
      }, { headers: { Authorization: `Bearer ${token}` } });

      const successMsg = `${res.data.message}. Password: ${res.data.password}`;
      
      // Close dialog first
      setFillDialogOpen(false);
      setSelectedGap(null);
      setFormData({ name: '', email: '', phone: '', plan_type: 'C', sponsor_id: '' });
      
      // Show success toast
      toast.success(successMsg);
      
      // Refresh gaps data with delay to allow DB update
      setTimeout(() => {
        fetchGaps();
      }, 1000);
    } catch (err) {
      console.error("Fill gap error:", err);
      toast.error(err.response?.data?.detail || "Failed to fill gap");
    } finally {
      setSubmitting(false);
    }
  };

  // Check if user is primary admin
  if (user?.admin_type === 'secondary') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="bg-red-500/10 border-red-500/30 p-8">
          <p className="text-red-400 text-center">Access Denied: Primary Admin Only</p>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="member-gaps-report">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Member Gaps Report</h1>
          <p className="text-slate-400 text-sm">View and fill gaps in the AI team member sequence</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={runFixTreePositions} disabled={fixing} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="fix-tree-positions-btn">
            <AlertTriangle className="w-4 h-4 mr-2" /> {fixing ? "Rebuilding..." : "Rebuild Matrix Positions"}
          </Button>
          <Button onClick={() => fetchGaps()} variant="outline" className="border-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <Card className="bg-amber-500/10 border-amber-500/30 mb-6">
        <CardContent className="p-4">
          <p className="text-amber-300 text-sm">
            <strong>Team not showing in a member's levels?</strong> Click <strong>Rebuild Matrix Positions</strong> above.
            It re-ranks every member into a dense, gap-free matrix (positions 1,2,3...) ordered by member ID, so every level
            fills top-down. Then it reports any duplicate IDs that still need cleanup.
          </p>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-400">Loading gaps report...</p>
        </div>
      ) : gapsData ? (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-4 text-center">
                <p className="text-slate-400 text-xs">Total Registered</p>
                <p className="text-2xl font-bold text-white">{gapsData.total_registered}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-4 text-center">
                <p className="text-slate-400 text-xs">Max Position</p>
                <p className="text-2xl font-bold text-[#D4AF37]">{gapsData.max_position}</p>
              </CardContent>
            </Card>
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-4 text-center">
                <p className="text-red-400 text-xs">Missing Gaps</p>
                <p className="text-2xl font-bold text-red-400">{gapsData.gaps_count}</p>
              </CardContent>
            </Card>
            <Card className="bg-orange-500/10 border-orange-500/30">
              <CardContent className="p-4 text-center">
                <p className="text-orange-400 text-xs">Inactive Members</p>
                <p className="text-2xl font-bold text-orange-400">{gapsData.inactive_count}</p>
              </CardContent>
            </Card>
          </div>

          {/* Gaps Table */}
          {gapsData.gaps_count > 0 && (
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader className="border-b border-slate-800">
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Missing Member IDs ({gapsData.gaps_count})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800">
                      <TableHead className="text-slate-400">Position</TableHead>
                      <TableHead className="text-slate-400">Expected ID</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {gapsData.gaps.map((gap) => (
                      <TableRow key={gap.position} className="border-slate-800">
                        <TableCell className="text-white font-mono">{gap.position}</TableCell>
                        <TableCell className="text-[#D4AF37] font-mono">{gap.expected_member_id}</TableCell>
                        <TableCell>
                          <Badge className="bg-red-500/20 text-red-400">MISSING</Badge>
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            onClick={() => openFillDialog(gap)}
                            className="h-7 bg-green-600 hover:bg-green-700 text-white text-xs"
                          >
                            <Plus className="w-3 h-3 mr-1" /> Fill Gap
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {gapsData.has_more_gaps && (
                  <p className="text-slate-400 text-sm p-4 text-center">
                    Showing first 100 gaps. {gapsData.gaps_count - 100} more gaps exist.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Inactive Members Table */}
          {gapsData.inactive_count > 0 && (
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader className="border-b border-slate-800">
                <CardTitle className="text-white flex items-center gap-2">
                  <Ban className="w-5 h-5 text-orange-400" />
                  Inactive Members ({gapsData.inactive_count})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800">
                      <TableHead className="text-slate-400">Position</TableHead>
                      <TableHead className="text-slate-400">Member ID</TableHead>
                      <TableHead className="text-slate-400">Name</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {gapsData.inactive_members.map((m) => (
                      <TableRow key={m.position} className="border-slate-800">
                        <TableCell className="text-white font-mono">{m.position}</TableCell>
                        <TableCell className="text-[#D4AF37] font-mono">{m.member_id}</TableCell>
                        <TableCell className="text-white">{m.name}</TableCell>
                        <TableCell>
                          <Badge className="bg-orange-500/20 text-orange-400">INACTIVE</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {gapsData.gaps_count === 0 && gapsData.inactive_count === 0 && (
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="p-8 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <p className="text-green-400 font-semibold">No gaps or inactive members found!</p>
                <p className="text-slate-400 text-sm">All member IDs are sequential and active.</p>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <p className="text-slate-400">No data available</p>
      )}

      {/* Fill Gap Dialog */}
      <Dialog open={fillDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setSelectedGap(null);
          setFormData({ name: '', email: '', phone: '', plan_type: 'C', sponsor_id: '' });
        }
        setFillDialogOpen(open);
      }}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              Fill Gap: {selectedGap?.expected_member_id || 'Loading...'}
            </DialogTitle>
          </DialogHeader>
          {selectedGap && (
          <div className="space-y-4">
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded p-3 text-center">
              <p className="text-[#D4AF37] text-sm">Position #{selectedGap?.position}</p>
              <p className="text-[#D4AF37] text-lg font-bold">{selectedGap?.expected_member_id}</p>
            </div>

            <div>
              <Label className="text-slate-400">Name*</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Enter member name"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-400">Email*</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="member@example.com"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-400">Phone*</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="9876543210"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-400">Plan Type</Label>
              <Select value={formData.plan_type} onValueChange={(v) => setFormData({...formData, plan_type: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="A">Plan A - ₹4,000</SelectItem>
                  <SelectItem value="B">Plan B - ₹2,000</SelectItem>
                  <SelectItem value="C">Plan C - ₹1,000</SelectItem>
                  <SelectItem value="D">Plan D - ₹500</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-400">Sponsor ID (Optional)</Label>
              <Input
                value={formData.sponsor_id}
                onChange={(e) => setFormData({...formData, sponsor_id: e.target.value.toUpperCase()})}
                placeholder="ACJ00001"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>

            <p className="text-slate-500 text-xs">
              Password will be last 4 digits of phone number
            </p>

            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => setFillDialogOpen(false)}
                className="flex-1 border-slate-700 text-slate-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={fillGap} 
                disabled={submitting || !formData.name || !formData.email || !formData.phone}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {submitting ? 'Creating...' : 'Create Member'}
              </Button>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Franchise Management
function FranchiseManagement() {
  const { token } = useAuth();
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    franchise_code: "",
    name: "",
    owner_name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    password: "",
    direct_sales_commission: 20,
    member_sales_commission: 10,
    commission_percent: 10
  });
  
  // Stock management state
  const [showStockModal, setShowStockModal] = useState(false);
  const [franchiseStock, setFranchiseStock] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [editingStockItem, setEditingStockItem] = useState(null);
  const [stockFormData, setStockFormData] = useState({ quantity: 0, unit_price: 0 });
  const [showAddStockForm, setShowAddStockForm] = useState(false);
  const [newStockItem, setNewStockItem] = useState({ product_name: "", quantity: 1, unit_price: 0 });
  const [products, setProducts] = useState([]);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState("deactivate"); // "deactivate" or "permanent"
  const [franchiseToDelete, setFranchiseToDelete] = useState(null);

  const fetchFranchises = async () => {
    try {
      const res = await axios.get(`${API}/admin/franchises`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFranchises(res.data || []);
    } catch (err) {
      toast.error("Failed to load franchises");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFranchises();
  }, [token]);

  const handleCreate = async () => {
    if (!formData.franchise_code || !formData.name || !formData.owner_name || !formData.phone || !formData.password) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      await axios.post(`${API}/admin/franchises`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Franchise created successfully!");
      setShowCreateModal(false);
      setFormData({
        franchise_code: "",
        name: "",
        owner_name: "",
        phone: "",
        email: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        password: "",
        direct_sales_commission: 20,
        member_sales_commission: 10,
        commission_percent: 10
      });
      fetchFranchises();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create franchise");
    }
  };

  const handleUpdate = async () => {
    if (!selectedFranchise) return;
    
    try {
      const updateData = {
        name: formData.name,
        owner_name: formData.owner_name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        direct_sales_commission: formData.direct_sales_commission,
        member_sales_commission: formData.member_sales_commission,
        commission_percent: formData.commission_percent
      };
      
      // Include password only if provided
      if (formData.password && formData.password.trim()) {
        updateData.password = formData.password.trim();
      }
      
      await axios.put(`${API}/admin/franchises/${selectedFranchise.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Franchise updated successfully!");
      setShowEditModal(false);
      setSelectedFranchise(null);
      fetchFranchises();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update franchise");
    }
  };

  const handleDelete = async (franchise) => {
    setFranchiseToDelete(franchise);
    setDeleteType("deactivate");
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!franchiseToDelete) return;
    
    try {
      if (deleteType === "permanent") {
        await axios.delete(`${API}/admin/franchises/${franchiseToDelete.id}/permanent`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Franchise permanently deleted");
      } else {
        await axios.delete(`${API}/admin/franchises/${franchiseToDelete.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Franchise deactivated");
      }
      setShowDeleteConfirm(false);
      setFranchiseToDelete(null);
      fetchFranchises();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete franchise");
    }
  };

  // Stock Management Functions
  const openStockModal = async (franchise) => {
    setSelectedFranchise(franchise);
    setLoadingStock(true);
    setShowStockModal(true);
    setShowAddStockForm(false);
    
    try {
      const [stockRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/franchises/${franchise.id}/stock`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/products?include_internal=true`)
      ]);
      setFranchiseStock(stockRes.data || []);
      setProducts(productsRes.data || []);
    } catch (err) {
      toast.error("Failed to load stock");
      setFranchiseStock([]);
    } finally {
      setLoadingStock(false);
    }
  };

  const editStockItem = (item) => {
    setEditingStockItem(item);
    setStockFormData({ quantity: item.quantity, unit_price: item.unit_price });
  };

  const saveStockItem = async () => {
    if (!editingStockItem || !selectedFranchise) return;
    
    try {
      await axios.put(
        `${API}/admin/franchises/${selectedFranchise.id}/stock/${editingStockItem.product_id}`,
        stockFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Stock updated");
      setEditingStockItem(null);
      
      // Refresh stock
      const res = await axios.get(`${API}/admin/franchises/${selectedFranchise.id}/stock`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFranchiseStock(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update stock");
    }
  };

  const deleteStockItem = async (item) => {
    if (!window.confirm(`Remove "${item.product_name}" from this franchise's stock?`)) return;
    
    try {
      await axios.delete(
        `${API}/admin/franchises/${selectedFranchise.id}/stock/${item.product_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Stock item removed");
      
      // Refresh stock
      const res = await axios.get(`${API}/admin/franchises/${selectedFranchise.id}/stock`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFranchiseStock(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove stock item");
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products?include_internal=true`);
      setProducts(res.data || []);
    } catch (err) {
      console.error("Failed to fetch products");
    }
  };

  const addStockItem = async () => {
    if (!newStockItem.product_name || !newStockItem.quantity) {
      toast.error("Please fill product name and quantity");
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/franchises/${selectedFranchise.id}/stock`,
        {
          product_id: `custom_${Date.now()}`,
          product_name: newStockItem.product_name,
          quantity: parseInt(newStockItem.quantity),
          unit_price: parseFloat(newStockItem.unit_price) || 0
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Stock item added");
      setNewStockItem({ product_name: "", quantity: 1, unit_price: 0 });
      setShowAddStockForm(false);
      
      // Refresh stock
      const res = await axios.get(`${API}/admin/franchises/${selectedFranchise.id}/stock`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFranchiseStock(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add stock item");
    }
  };

  const addProductToStock = async (product) => {
    try {
      await axios.post(
        `${API}/admin/franchises/${selectedFranchise.id}/stock`,
        {
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          unit_price: product.price
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Added ${product.name} to stock`);
      
      // Refresh stock
      const res = await axios.get(`${API}/admin/franchises/${selectedFranchise.id}/stock`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFranchiseStock(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add product");
    }
  };

  const openEditModal = (franchise) => {
    setSelectedFranchise(franchise);
    setFormData({
      franchise_code: franchise.franchise_code,
      name: franchise.name,
      owner_name: franchise.owner_name,
      phone: franchise.phone,
      email: franchise.email || "",
      address: franchise.address || "",
      city: franchise.city || "",
      state: franchise.state || "",
      pincode: franchise.pincode || "",
      password: "",
      direct_sales_commission: franchise.direct_sales_commission || 20,
      member_sales_commission: franchise.member_sales_commission || 10,
      commission_percent: franchise.commission_percent ?? (franchise.member_sales_commission || 10)
    });
    setShowEditModal(true);
  };

  const filteredFranchises = franchises.filter(f =>
    f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.franchise_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="franchise-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Store className="w-7 h-7 text-[#D4AF37]" />
            Franchise Management
          </h1>
          <p className="text-slate-400 text-sm mt-1">Manage franchise stores and their settings</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
          data-testid="create-franchise-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Franchise
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search by name, code, or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
          <CardContent className="p-4">
            <p className="text-green-400 text-xs font-medium">Total Franchises</p>
            <p className="text-2xl font-bold text-white">{franchises.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
          <CardContent className="p-4">
            <p className="text-blue-400 text-xs font-medium">Active</p>
            <p className="text-2xl font-bold text-white">{franchises.filter(f => f.is_active).length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 border-orange-500/30">
          <CardContent className="p-4">
            <p className="text-orange-400 text-xs font-medium">Total Sales</p>
            <p className="text-2xl font-bold text-white">₹{franchises.reduce((sum, f) => sum + (f.total_sales || 0), 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
          <CardContent className="p-4">
            <p className="text-purple-400 text-xs font-medium">Total Commission</p>
            <p className="text-2xl font-bold text-white">₹{franchises.reduce((sum, f) => sum + (f.total_commission || 0), 0).toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Franchises Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37] mx-auto"></div>
        </div>
      ) : filteredFranchises.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-12 text-center">
            <Store className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No franchises found</p>
            <Button onClick={() => setShowCreateModal(true)} className="mt-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black">
              <Plus className="w-4 h-4 mr-2" /> Create First Franchise
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredFranchises.map((franchise) => (
            <Card key={franchise.id} className="bg-slate-900/50 border-slate-800 hover:border-[#D4AF37]/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-white">{franchise.name}</h3>
                    <p className="text-[#D4AF37] text-sm font-mono">{franchise.franchise_code}</p>
                  </div>
                  <Badge className={franchise.is_active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                    {franchise.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
                
                <div className="space-y-2 text-sm text-slate-400 mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>{franchise.owner_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <span>{franchise.phone}</span>
                  </div>
                  {franchise.city && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{franchise.city}, {franchise.state}</span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4 text-center">
                  <div className="bg-slate-800 rounded p-2">
                    <p className="text-xs text-slate-400">Direct Sale</p>
                    <p className="font-bold text-green-400">{franchise.direct_sales_commission}%</p>
                  </div>
                  <div className="bg-slate-800 rounded p-2">
                    <p className="text-xs text-slate-400">Member Sale</p>
                    <p className="font-bold text-blue-400">{franchise.member_sales_commission}%</p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-700">
                  <div>
                    <p className="text-xs text-slate-400">Total Sales</p>
                    <p className="font-bold text-white">₹{(franchise.total_sales || 0).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openStockModal(franchise)} className="border-cyan-600/50 text-cyan-400 hover:bg-cyan-600/10" title="View Stock">
                      <Package className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEditModal(franchise)} className="border-slate-600 text-slate-300 hover:bg-slate-800" title="Edit">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(franchise)} className="border-red-600/50 text-red-400 hover:bg-red-600/10" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Franchise Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Store className="w-5 h-5 text-[#D4AF37]" />
              Create New Franchise
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Franchise Code *</Label>
                <Input
                  value={formData.franchise_code}
                  onChange={(e) => setFormData({...formData, franchise_code: e.target.value.toUpperCase()})}
                  placeholder="FRN-JAU-001"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-400">Store Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Achraj Jaunpur Store"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Owner Name *</Label>
                <Input
                  value={formData.owner_name}
                  onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                  placeholder="Rahul Sharma"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-400">Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="9876543210"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-400">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="store@achraj.com"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-400">Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="123 Main Street"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-400">City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder="Jaunpur"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-400">State</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                  placeholder="Uttar Pradesh"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-400">Pincode</Label>
                <Input
                  value={formData.pincode}
                  onChange={(e) => setFormData({...formData, pincode: e.target.value})}
                  placeholder="222001"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Direct Sale Commission (%)</Label>
                <Input
                  type="number"
                  value={formData.direct_sales_commission}
                  onChange={(e) => setFormData({...formData, direct_sales_commission: parseFloat(e.target.value) || 0})}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-400">Member Sale Commission (%)</Label>
                <Input
                  type="number"
                  value={formData.member_sales_commission}
                  onChange={(e) => setFormData({...formData, member_sales_commission: parseFloat(e.target.value) || 0})}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#D4AF37]">PV Commission (%) — HO paid *</Label>
              <Input
                type="number"
                value={formData.commission_percent}
                onChange={(e) => setFormData({...formData, commission_percent: parseFloat(e.target.value) || 0})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                data-testid="create-commission-percent"
              />
              <p className="text-xs text-slate-500 mt-1">Commission = (Total PV × 10) × this %. Head Office pays this.</p>
            </div>


            <div>
              <Label className="text-slate-400">Password *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="••••••••"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">This will be used for franchise login</p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1 border-slate-700 text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleCreate} className="flex-1 bg-[#D4AF37] hover:bg-[#B5952F] text-black">
                <Plus className="w-4 h-4 mr-2" /> Create Franchise
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Franchise Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#D4AF37]" />
              Edit Franchise: {formData.franchise_code}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Store Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-400">Owner Name *</Label>
                <Input
                  value={formData.owner_name}
                  onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-400">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-400">Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-400">City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-400">State</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-400">Pincode</Label>
                <Input
                  value={formData.pincode}
                  onChange={(e) => setFormData({...formData, pincode: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Direct Sale Commission (%)</Label>
                <Input
                  type="number"
                  value={formData.direct_sales_commission}
                  onChange={(e) => setFormData({...formData, direct_sales_commission: parseFloat(e.target.value) || 0})}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-400">Member Sale Commission (%)</Label>
                <Input
                  type="number"
                  value={formData.member_sales_commission}
                  onChange={(e) => setFormData({...formData, member_sales_commission: parseFloat(e.target.value) || 0})}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#D4AF37]">PV Commission (%) — HO paid</Label>
              <Input
                type="number"
                value={formData.commission_percent}
                onChange={(e) => setFormData({...formData, commission_percent: parseFloat(e.target.value) || 0})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                data-testid="edit-commission-percent"
              />
              <p className="text-xs text-slate-500 mt-1">Commission = (Total PV × 10) × this %.</p>
            </div>


            <div>
              <Label className="text-slate-400">New Password (leave empty to keep current)</Label>
              <Input
                type="password"
                value={formData.password || ""}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="Enter new password..."
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Only fill this if you want to change the password</p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowEditModal(false)} className="flex-1 border-slate-700 text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleUpdate} className="flex-1 bg-[#D4AF37] hover:bg-[#B5952F] text-black">
                <CheckCircle className="w-4 h-4 mr-2" /> Update Franchise
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock Management Modal */}
      <Dialog open={showStockModal} onOpenChange={setShowStockModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Package className="w-5 h-5 text-cyan-400" />
                Stock Management - {selectedFranchise?.name}
              </span>
              <Button 
                size="sm" 
                onClick={() => setShowAddStockForm(!showAddStockForm)}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Stock
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          {/* Add Stock Form */}
          {showAddStockForm && (
            <div className="bg-slate-800 rounded-lg p-4 mb-4 border border-green-500/30">
              <h4 className="text-green-400 font-medium mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add Stock Item
              </h4>
              
              {/* Quick Add from Products */}
              <div className="mb-4">
                <Label className="text-slate-400 text-sm">Quick Add from Catalog:</Label>
                <div className="flex flex-wrap gap-2 mt-2 max-h-32 overflow-y-auto">
                  {products.slice(0, 10).map(product => (
                    <Button
                      key={product.id}
                      size="sm"
                      variant="outline"
                      onClick={() => addProductToStock(product)}
                      className="text-slate-300 border-slate-600 hover:bg-slate-700 text-xs"
                    >
                      {product.name} (₹{product.price})
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="border-t border-slate-700 pt-4">
                <Label className="text-slate-400 text-sm">Or Add Custom Item:</Label>
                <div className="grid grid-cols-4 gap-3 mt-2">
                  <div className="col-span-2">
                    <Input
                      placeholder="Product Name"
                      value={newStockItem.product_name}
                      onChange={(e) => setNewStockItem({...newStockItem, product_name: e.target.value})}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={newStockItem.quantity}
                      onChange={(e) => setNewStockItem({...newStockItem, quantity: e.target.value})}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Price"
                      value={newStockItem.unit_price}
                      onChange={(e) => setNewStockItem({...newStockItem, unit_price: e.target.value})}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                </div>
                <Button onClick={addStockItem} className="mt-3 bg-green-600 hover:bg-green-700 text-white">
                  <Plus className="w-4 h-4 mr-1" /> Add Custom Item
                </Button>
              </div>
            </div>
          )}
          
          {loadingStock ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
            </div>
          ) : franchiseStock.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No stock items found</p>
              <p className="text-slate-500 text-sm mt-2">Click "Add Stock" to add items directly or use Stock Transfers</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-slate-400 text-sm">Total Items: <span className="text-white font-bold">{franchiseStock.length}</span></p>
                <p className="text-slate-400 text-sm">Total Value: <span className="text-[#D4AF37] font-bold">₹{franchiseStock.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0).toLocaleString()}</span></p>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">Product</TableHead>
                    <TableHead className="text-slate-400">Quantity</TableHead>
                    <TableHead className="text-slate-400">Unit Price</TableHead>
                    <TableHead className="text-slate-400">Total Value</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {franchiseStock.map((item) => (
                    <TableRow key={item.product_id} className="border-slate-700">
                      <TableCell className="text-white font-medium">{item.product_name}</TableCell>
                      <TableCell>
                        {editingStockItem?.product_id === item.product_id ? (
                          <Input
                            type="number"
                            value={stockFormData.quantity}
                            onChange={(e) => setStockFormData({...stockFormData, quantity: parseInt(e.target.value) || 0})}
                            className="w-20 h-8 bg-slate-700 border-slate-600 text-white"
                          />
                        ) : (
                          <span className="text-cyan-400 font-bold">{item.quantity}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingStockItem?.product_id === item.product_id ? (
                          <Input
                            type="number"
                            value={stockFormData.unit_price}
                            onChange={(e) => setStockFormData({...stockFormData, unit_price: parseFloat(e.target.value) || 0})}
                            className="w-24 h-8 bg-slate-700 border-slate-600 text-white"
                          />
                        ) : (
                          <span className="text-slate-300">₹{item.unit_price}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-[#D4AF37] font-bold">₹{(item.quantity * item.unit_price).toLocaleString()}</TableCell>
                      <TableCell>
                        {editingStockItem?.product_id === item.product_id ? (
                          <div className="flex gap-1">
                            <Button size="sm" onClick={saveStockItem} className="bg-green-600 hover:bg-green-700 h-8 px-2">
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingStockItem(null)} className="border-slate-600 h-8 px-2">
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => editStockItem(item)} className="border-cyan-600/50 text-cyan-400 hover:bg-cyan-600/10 h-8 px-2">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => deleteStockItem(item)} className="border-red-600/50 text-red-400 hover:bg-red-600/10 h-8 px-2">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Delete Franchise
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-slate-300">
              Are you sure you want to delete <span className="text-white font-bold">{franchiseToDelete?.name}</span>?
            </p>
            
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg cursor-pointer border border-slate-700 hover:border-yellow-500/50">
                <input
                  type="radio"
                  name="deleteType"
                  checked={deleteType === "deactivate"}
                  onChange={() => setDeleteType("deactivate")}
                  className="w-4 h-4 accent-yellow-500"
                />
                <div>
                  <p className="text-yellow-400 font-medium">Deactivate Only</p>
                  <p className="text-slate-500 text-sm">Franchise will be marked inactive but data preserved</p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg cursor-pointer border border-slate-700 hover:border-red-500/50">
                <input
                  type="radio"
                  name="deleteType"
                  checked={deleteType === "permanent"}
                  onChange={() => setDeleteType("permanent")}
                  className="w-4 h-4 accent-red-500"
                />
                <div>
                  <p className="text-red-400 font-medium">Permanent Delete</p>
                  <p className="text-slate-500 text-sm">Delete franchise and ALL related data (stock, sales, transfers)</p>
                </div>
              </label>
            </div>
            
            {deleteType === "permanent" && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-400 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  This action cannot be undone!
                </p>
              </div>
            )}
            
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="flex-1 border-slate-700 text-slate-300">
                Cancel
              </Button>
              <Button 
                onClick={confirmDelete} 
                className={deleteType === "permanent" ? "flex-1 bg-red-600 hover:bg-red-700 text-white" : "flex-1 bg-yellow-600 hover:bg-yellow-700 text-black"}
              >
                {deleteType === "permanent" ? "Delete Permanently" : "Deactivate"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Franchise Billing Management - Create/Edit backdated bills, daily reports
function FranchiseBillingManagement() {
  const { token } = useAuth();
  const [franchises, setFranchises] = useState([]);
  const [allSales, setAllSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("history"); // history, create, report
  const [selectedFranchise, setSelectedFranchise] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [dailyReport, setDailyReport] = useState(null);
  const [reportDate, setReportDate] = useState(new Date().toISOString().split("T")[0]);
  
  // Create bill states
  const [billForm, setBillForm] = useState({
    franchise_id: "", sale_type: "direct", member_id: "", customer_name: "", customer_phone: "",
    payment_mode: "cash", notes: "", bill_date: new Date().toISOString().split("T")[0],
    discount: 0, gst_percent: 0
  });
  const [billProducts, setBillProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({ product_name: "", quantity: 1, unit_price: 0, point_value: 0 });
  
  // Edit bill states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  // View bill detail
  const [viewBill, setViewBill] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const [franchisesRes] = await Promise.all([
        axios.get(`${API}/admin/franchises`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setFranchises(franchisesRes.data || []);
      await fetchSales();
    } catch (err) { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const fetchSales = async (franchiseId) => {
    try {
      let url = `${API}/admin/franchise-sales`;
      const params = [];
      if (franchiseId || selectedFranchise) params.push(`franchise_id=${franchiseId || selectedFranchise}`);
      if (filterDate) params.push(`date=${filterDate}`);
      if (filterStart) params.push(`start_date=${filterStart}`);
      if (filterEnd) params.push(`end_date=${filterEnd}`);
      if (params.length > 0) url += "?" + params.join("&");
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setAllSales(res.data || []);
    } catch (err) { console.error(err); }
  };

  const exportSalesCSV = () => {
    if (!filteredSales.length) { toast.error("No bills to export"); return; }
    const headers = [
      "Bill Number", "Bill Date", "Franchise", "Franchise Code", "Sale Type",
      "Member ID", "Customer/Member Name", "Payment Mode", "Items",
      "Subtotal", "Discount", "GST %", "GST Amount", "Total Amount",
      "Total PV", "Commission %", "Commission Amount", "Notes"
    ];
    const esc = (v) => {
      const s = (v === null || v === undefined) ? "" : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const rows = filteredSales.map(s => [
      s.bill_number || "",
      s.bill_date || (s.created_at || "").split("T")[0],
      s.franchise_name || "",
      s.franchise_code || "",
      s.sale_type || "",
      s.member_id || "",
      s.member_name || s.customer_name || "",
      s.payment_mode || "",
      (s.products || []).length,
      s.subtotal ?? s.total_amount ?? 0,
      s.discount || 0,
      s.gst_percent || 0,
      s.gst_amount || 0,
      s.total_amount || 0,
      s.total_pv || 0,
      s.commission_percent || 0,
      s.commission_amount || 0,
      (s.notes || "").replace(/\n/g, " ")
    ].map(esc).join(","));
    // Totals summary row
    const totAmount = filteredSales.reduce((a, s) => a + (s.total_amount || 0), 0);
    const totComm = filteredSales.reduce((a, s) => a + (s.commission_amount || 0), 0);
    const totPV = filteredSales.reduce((a, s) => a + (s.total_pv || 0), 0);
    const totalRow = ["TOTAL", "", "", "", "", "", "", "", filteredSales.length, "", "", "", "", totAmount, totPV, "", totComm, ""].map(esc).join(",");
    const csv = [headers.map(esc).join(","), ...rows, totalRow].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const rangeLabel = filterStart || filterEnd ? `_${filterStart || "start"}_to_${filterEnd || "end"}` : (filterDate ? `_${filterDate}` : "");
    link.href = URL.createObjectURL(blob);
    link.download = `franchise-billing${rangeLabel}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filteredSales.length} bill(s) to CSV`);
  };

  const fetchDailyReport = async () => {
    try {
      let url = `${API}/admin/franchise-daily-report?date=${reportDate}`;
      if (selectedFranchise) url += `&franchise_id=${selectedFranchise}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setDailyReport(res.data);
    } catch (err) { toast.error("Failed to fetch report"); }
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (activeView === "report") fetchDailyReport(); }, [reportDate, selectedFranchise, activeView]);

  const addProductToBill = () => {
    if (!newProduct.product_name || !newProduct.unit_price) { toast.error("Fill product details"); return; }
    setBillProducts([...billProducts, { ...newProduct, product_id: `custom_${Date.now()}` }]);
    setNewProduct({ product_name: "", quantity: 1, unit_price: 0, point_value: 0 });
  };

  const removeProductFromBill = (idx) => setBillProducts(billProducts.filter((_, i) => i !== idx));

  const exportBill = (sale) => {
    const items = (sale.products || []).map(p => {
      const qty = p.quantity || 0;
      const up = p.unit_price || 0;
      const pv = (p.point_value || 0) * qty;
      return `<tr>
        <td style="padding:8px;border:1px solid #ddd;">${p.product_name || "-"}</td>
        <td style="padding:8px;border:1px solid #ddd;text-align:center;">${qty}</td>
        <td style="padding:8px;border:1px solid #ddd;text-align:right;">₹${up.toLocaleString()}</td>
        <td style="padding:8px;border:1px solid #ddd;text-align:center;">${pv || 0}</td>
        <td style="padding:8px;border:1px solid #ddd;text-align:right;">₹${(up * qty).toLocaleString()}</td>
      </tr>`;
    }).join("");
    const html = `<!DOCTYPE html><html><head><title>Bill ${sale.bill_number || ""}</title>
      <meta charset="utf-8" />
      <style>body{font-family:Arial,sans-serif;color:#1a1a1a;padding:32px;max-width:720px;margin:0 auto;}
      h1{margin:0;font-size:22px;color:#B5952F;} .muted{color:#666;font-size:13px;}
      table{width:100%;border-collapse:collapse;margin-top:16px;font-size:13px;}
      th{background:#f4f4f4;padding:8px;border:1px solid #ddd;text-align:left;}
      .totals{margin-top:16px;font-size:14px;} .totals div{display:flex;justify-content:space-between;padding:3px 0;}
      .grand{font-weight:bold;font-size:16px;border-top:2px solid #333;padding-top:8px;margin-top:6px;}
      .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #B5952F;padding-bottom:12px;}</style></head>
      <body>
        <div class="head">
          <div><h1>Achraj Services Private Limited</h1><p class="muted">Franchise Sale Invoice</p></div>
          <div style="text-align:right;"><p class="muted">Bill No.<br/><strong>${sale.bill_number || "-"}</strong></p>
          <p class="muted">Date: ${sale.bill_date || (sale.created_at || "").split("T")[0]}</p></div>
        </div>
        <div style="margin-top:16px;font-size:13px;">
          <p><strong>Franchise:</strong> ${sale.franchise_name || "-"} (${sale.franchise_code || ""})</p>
          <p><strong>${sale.sale_type === "member" ? "Member" : "Customer"}:</strong> ${sale.member_name || sale.customer_name || "-"} ${sale.member_id ? "(" + sale.member_id + ")" : ""}</p>
          <p><strong>Payment Mode:</strong> ${(sale.payment_mode || "-").toUpperCase()} &nbsp;|&nbsp; <strong>Type:</strong> ${sale.sale_type}</p>
        </div>
        <table>
          <thead><tr><th>Product</th><th style="text-align:center;">Qty</th><th style="text-align:right;">Unit Price</th><th style="text-align:center;">PV</th><th style="text-align:right;">Amount</th></tr></thead>
          <tbody>${items}</tbody>
        </table>
        <div class="totals">
          <div><span>Subtotal</span><span>₹${(sale.subtotal || sale.total_amount || 0).toLocaleString()}</span></div>
          ${sale.discount ? `<div><span>Discount</span><span>-₹${sale.discount.toLocaleString()}</span></div>` : ""}
          ${sale.gst_amount ? `<div><span>GST (${sale.gst_percent || 0}%)</span><span>₹${sale.gst_amount.toLocaleString()}</span></div>` : ""}
          <div class="grand"><span>Total (Business Volume)</span><span>₹${(sale.total_amount || 0).toLocaleString()}</span></div>
          ${sale.total_pv ? `<div><span>Total Point Value</span><span>${sale.total_pv} PV</span></div>` : ""}
          <div><span>Commission (${sale.commission_percent || 0}%)</span><span>₹${(sale.commission_amount || 0).toLocaleString()}</span></div>
        </div>
        ${sale.notes ? `<p class="muted" style="margin-top:16px;">Note: ${sale.notes}</p>` : ""}
        <p class="muted" style="margin-top:32px;text-align:center;">Thank you! • Achraj Services Private Limited</p>
        <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to export the bill"); return; }
    w.document.write(html);
    w.document.close();
  };

  const billSubtotal = billProducts.reduce((sum, p) => sum + p.quantity * p.unit_price, 0);
  const billTotalPV = billProducts.reduce((sum, p) => sum + p.quantity * (Number(p.point_value) || 0), 0);
  const billGst = Math.round((billSubtotal - billForm.discount) * billForm.gst_percent / 100 * 100) / 100;
  const billTotal = billSubtotal - billForm.discount + billGst;

  const createBackdatedBill = async () => {
    if (!billForm.franchise_id) { toast.error("Select a franchise"); return; }
    if (billProducts.length === 0) { toast.error("Add products"); return; }
    if (billForm.sale_type === "direct" && !billForm.customer_name) { toast.error("Enter customer name"); return; }
    if (billForm.sale_type === "member" && !billForm.member_id) { toast.error("Enter member ID"); return; }

    try {
      const payload = {
        ...billForm,
        products: billProducts.map(p => ({ product_id: p.product_id, product_name: p.product_name, quantity: p.quantity, unit_price: p.unit_price, point_value: Number(p.point_value) || 0 }))
      };
      const res = await axios.post(`${API}/admin/franchise-sales/create`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Bill created! #${res.data.bill_number}`);
      setBillProducts([]);
      setBillForm({ ...billForm, customer_name: "", customer_phone: "", member_id: "", notes: "" });
      fetchSales();
      setActiveView("history");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create bill"); }
  };

  const openEditModal = (sale) => {
    setEditingSale(sale);
    setEditForm({
      bill_date: sale.bill_date || "",
      payment_mode: sale.payment_mode || "cash",
      discount: sale.discount || 0,
      gst_percent: sale.gst_percent || 0,
      notes: sale.notes || "",
      customer_name: sale.customer_name || "",
      customer_phone: sale.customer_phone || ""
    });
    setEditModalOpen(true);
  };

  const saveEditBill = async () => {
    try {
      await axios.put(`${API}/admin/franchise-sales/${editingSale.id}`, editForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Bill updated!");
      setEditModalOpen(false);
      fetchSales();
    } catch (err) { toast.error("Update failed"); }
  };

  const filteredSales = allSales.filter(s => {
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      return (s.bill_number?.toLowerCase().includes(q) || s.customer_name?.toLowerCase().includes(q) || s.member_id?.toLowerCase().includes(q) || s.member_name?.toLowerCase().includes(q));
    }
    return true;
  });

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#D4AF37]"></div></div>;

  return (
    <div className="space-y-6" data-testid="admin-franchise-billing">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-white">Franchise Billing</h2>
        <div className="flex gap-2">
          <Button onClick={() => setActiveView("history")} variant={activeView === "history" ? "default" : "outline"} className={activeView === "history" ? "bg-[#D4AF37] text-black" : "border-slate-600 text-white"} data-testid="view-history-btn">
            <FileText className="w-4 h-4 mr-1" /> History
          </Button>
          <Button onClick={() => setActiveView("create")} variant={activeView === "create" ? "default" : "outline"} className={activeView === "create" ? "bg-[#D4AF37] text-black" : "border-slate-600 text-white"} data-testid="create-bill-btn">
            <Plus className="w-4 h-4 mr-1" /> Create Bill
          </Button>
          <Button onClick={() => setActiveView("report")} variant={activeView === "report" ? "default" : "outline"} className={activeView === "report" ? "bg-[#D4AF37] text-black" : "border-slate-600 text-white"} data-testid="view-report-btn">
            <TrendingUp className="w-4 h-4 mr-1" /> Daily Report
          </Button>
        </div>
      </div>

      {/* Bill History View */}
      {activeView === "history" && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <select value={selectedFranchise} onChange={e => { setSelectedFranchise(e.target.value); fetchSales(e.target.value); }}
              className="bg-slate-800 border border-slate-600 text-white rounded-md px-3 py-2 text-sm" data-testid="franchise-filter">
              <option value="">All Franchises</option>
              {franchises.map(f => <option key={f.id} value={f.id}>{f.name} ({f.franchise_code})</option>)}
            </select>
            <Input type="date" value={filterDate} onChange={e => { setFilterDate(e.target.value); setTimeout(() => fetchSales(), 100); }}
              className="bg-slate-800 border-slate-600 text-white w-40" data-testid="date-filter" title="Single day" />
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-500">Range</span>
              <Input type="date" value={filterStart} onChange={e => { setFilterStart(e.target.value); setTimeout(() => fetchSales(), 100); }}
                className="bg-slate-800 border-slate-600 text-white w-40" data-testid="start-date-filter" title="From date" />
              <span className="text-xs text-slate-500">to</span>
              <Input type="date" value={filterEnd} onChange={e => { setFilterEnd(e.target.value); setTimeout(() => fetchSales(), 100); }}
                className="bg-slate-800 border-slate-600 text-white w-40" data-testid="end-date-filter" title="To date" />
            </div>
            <Input value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder="Search bill#, name..."
              className="bg-slate-800 border-slate-600 text-white w-52" data-testid="search-filter" />
            {(filterDate || filterStart || filterEnd) && (
              <Button variant="outline" size="sm" className="border-slate-600 text-slate-300"
                onClick={() => { setFilterDate(""); setFilterStart(""); setFilterEnd(""); setTimeout(() => fetchSales(), 100); }}
                data-testid="clear-date-filter-btn">Clear Dates</Button>
            )}
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white ml-auto"
              onClick={exportSalesCSV} data-testid="export-billing-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>

          <p className="text-sm text-slate-400">
            {filteredSales.length} bill(s)
            {(filterStart || filterEnd) && <span className="text-slate-500"> · {filterStart || "start"} → {filterEnd || "end"}</span>}
            {" · "}Total: <span className="text-green-400 font-semibold">₹{filteredSales.reduce((a, s) => a + (s.total_amount || 0), 0).toLocaleString()}</span>
          </p>

          <div className="space-y-2">
            {filteredSales.map(sale => (
              <Card key={sale.id} className="bg-slate-900/80 border-slate-700/50 hover:border-slate-500 transition-colors">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-[#D4AF37]">{sale.bill_number || "—"}</span>
                        <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">{sale.franchise_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${sale.sale_type === 'member' ? 'bg-purple-500/20 text-purple-400' : 'bg-cyan-500/20 text-cyan-400'}`}>{sale.sale_type}</span>
                        {sale.created_by_admin && <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">Admin</span>}
                      </div>
                      <p className="text-sm text-slate-300 mt-1">{sale.member_name || sale.customer_name || "—"} {sale.member_id ? `(${sale.member_id})` : ""}</p>
                      <p className="text-xs text-slate-500">Date: {sale.bill_date || sale.created_at?.split("T")[0]} | {sale.payment_mode} | {sale.products?.length} item(s)</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-400">₹{(sale.total_amount || 0).toLocaleString()}</p>
                        <p className="text-xs text-slate-400">Comm: ₹{(sale.commission_amount || 0).toFixed(0)}</p>
                      </div>
                      <Button size="sm" className="bg-[#D4AF37] text-black hover:bg-[#c5a02f]" onClick={() => setViewBill(sale)} data-testid={`view-bill-${sale.id}`}>
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                      <Button size="sm" variant="outline" className="border-slate-600 text-white" onClick={() => openEditModal(sale)} data-testid={`edit-bill-${sale.id}`}>
                        Edit
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {filteredSales.length === 0 && <div className="text-center py-12 text-slate-500"><FileText className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No bills found</p></div>}
          </div>
        </div>
      )}

      {/* Create Backdated Bill View */}
      {activeView === "create" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader><CardTitle className="text-white text-lg">Bill Details</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div><Label className="text-slate-400 text-xs">Franchise *</Label>
              <select value={billForm.franchise_id} onChange={e => setBillForm({...billForm, franchise_id: e.target.value})}
                className="w-full bg-slate-800 border border-slate-600 text-white rounded-md px-3 py-2 text-sm" data-testid="bill-franchise-select">
                <option value="">Select Franchise</option>
                {franchises.map(f => <option key={f.id} value={f.id}>{f.name} ({f.franchise_code})</option>)}
              </select></div>

              <div><Label className="text-slate-400 text-xs">Bill Date *</Label>
              <Input type="date" value={billForm.bill_date} onChange={e => setBillForm({...billForm, bill_date: e.target.value})}
                className="bg-slate-800 border-slate-600 text-white" data-testid="bill-date-input" /></div>

              <div><Label className="text-slate-400 text-xs">Sale Type</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => setBillForm({...billForm, sale_type: "direct"})} className={billForm.sale_type === "direct" ? "bg-cyan-500" : "bg-slate-700"}>Direct</Button>
                <Button onClick={() => setBillForm({...billForm, sale_type: "member"})} className={billForm.sale_type === "member" ? "bg-purple-500" : "bg-slate-700"}>Member</Button>
              </div></div>

              {billForm.sale_type === "direct" ? (
                <div className="space-y-2">
                  <Input value={billForm.customer_name} onChange={e => setBillForm({...billForm, customer_name: e.target.value})} placeholder="Customer Name *" className="bg-slate-800 border-slate-600 text-white" data-testid="bill-customer-name" />
                  <Input value={billForm.customer_phone} onChange={e => setBillForm({...billForm, customer_phone: e.target.value})} placeholder="Phone (optional)" className="bg-slate-800 border-slate-600 text-white" />
                </div>
              ) : (
                <Input value={billForm.member_id} onChange={e => setBillForm({...billForm, member_id: e.target.value})} placeholder="Member ID (e.g., ACJ00001)" className="bg-slate-800 border-slate-600 text-white" data-testid="bill-member-id" />
              )}

              <div><Label className="text-slate-400 text-xs">Payment Mode</Label>
              <select value={billForm.payment_mode} onChange={e => setBillForm({...billForm, payment_mode: e.target.value})}
                className="w-full bg-slate-800 border border-slate-600 text-white rounded-md px-3 py-2 text-sm">
                <option value="cash">Cash</option><option value="upi">UPI</option><option value="card">Card</option><option value="credit">Credit</option>
              </select></div>

              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-slate-400 text-xs">Discount (₹)</Label>
                <Input type="number" value={billForm.discount} onChange={e => setBillForm({...billForm, discount: Number(e.target.value)})} className="bg-slate-800 border-slate-600 text-white" /></div>
                <div><Label className="text-slate-400 text-xs">GST %</Label>
                <Input type="number" value={billForm.gst_percent} onChange={e => setBillForm({...billForm, gst_percent: Number(e.target.value)})} className="bg-slate-800 border-slate-600 text-white" /></div>
              </div>

              <div><Label className="text-slate-400 text-xs">Notes</Label>
              <Input value={billForm.notes} onChange={e => setBillForm({...billForm, notes: e.target.value})} placeholder="Optional notes" className="bg-slate-800 border-slate-600 text-white" /></div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader><CardTitle className="text-white text-lg">Products</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input value={newProduct.product_name} onChange={e => setNewProduct({...newProduct, product_name: e.target.value})} placeholder="Product Name" className="bg-slate-800 border-slate-600 text-white flex-1" data-testid="new-product-name" />
                <Input type="number" value={newProduct.quantity} onChange={e => setNewProduct({...newProduct, quantity: Number(e.target.value)})} className="bg-slate-800 border-slate-600 text-white w-16" placeholder="Qty" />
                <Input type="number" value={newProduct.unit_price} onChange={e => setNewProduct({...newProduct, unit_price: Number(e.target.value)})} className="bg-slate-800 border-slate-600 text-white w-24" placeholder="Price" />
                <Input type="number" value={newProduct.point_value} onChange={e => setNewProduct({...newProduct, point_value: Number(e.target.value)})} className="bg-slate-800 border-slate-600 text-white w-20" placeholder="PV" data-testid="new-product-pv" />
                <Button onClick={addProductToBill} className="bg-cyan-500" data-testid="add-product-btn"><Plus className="w-4 h-4" /></Button>
              </div>

              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {billProducts.map((p, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-800/50 rounded border border-slate-700/30">
                    <div><span className="text-sm text-white">{p.product_name}</span><span className="text-xs text-slate-400 ml-2">x{p.quantity} @ ₹{p.unit_price}</span>{p.point_value > 0 && <span className="text-xs text-purple-400 ml-2">{p.point_value} PV</span>}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-green-400">₹{p.quantity * p.unit_price}</span>
                      <button onClick={() => removeProductFromBill(idx)} className="text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                ))}
              </div>

              {billProducts.length > 0 && (
                <div className="border-t border-slate-700 pt-2 space-y-1">
                  <div className="flex justify-between text-sm"><span className="text-slate-400">Subtotal</span><span className="text-white">₹{billSubtotal.toLocaleString()}</span></div>
                  {billForm.discount > 0 && <div className="flex justify-between text-sm"><span className="text-slate-400">Discount</span><span className="text-red-400">-₹{billForm.discount}</span></div>}
                  {billForm.gst_percent > 0 && <div className="flex justify-between text-sm"><span className="text-slate-400">GST ({billForm.gst_percent}%)</span><span className="text-white">₹{billGst}</span></div>}
                  <div className="flex justify-between text-lg font-bold border-t border-slate-600 pt-1"><span className="text-[#D4AF37]">Total</span><span className="text-green-400">₹{billTotal.toLocaleString()}</span></div>
                  <div className="flex justify-between text-sm font-semibold"><span className="text-purple-300">Total Point Value</span><span className="text-purple-300" data-testid="bill-total-pv">{billTotalPV} PV</span></div>
                </div>
              )}

              <Button onClick={createBackdatedBill} className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-black font-bold" disabled={billProducts.length === 0} data-testid="submit-bill-btn">
                <Receipt className="w-4 h-4 mr-2" /> Create Bill
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Daily Report View */}
      {activeView === "report" && (
        <div className="space-y-4">
          <div className="flex gap-2 items-center">
            <Input type="date" value={reportDate} onChange={e => setReportDate(e.target.value)} className="bg-slate-800 border-slate-600 text-white w-40" data-testid="report-date" />
            <select value={selectedFranchise} onChange={e => setSelectedFranchise(e.target.value)}
              className="bg-slate-800 border border-slate-600 text-white rounded-md px-3 py-2 text-sm">
              <option value="">All Franchises</option>
              {franchises.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <Button onClick={fetchDailyReport} className="bg-[#D4AF37] text-black"><RefreshCw className="w-4 h-4" /></Button>
          </div>

          {dailyReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-slate-900/80 border-slate-700/50"><CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-white">{dailyReport.total_bills}</p><p className="text-xs text-slate-400">Total Bills</p>
                </CardContent></Card>
                <Card className="bg-slate-900/80 border-slate-700/50"><CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-green-400">₹{(dailyReport.total_amount || 0).toLocaleString()}</p><p className="text-xs text-slate-400">Total Revenue</p>
                </CardContent></Card>
                <Card className="bg-slate-900/80 border-slate-700/50"><CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-[#D4AF37]">₹{(dailyReport.total_commission || 0).toLocaleString()}</p><p className="text-xs text-slate-400">Total Commission</p>
                </CardContent></Card>
              </div>

              {dailyReport.franchises?.map(franchise => (
                <Card key={franchise.franchise_id} className="bg-slate-900/80 border-slate-700/50">
                  <CardHeader className="pb-2"><CardTitle className="text-white text-base flex items-center justify-between">
                    <span><Store className="w-4 h-4 inline mr-2 text-cyan-400" />{franchise.franchise_name} ({franchise.franchise_code})</span>
                    <span className="text-green-400">₹{franchise.total_amount.toLocaleString()}</span>
                  </CardTitle></CardHeader>
                  <CardContent className="space-y-1">
                    {franchise.bills.map(bill => (
                      <div key={bill.id} className="flex items-center justify-between py-1 text-sm border-b border-slate-800 last:border-0">
                        <div>
                          <span className="text-[#D4AF37] font-mono">{bill.bill_number}</span>
                          <span className="text-slate-300 ml-2">{bill.member_name || bill.customer_name}</span>
                          <span className="text-xs text-slate-500 ml-2">{bill.payment_mode}</span>
                        </div>
                        <span className="font-medium text-green-400">₹{bill.total_amount}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
              {(!dailyReport.franchises || dailyReport.franchises.length === 0) && (
                <div className="text-center py-8 text-slate-500"><Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No bills on this date</p></div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Edit Bill Modal */}
      {editModalOpen && editingSale && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEditModalOpen(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()} data-testid="edit-bill-modal">
            <h3 className="text-lg font-bold text-white">Edit Bill #{editingSale.bill_number}</h3>
            <div><Label className="text-slate-400 text-xs">Bill Date</Label>
            <Input type="date" value={editForm.bill_date} onChange={e => setEditForm({...editForm, bill_date: e.target.value})} className="bg-slate-800 border-slate-600 text-white" /></div>
            <div><Label className="text-slate-400 text-xs">Payment Mode</Label>
            <select value={editForm.payment_mode} onChange={e => setEditForm({...editForm, payment_mode: e.target.value})} className="w-full bg-slate-800 border border-slate-600 text-white rounded-md px-3 py-2 text-sm">
              <option value="cash">Cash</option><option value="upi">UPI</option><option value="card">Card</option><option value="credit">Credit</option>
            </select></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-slate-400 text-xs">Discount (₹)</Label>
              <Input type="number" value={editForm.discount} onChange={e => setEditForm({...editForm, discount: Number(e.target.value)})} className="bg-slate-800 border-slate-600 text-white" /></div>
              <div><Label className="text-slate-400 text-xs">GST %</Label>
              <Input type="number" value={editForm.gst_percent} onChange={e => setEditForm({...editForm, gst_percent: Number(e.target.value)})} className="bg-slate-800 border-slate-600 text-white" /></div>
            </div>
            {editingSale.sale_type === "direct" && (
              <div className="space-y-2">
                <Input value={editForm.customer_name} onChange={e => setEditForm({...editForm, customer_name: e.target.value})} placeholder="Customer Name" className="bg-slate-800 border-slate-600 text-white" />
                <Input value={editForm.customer_phone} onChange={e => setEditForm({...editForm, customer_phone: e.target.value})} placeholder="Phone" className="bg-slate-800 border-slate-600 text-white" />
              </div>
            )}
            <div><Label className="text-slate-400 text-xs">Notes</Label>
            <Input value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} className="bg-slate-800 border-slate-600 text-white" /></div>
            <div className="flex gap-2">
              <Button onClick={saveEditBill} className="flex-1 bg-[#D4AF37] text-black" data-testid="save-edit-btn">Save Changes</Button>
              <Button onClick={() => setEditModalOpen(false)} variant="outline" className="border-slate-600 text-white">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* View Bill Detail Modal */}
      {viewBill && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setViewBill(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="view-bill-modal">
            <div className="flex items-start justify-between p-5 border-b border-slate-700 sticky top-0 bg-slate-900 z-10">
              <div>
                <h3 className="text-lg font-bold text-[#D4AF37]">Bill #{viewBill.bill_number || "—"}</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {viewBill.franchise_name} ({viewBill.franchise_code || ""}) • {viewBill.bill_date || viewBill.created_at?.split("T")[0]}
                </p>
              </div>
              <button onClick={() => setViewBill(null)} className="text-slate-400 hover:text-white" data-testid="close-view-bill-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Customer / Member info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-800/60 rounded-lg p-3">
                  <p className="text-xs text-slate-500">{viewBill.sale_type === "member" ? "Member" : "Customer"}</p>
                  <p className="text-white font-medium">{viewBill.member_name || viewBill.customer_name || "—"} {viewBill.member_id ? `(${viewBill.member_id})` : ""}</p>
                  {viewBill.customer_phone && <p className="text-xs text-slate-400">{viewBill.customer_phone}</p>}
                </div>
                <div className="bg-slate-800/60 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Payment / Type</p>
                  <p className="text-white font-medium">{(viewBill.payment_mode || "—").toUpperCase()} • {viewBill.sale_type}</p>
                </div>
              </div>

              {/* Items table */}
              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800 text-slate-400 text-xs">
                    <tr>
                      <th className="text-left p-2.5">Product</th>
                      <th className="text-center p-2.5">Qty</th>
                      <th className="text-right p-2.5">Unit ₹</th>
                      <th className="text-center p-2.5">PV</th>
                      <th className="text-right p-2.5">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewBill.products || []).map((p, i) => (
                      <tr key={i} className="border-t border-slate-700/60" data-testid={`view-bill-item-${i}`}>
                        <td className="p-2.5 text-white">{p.product_name || "—"}</td>
                        <td className="p-2.5 text-center text-slate-300">{p.quantity || 0}</td>
                        <td className="p-2.5 text-right text-slate-300">₹{(p.unit_price || 0).toLocaleString()}</td>
                        <td className="p-2.5 text-center text-slate-300">{((p.point_value || 0) * (p.quantity || 0)) || 0}</td>
                        <td className="p-2.5 text-right text-white">₹{((p.unit_price || 0) * (p.quantity || 0)).toLocaleString()}</td>
                      </tr>
                    ))}
                    {(viewBill.products || []).length === 0 && (
                      <tr><td colSpan="5" className="p-4 text-center text-slate-500">No items</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="bg-slate-800/60 rounded-lg p-4 space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-300"><span>Subtotal</span><span>₹{(viewBill.subtotal || viewBill.total_amount || 0).toLocaleString()}</span></div>
                {viewBill.discount ? <div className="flex justify-between text-slate-300"><span>Discount</span><span>-₹{viewBill.discount.toLocaleString()}</span></div> : null}
                {viewBill.gst_amount ? <div className="flex justify-between text-slate-300"><span>GST ({viewBill.gst_percent || 0}%)</span><span>₹{viewBill.gst_amount.toLocaleString()}</span></div> : null}
                <div className="flex justify-between text-white font-bold text-base border-t border-slate-600 pt-2 mt-1"><span>Total (BV)</span><span>₹{(viewBill.total_amount || 0).toLocaleString()}</span></div>
                {viewBill.total_pv ? <div className="flex justify-between text-[#D4AF37]"><span>Total Point Value</span><span>{viewBill.total_pv} PV</span></div> : null}
                <div className="flex justify-between text-green-400"><span>Commission ({viewBill.commission_percent || 0}%)</span><span>₹{(viewBill.commission_amount || 0).toLocaleString()}</span></div>
              </div>

              {viewBill.notes && <p className="text-xs text-slate-400">Note: {viewBill.notes}</p>}

              <div className="flex gap-2 pt-1">
                <Button onClick={() => exportBill(viewBill)} className="flex-1 bg-[#D4AF37] text-black hover:bg-[#c5a02f]" data-testid="export-bill-btn">
                  <Download className="w-4 h-4 mr-2" /> Export / Print
                </Button>
                <Button onClick={() => { const s = viewBill; setViewBill(null); openEditModal(s); }} variant="outline" className="border-slate-600 text-white" data-testid="view-edit-bill-btn">
                  Edit
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


// Stock Transfers Management

// Daily Reports Page - Auto-generated 9PM IST reports
function DailyReportsPage() {
  const { token } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [report, setReport] = useState(null);
  const [reportsList, setReportsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const fetchReport = async (date) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/daily-reports?date=${date}`, { headers: { Authorization: `Bearer ${token}` } });
      setReport(res.data);
    } catch (err) { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };

  const fetchReportsList = async () => {
    try {
      const res = await axios.get(`${API}/admin/daily-reports/list?limit=30`, { headers: { Authorization: `Bearer ${token}` } });
      setReportsList(res.data || []);
    } catch (err) { console.error(err); }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/daily-reports/generate?date=${selectedDate}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Report generated for ${selectedDate}`);
      setReport(res.data.report);
      fetchReportsList();
    } catch (err) { toast.error("Generation failed"); }
    finally { setGenerating(false); }
  };

  useEffect(() => { fetchReport(selectedDate); fetchReportsList(); }, []);
  useEffect(() => { fetchReport(selectedDate); }, [selectedDate]);

  return (
    <div className="space-y-6" data-testid="daily-reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Daily Reports (9PM)</h2>
          <p className="text-slate-400 text-sm">Auto-generated every day at 9:00 PM IST</p>
        </div>
        <div className="flex items-center gap-3">
          <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
            className="bg-slate-800 border-slate-600 text-white w-44" data-testid="report-date-picker" />
          <Button onClick={generateReport} disabled={generating} className="bg-[#D4AF37] hover:bg-[#B5952F] text-black" data-testid="generate-report-btn">
            {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-2">{generating ? "Generating..." : "Generate Now"}</span>
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#D4AF37]"></div></div>
      ) : report ? (
        <div className="space-y-6">
          {/* Top Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-slate-900/80 border-slate-700/50">
              <CardContent className="p-5 text-center">
                <Calendar className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{report.date}</p>
                <p className="text-xs text-slate-400">Report Date</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/80 border-slate-700/50">
              <CardContent className="p-5 text-center">
                <Receipt className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{report.total_bills || 0}</p>
                <p className="text-xs text-slate-400">Total Bills</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/80 border-slate-700/50">
              <CardContent className="p-5 text-center">
                <DollarSign className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-400">₹{(report.total_revenue || 0).toLocaleString()}</p>
                <p className="text-xs text-slate-400">Total Revenue</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/80 border-slate-700/50">
              <CardContent className="p-5 text-center">
                <TrendingUp className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-purple-400">₹{(report.total_commission || 0).toLocaleString()}</p>
                <p className="text-xs text-slate-400">Total Commission</p>
              </CardContent>
            </Card>
          </div>

          {report.generated_at && (
            <p className="text-xs text-slate-500 text-right">Generated: {report.generated_at_ist || report.generated_at}</p>
          )}

          {/* Franchise-wise Breakdown */}
          {report.franchises && report.franchises.length > 0 ? (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Franchise Breakdown</h3>
              {report.franchises.map((franchise, idx) => (
                <Card key={idx} className="bg-slate-900/80 border-slate-700/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#B5952F] rounded-lg flex items-center justify-center">
                          <Store className="w-5 h-5 text-black" />
                        </div>
                        <div>
                          <span className="text-base">{franchise.franchise_name}</span>
                          <p className="text-xs text-slate-400 font-normal">{franchise.franchise_code}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-400">{franchise.total_bills} bills</span>
                        <span className="text-slate-400">Direct: {franchise.direct_sales || 0}</span>
                        <span className="text-slate-400">Member: {franchise.member_sales || 0}</span>
                        <span className="text-green-400 font-bold text-lg">₹{(franchise.total_revenue || 0).toLocaleString()}</span>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-700">
                            <th className="text-left py-2 px-3 text-slate-400 font-medium">Bill #</th>
                            <th className="text-left py-2 px-3 text-slate-400 font-medium">Type</th>
                            <th className="text-left py-2 px-3 text-slate-400 font-medium">Customer</th>
                            <th className="text-left py-2 px-3 text-slate-400 font-medium">Items</th>
                            <th className="text-left py-2 px-3 text-slate-400 font-medium">Payment</th>
                            <th className="text-right py-2 px-3 text-slate-400 font-medium">Amount</th>
                            <th className="text-right py-2 px-3 text-slate-400 font-medium">Commission</th>
                          </tr>
                        </thead>
                        <tbody>
                          {franchise.bills.map((bill, bIdx) => (
                            <tr key={bIdx} className="border-b border-slate-800 hover:bg-slate-800/50">
                              <td className="py-2 px-3 font-mono text-[#D4AF37] text-xs">{bill.bill_number || "—"}</td>
                              <td className="py-2 px-3">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${bill.sale_type === 'member' ? 'bg-purple-500/20 text-purple-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                                  {bill.sale_type}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-white">{bill.customer_name || "—"} {bill.member_id ? <span className="text-slate-400 text-xs">({bill.member_id})</span> : ""}</td>
                              <td className="py-2 px-3 text-slate-300">{bill.products?.length || 0} item(s)</td>
                              <td className="py-2 px-3 text-slate-300 capitalize">{bill.payment_mode}</td>
                              <td className="py-2 px-3 text-right text-green-400 font-medium">₹{(bill.total_amount || 0).toLocaleString()}</td>
                              <td className="py-2 px-3 text-right text-slate-400">₹{(bill.commission_amount || 0).toFixed(0)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-slate-600">
                            <td colSpan="5" className="py-2 px-3 text-right font-semibold text-slate-300">Total</td>
                            <td className="py-2 px-3 text-right font-bold text-green-400">₹{(franchise.total_revenue || 0).toLocaleString()}</td>
                            <td className="py-2 px-3 text-right font-bold text-purple-400">₹{(franchise.total_commission || 0).toFixed(0)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-slate-900/80 border-slate-700/50">
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No franchise bills on {report.date}</p>
                <p className="text-slate-500 text-sm mt-1">Bills will appear here after they are created for this date</p>
              </CardContent>
            </Card>
          )}

          {/* Past Reports List */}
          {reportsList.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-white">Past Reports</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {reportsList.map((r, idx) => (
                  <button key={idx} onClick={() => setSelectedDate(r.date)}
                    className={`text-left p-4 rounded-xl border transition-all ${selectedDate === r.date ? 'bg-[#D4AF37]/10 border-[#D4AF37]/50' : 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600'}`}
                    data-testid={`past-report-${r.date}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-white font-medium">{r.date}</span>
                      <span className="text-green-400 font-bold">₹{(r.total_revenue || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                      <span>{r.total_bills} bills</span>
                      <span>{r.franchise_count || 0} franchise(s)</span>
                      <span>Comm: ₹{(r.total_commission || 0).toFixed(0)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <Card className="bg-slate-900/80 border-slate-700/50">
          <CardContent className="py-16 text-center">
            <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">No report for {selectedDate}</p>
            <p className="text-slate-500 text-sm mt-2">Click "Generate Now" to create a report for this date</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function FranchiseSettlementsManagement() {
  const { token } = useAuth();
  const [overview, setOverview] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending"); // pending | history
  const [settling, setSettling] = useState(null); // franchise being settled
  const [notes, setNotes] = useState("");

  const fetchAll = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [ovRes, histRes] = await Promise.all([
        axios.get(`${API}/admin/franchise-settlements`, { headers }),
        axios.get(`${API}/admin/franchise-settlements/history`, { headers })
      ]);
      setOverview(ovRes.data || []);
      setHistory(histRes.data || []);
    } catch (err) { toast.error("Failed to load settlements"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const doSettle = async (franchiseId) => {
    setSettling(franchiseId);
    try {
      await axios.post(`${API}/admin/franchise-settlements/settle`, { franchise_id: franchiseId, notes }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Settlement recorded");
      setNotes("");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to settle"); }
    finally { setSettling(null); }
  };

  const pending = overview.filter(o => o.unsettled_count > 0);

  return (
    <div data-testid="franchise-settlements-management" className="space-y-4">
      <h2 className="text-2xl font-bold text-white flex items-center gap-2"><Wallet className="w-6 h-6 text-[#D4AF37]" /> Franchise Settlements</h2>
      <p className="text-sm text-slate-400">Cash stays with the franchise; UPI & Card go to Head Office. Commission is adjusted against the franchise's cash. Settle weekly (Saturday).</p>

      <div className="flex gap-2">
        <Button onClick={() => setTab("pending")} variant={tab === "pending" ? "default" : "outline"} className={tab === "pending" ? "bg-[#D4AF37] text-black" : "border-slate-600 text-white"} data-testid="settlements-pending-tab">Pending ({pending.length})</Button>
        <Button onClick={() => setTab("history")} variant={tab === "history" ? "default" : "outline"} className={tab === "history" ? "bg-[#D4AF37] text-black" : "border-slate-600 text-white"} data-testid="settlements-history-tab">History ({history.length})</Button>
      </div>

      {loading ? <p className="text-slate-400 text-center py-10">Loading...</p> : tab === "pending" ? (
        pending.length === 0 ? (
          <div className="text-center py-12 text-slate-500"><Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No pending settlements</p></div>
        ) : (
          <div className="space-y-3">
            {pending.map(o => (
              <Card key={o.franchise_id} className="bg-slate-900/80 border-slate-700/50" data-testid={`settlement-row-${o.franchise_id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="text-white font-semibold">{o.franchise_name} <span className="text-slate-500 text-sm">({o.franchise_code})</span></p>
                      <p className="text-xs text-slate-400">{o.unsettled_count} unsettled bills</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400">{o.direction === "franchise_to_ho" ? "Franchise pays HO" : "HO pays Franchise"}</p>
                      <p className={`text-2xl font-bold ${o.direction === "franchise_to_ho" ? "text-amber-400" : "text-green-400"}`}>₹{o.net_amount.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                    <div className="bg-slate-800/50 rounded p-2"><p className="text-[11px] text-slate-400">Cash (held)</p><p className="text-sm font-bold text-amber-400">₹{o.cash_collected.toLocaleString()}</p></div>
                    <div className="bg-slate-800/50 rounded p-2"><p className="text-[11px] text-slate-400">UPI (to HO)</p><p className="text-sm font-bold text-cyan-400">₹{o.upi_collected.toLocaleString()}</p></div>
                    <div className="bg-slate-800/50 rounded p-2"><p className="text-[11px] text-slate-400">Card (to HO)</p><p className="text-sm font-bold text-cyan-400">₹{o.card_collected.toLocaleString()}</p></div>
                    <div className="bg-slate-800/50 rounded p-2"><p className="text-[11px] text-slate-400">Commission</p><p className="text-sm font-bold text-green-400">₹{o.total_commission.toLocaleString()}</p></div>
                  </div>
                  <div className="flex gap-2">
                    <Input value={settling === o.franchise_id ? notes : ""} onChange={e => setNotes(e.target.value)} onFocus={() => setNotes("")} placeholder="Settlement note (optional)" className="bg-slate-800 border-slate-600 text-white flex-1" data-testid={`settle-notes-${o.franchise_id}`} />
                    <Button onClick={() => doSettle(o.franchise_id)} disabled={settling === o.franchise_id} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`settle-btn-${o.franchise_id}`}>
                      {settling === o.franchise_id ? "Settling..." : "Mark Settled"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : (
        history.length === 0 ? (
          <div className="text-center py-12 text-slate-500"><Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No settlement history</p></div>
        ) : (
          <div className="space-y-2">
            {history.map(s => (
              <Card key={s.id} className="bg-slate-900/80 border-slate-700/50">
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-white font-medium">{s.franchise_name} <span className="text-slate-500 text-sm">({s.franchise_code})</span></p>
                    <p className="text-xs text-slate-400">{s.settled_at?.split("T")[0]} | Cash ₹{(s.cash_collected||0).toLocaleString()} | Commission ₹{(s.total_commission||0).toLocaleString()} | {s.unsettled_count} bills</p>
                    {s.notes && <p className="text-xs text-slate-500 italic">{s.notes}</p>}
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${s.direction === "franchise_to_ho" ? "text-amber-400" : "text-green-400"}`}>₹{(s.net_amount||0).toLocaleString()}</p>
                    <p className="text-[11px] text-slate-500">{s.direction === "franchise_to_ho" ? "paid to HO" : "paid by HO"}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function StockOrdersManagement() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);   // order being viewed/edited
  const [editItems, setEditItems] = useState([]);
  const [adminNotes, setAdminNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [stockMap, setStockMap] = useState({});  // product_id -> available stock at head office

  const fetchOrders = async () => {
    try {
      const [ordersRes, catRes] = await Promise.all([
        axios.get(`${API}/admin/stock-orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products?include_internal=true`)
      ]);
      setOrders(ordersRes.data || []);
      const map = {};
      (catRes.data || []).forEach(p => { map[p.id] = p.stock; });
      setStockMap(map);
    } catch (err) { toast.error("Failed to load orders"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchOrders(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openOrder = (o) => {
    setSelected(o);
    setEditItems((o.products || []).map(p => ({ ...p })));
    setAdminNotes(o.admin_notes || "");
  };

  const editTotal = editItems.reduce((s, p) => s + (Number(p.quantity) || 0) * (Number(p.unit_price) || 0), 0);

  const setQty = (idx, qty) => {
    setEditItems(editItems.map((p, i) => i === idx ? { ...p, quantity: Math.max(0, parseInt(qty) || 0) } : p));
  };

  const removeItem = (idx) => setEditItems(editItems.filter((_, i) => i !== idx));

  const saveOrder = async (newStatus) => {
    setSaving(true);
    try {
      const payload = { products: editItems.filter(p => p.quantity > 0), admin_notes: adminNotes };
      if (newStatus) payload.status = newStatus;
      await axios.put(`${API}/admin/stock-orders/${selected.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newStatus ? `Order ${newStatus}` : "Order updated");
      setSelected(null);
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update order"); }
    finally { setSaving(false); }
  };

  const filtered = statusFilter === "all" ? orders : orders.filter(o => o.status === statusFilter);
  const statusColor = (s) => s === "approved" ? "bg-green-500/20 text-green-400" : s === "rejected" ? "bg-red-500/20 text-red-400" : s === "fulfilled" ? "bg-purple-500/20 text-purple-400" : "bg-amber-500/20 text-amber-400";

  return (
    <div data-testid="stock-orders-management" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2"><ShoppingCart className="w-6 h-6 text-[#D4AF37]" /> Franchise Stock Orders</h2>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 bg-slate-800 border-slate-600 text-white" data-testid="order-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="fulfilled">Fulfilled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-slate-400 text-center py-10">Loading orders...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500"><ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No stock orders</p></div>
      ) : (
        <div className="space-y-2">
          {filtered.map(o => (
            <Card key={o.id} onClick={() => openOrder(o)} className="bg-slate-900/80 border-slate-700/50 hover:border-[#D4AF37]/50 transition-colors cursor-pointer" data-testid={`admin-order-${o.id}`}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-cyan-400">{o.order_number}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor(o.status)}`}>{o.status}</span>
                  </div>
                  <p className="text-sm text-white mt-1">{o.franchise_name} <span className="text-slate-500">({o.franchise_code})</span></p>
                  <p className="text-xs text-slate-400">{o.products?.length} item(s) | {o.created_at?.split("T")[0]}</p>
                </div>
                <p className="text-lg font-bold text-green-400">₹{(o.total_value || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Order Detail / Edit Modal */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-order-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-white"><ShoppingCart className="w-5 h-5 text-[#D4AF37]" /> {selected.order_number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-slate-800/60 rounded-lg p-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-slate-400">Franchise</span><span className="text-white">{selected.franchise_name} ({selected.franchise_code})</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Date</span><span className="text-white">{selected.created_at?.split("T")[0]}</span></div>
                  <div className="flex justify-between items-center"><span className="text-slate-400">Status</span><span className={`text-xs px-2 py-0.5 rounded-full ${statusColor(selected.status)}`}>{selected.status}</span></div>
                  {selected.notes && <div className="flex justify-between"><span className="text-slate-400">Franchise Note</span><span className="text-white italic">{selected.notes}</span></div>}
                </div>

                <div>
                  <p className="text-xs text-slate-400 mb-2">Items — adjust quantity or remove based on availability</p>
                  <div className="space-y-1 max-h-[260px] overflow-y-auto">
                    {editItems.length === 0 && <p className="text-xs text-amber-400 py-2">All items removed. Saving will empty this order.</p>}
                    {editItems.map((p, idx) => {
                      const avail = stockMap[p.product_id];
                      const reqQty = Number(p.quantity) || 0;
                      const overStock = avail !== undefined && reqQty > avail;
                      return (
                      <div key={idx} className={`flex items-center gap-2 rounded p-2 ${overStock ? 'bg-red-500/10 border border-red-500/30' : 'bg-slate-800/40'}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-white truncate">{p.product_name}</p>
                          <p className="text-xs text-slate-400">₹{p.unit_price} each = ₹{(reqQty * (Number(p.unit_price)||0)).toLocaleString()}</p>
                          <p className={`text-[11px] ${overStock ? 'text-red-400 font-semibold' : 'text-slate-500'}`}>
                            Available at HO: {avail === undefined ? 'N/A' : avail}{overStock ? ` — only ${avail} in stock!` : ''}
                          </p>
                        </div>
                        <Input type="number" value={p.quantity} onChange={e => setQty(idx, e.target.value)} className="w-20 bg-slate-800 border-slate-600 text-white text-center" data-testid={`admin-order-qty-${idx}`} />
                        <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-300 shrink-0" title="Remove (unavailable)" data-testid={`admin-order-remove-${idx}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex justify-between text-lg font-bold border-t border-slate-700 pt-2">
                  <span className="text-[#D4AF37]">Order Total</span>
                  <span className="text-green-400" data-testid="admin-order-total">₹{editTotal.toLocaleString()}</span>
                </div>

                <div>
                  <label className="text-xs text-slate-400">Admin / Head Office Note</label>
                  <Input value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="Add a note for the franchise..." className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="admin-order-notes" />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={() => saveOrder("approved")} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="approve-order-btn">Approve</Button>
                  <Button onClick={() => saveOrder("rejected")} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="reject-order-btn">Reject</Button>
                  <Button onClick={() => saveOrder("fulfilled")} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="fulfill-order-btn">Mark Fulfilled</Button>
                  <Button onClick={() => saveOrder(null)} disabled={saving} variant="outline" className="border-slate-600 text-white" data-testid="save-order-btn">Save Changes</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StockTransfersManagement() {
  const { token } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedFranchise, setSelectedFranchise] = useState("");
  const [transferProducts, setTransferProducts] = useState([]);
  const [notes, setNotes] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [transferStart, setTransferStart] = useState("");
  const [transferEnd, setTransferEnd] = useState("");
  const [activeTab, setActiveTab] = useState("catalog"); // catalog or custom
  const [customItem, setCustomItem] = useState({ name: "", price: "", quantity: 1, point_value: "" });
  const [productSearch, setProductSearch] = useState("");

  const fetchData = async () => {
    try {
      const [transfersRes, franchisesRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/stock-transfers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/franchises`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products?include_internal=true`)  // Include internal products for transfers
      ]);
      setTransfers(transfersRes.data || []);
      setFranchises(franchisesRes.data || []);
      setProducts(productsRes.data || []);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const addProduct = (product) => {
    const existing = transferProducts.find(p => p.product_id === product.id);
    if (existing) {
      setTransferProducts(transferProducts.map(p =>
        p.product_id === product.id ? { ...p, quantity: p.quantity + 1 } : p
      ));
    } else {
      setTransferProducts([...transferProducts, {
        product_id: product.id,
        product_name: product.name,
        unit_price: product.price,
        point_value: product.point_value || 0,
        quantity: 1,
        is_custom: false
      }]);
    }
  };

  const addCustomItem = () => {
    if (!customItem.name || !customItem.price) {
      toast.error("Please enter item name and price");
      return;
    }
    const customId = `custom_${Date.now()}`;
    setTransferProducts([...transferProducts, {
      product_id: customId,
      product_name: customItem.name,
      unit_price: parseFloat(customItem.price),
      point_value: parseFloat(customItem.point_value) || 0,
      quantity: parseInt(customItem.quantity) || 1,
      is_custom: true
    }]);
    setCustomItem({ name: "", price: "", quantity: 1, point_value: "" });
    toast.success("Custom item added!");
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      setTransferProducts(transferProducts.filter(p => p.product_id !== productId));
    } else {
      setTransferProducts(transferProducts.map(p =>
        p.product_id === productId ? { ...p, quantity } : p
      ));
    }
  };

  const removeProduct = (productId) => {
    setTransferProducts(transferProducts.filter(p => p.product_id !== productId));
  };

  const handleCreate = async () => {
    if (!selectedFranchise) {
      toast.error("Please select a franchise");
      return;
    }
    if (transferProducts.length === 0) {
      toast.error("Please add products to transfer");
      return;
    }

    try {
      await axios.post(`${API}/admin/stock-transfers`, {
        franchise_id: selectedFranchise,
        products: transferProducts,
        notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Stock transfer created successfully!");
      setShowCreateModal(false);
      setSelectedFranchise("");
      setTransferProducts([]);
      setNotes("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create transfer");
    }
  };

  const getTotalValue = () => {
    return transferProducts.reduce((sum, p) => sum + (p.unit_price * p.quantity), 0);
  };

  const transferDate = (t) => (t.created_at || "").split("T")[0];
  const filteredTransfers = transfers.filter(t => {
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    const d = transferDate(t);
    if (transferStart && d < transferStart) return false;
    if (transferEnd && d > transferEnd) return false;
    return true;
  });

  const exportTransfersCSV = () => {
    if (!filteredTransfers.length) { toast.error("No transfers to export"); return; }
    const headers = ["Date", "Franchise", "Franchise Code", "Status", "Items", "Products", "Total Value", "Notes"];
    const esc = (v) => `"${(v === null || v === undefined ? "" : String(v)).replace(/"/g, '""')}"`;
    const rows = filteredTransfers.map(t => [
      transferDate(t),
      t.franchise_name || "",
      t.franchise_code || "",
      t.status || "",
      (t.products || []).length,
      (t.products || []).map(p => `${p.product_name} x${p.quantity}`).join("; "),
      t.total_value || 0,
      (t.notes || "").replace(/\n/g, " ")
    ].map(esc).join(","));
    const totalVal = filteredTransfers.reduce((a, t) => a + (t.total_value || 0), 0);
    const totalRow = ["TOTAL", "", "", "", filteredTransfers.length, "", totalVal, ""].map(esc).join(",");
    const csv = [headers.map(esc).join(","), ...rows, totalRow].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const rangeLabel = (transferStart || transferEnd) ? `_${transferStart || "start"}_to_${transferEnd || "end"}` : "";
    link.href = URL.createObjectURL(blob);
    link.download = `stock-transfers${rangeLabel}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filteredTransfers.length} transfer(s) to CSV`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "bg-amber-500/20 text-amber-400";
      case "confirmed": return "bg-green-500/20 text-green-400";
      case "cancelled": return "bg-red-500/20 text-red-400";
      default: return "bg-slate-500/20 text-slate-400";
    }
  };

  return (
    <div data-testid="stock-transfers-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Truck className="w-7 h-7 text-[#D4AF37]" />
            Stock Transfers
          </h1>
          <p className="text-slate-400 text-sm mt-1">Transfer stock from Head Office to franchises</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={exportTransfersCSV}
            variant="outline"
            className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/10"
            data-testid="export-transfers-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
            disabled={franchises.length === 0}
            data-testid="create-transfer-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Transfer
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6 items-center">
        {["all", "pending", "confirmed", "cancelled"].map((status) => (
          <Button
            key={status}
            variant={statusFilter === status ? "default" : "outline"}
            onClick={() => setStatusFilter(status)}
            className={statusFilter === status ? "bg-[#D4AF37] text-black" : "border-slate-700 text-slate-300"}
            size="sm"
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Button>
        ))}
        <div className="flex items-center gap-1 ml-2">
          <span className="text-xs text-slate-500">Range</span>
          <Input type="date" value={transferStart} onChange={e => setTransferStart(e.target.value)}
            className="bg-slate-800 border-slate-600 text-white w-40 h-9" data-testid="transfer-start-date" title="From date" />
          <span className="text-xs text-slate-500">to</span>
          <Input type="date" value={transferEnd} onChange={e => setTransferEnd(e.target.value)}
            className="bg-slate-800 border-slate-600 text-white w-40 h-9" data-testid="transfer-end-date" title="To date" />
          {(transferStart || transferEnd) && (
            <Button variant="outline" size="sm" className="border-slate-600 text-slate-300"
              onClick={() => { setTransferStart(""); setTransferEnd(""); }} data-testid="clear-transfer-dates-btn">Clear</Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
          <CardContent className="p-4">
            <p className="text-blue-400 text-xs font-medium">Total Transfers</p>
            <p className="text-2xl font-bold text-white">{transfers.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/30">
          <CardContent className="p-4">
            <p className="text-amber-400 text-xs font-medium">Pending</p>
            <p className="text-2xl font-bold text-white">{transfers.filter(t => t.status === "pending").length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
          <CardContent className="p-4">
            <p className="text-green-400 text-xs font-medium">Confirmed</p>
            <p className="text-2xl font-bold text-white">{transfers.filter(t => t.status === "confirmed").length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
          <CardContent className="p-4">
            <p className="text-purple-400 text-xs font-medium">Total Value</p>
            <p className="text-2xl font-bold text-white">₹{transfers.reduce((sum, t) => sum + (t.total_value || 0), 0).toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Transfers List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37] mx-auto"></div>
        </div>
      ) : filteredTransfers.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-12 text-center">
            <Truck className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No stock transfers found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredTransfers.map((transfer) => (
            <Card key={transfer.id} className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-white">{transfer.franchise_name}</h3>
                      <Badge className="text-xs font-mono">{transfer.franchise_code}</Badge>
                      <Badge className={getStatusColor(transfer.status)}>{transfer.status}</Badge>
                    </div>
                    <p className="text-sm text-slate-400">
                      {transfer.products?.length || 0} products • ₹{(transfer.total_value || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(transfer.created_at).toLocaleString()}
                    </p>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {transfer.products?.map((p, idx) => (
                      <Badge key={idx} variant="outline" className="border-slate-600 text-slate-300">
                        {p.product_name} × {p.quantity}
                      </Badge>
                    ))}
                  </div>
                </div>
                {transfer.notes && (
                  <p className="text-sm text-slate-400 mt-3 pt-3 border-t border-slate-700">
                    <span className="text-slate-500">Note:</span> {transfer.notes}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Transfer Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2 text-xl">
              <Truck className="w-6 h-6 text-[#D4AF37]" />
              Create Stock Transfer
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Select Franchise */}
            <div>
              <Label className="text-slate-400">Select Franchise *</Label>
              <Select value={selectedFranchise} onValueChange={setSelectedFranchise}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue placeholder="Select a franchise..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {franchises.filter(f => f.is_active).map((f) => (
                    <SelectItem key={f.id} value={f.id} className="text-white">
                      {f.name} ({f.franchise_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Products Selection */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                {/* Tabs for Catalog vs Custom */}
                <div className="flex gap-2 mb-3">
                  <Button
                    type="button"
                    size="sm"
                    variant={activeTab === "catalog" ? "default" : "outline"}
                    onClick={() => setActiveTab("catalog")}
                    className={activeTab === "catalog" ? "bg-[#D4AF37] text-black" : "border-slate-600 text-slate-300"}
                  >
                    <Package className="w-4 h-4 mr-1" /> Catalog Products
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={activeTab === "custom" ? "default" : "outline"}
                    onClick={() => setActiveTab("custom")}
                    className={activeTab === "custom" ? "bg-green-600 text-white" : "border-slate-600 text-slate-300"}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Custom Item
                  </Button>
                </div>

                {activeTab === "catalog" ? (
                  <>
                    <Label className="text-slate-400 mb-2 block">Available Products (Click to Add)</Label>
                    {/* Product search */}
                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        placeholder="Search product by name..."
                        className="bg-slate-800 border-slate-700 text-white pl-9"
                        data-testid="transfer-product-search"
                      />
                    </div>
                    <div className="bg-slate-800 rounded-lg max-h-96 overflow-y-auto">
                      {products
                        .filter((product) => product.name.toLowerCase().includes(productSearch.toLowerCase()))
                        .map((product) => (
                        <div
                          key={product.id}
                          className="flex items-center justify-between p-3 border-b border-slate-700 last:border-0 hover:bg-slate-700/50 cursor-pointer"
                          onClick={() => addProduct(product)}
                          data-testid={`transfer-product-${product.id}`}
                        >
                          <div>
                            <p className="text-white text-sm font-medium flex items-center gap-2">
                              {product.name}
                              {product.is_internal && <span className="text-xs bg-amber-600/30 text-amber-400 px-1.5 rounded">Internal</span>}
                            </p>
                            <p className="text-slate-400 text-xs">₹{product.price}<span className="text-purple-400 ml-2">{product.point_value || 0} PV</span></p>
                          </div>
                          <Plus className="w-5 h-5 text-[#D4AF37]" />
                        </div>
                      ))}
                      {products.filter((product) => product.name.toLowerCase().includes(productSearch.toLowerCase())).length === 0 && (
                        <p className="text-slate-500 text-sm text-center py-6" data-testid="transfer-no-products">No products match "{productSearch}"</p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <Label className="text-slate-400 mb-2 block">Add Custom Item</Label>
                    <div className="bg-slate-800 rounded-lg p-4 space-y-3">
                      <div>
                        <Label className="text-slate-500 text-xs">Item Name *</Label>
                        <Input
                          value={customItem.name}
                          onChange={(e) => setCustomItem({...customItem, name: e.target.value})}
                          placeholder="e.g., Packaging Materials"
                          className="bg-slate-700 border-slate-600 text-white mt-1"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-slate-500 text-xs">Price (₹) *</Label>
                          <Input
                            type="number"
                            value={customItem.price}
                            onChange={(e) => setCustomItem({...customItem, price: e.target.value})}
                            placeholder="0"
                            className="bg-slate-700 border-slate-600 text-white mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-500 text-xs">PV</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={customItem.point_value}
                            onChange={(e) => setCustomItem({...customItem, point_value: e.target.value})}
                            placeholder="0"
                            className="bg-slate-700 border-slate-600 text-white mt-1"
                            data-testid="custom-item-pv-input"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-500 text-xs">Quantity</Label>
                          <Input
                            type="number"
                            min="1"
                            value={customItem.quantity}
                            onChange={(e) => setCustomItem({...customItem, quantity: parseInt(e.target.value) || 1})}
                            className="bg-slate-700 border-slate-600 text-white mt-1"
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        onClick={addCustomItem}
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Plus className="w-4 h-4 mr-2" /> Add Custom Item
                      </Button>
                      <p className="text-xs text-slate-500">Custom items will be added to transfer but not saved to product catalog</p>
                    </div>
                  </>
                )}
              </div>

              <div>
                <Label className="text-slate-400 mb-2 block">Transfer Items ({transferProducts.length})</Label>
                <div className="bg-slate-800 rounded-lg max-h-96 overflow-y-auto">
                  {transferProducts.length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-8">Add products from catalog or custom items</p>
                  ) : (
                    transferProducts.map((item) => (
                      <div key={item.product_id} className="flex items-center justify-between p-3 border-b border-slate-700 last:border-0">
                        <div className="flex-1">
                          <p className="text-white text-sm flex items-center gap-2">
                            {item.product_name}
                            {item.is_custom && <span className="text-xs bg-green-600/30 text-green-400 px-1.5 rounded">Custom</span>}
                          </p>
                          <p className="text-slate-400 text-xs">₹{item.unit_price} × {item.quantity} = ₹{item.unit_price * item.quantity}<span className="text-purple-400 ml-2">{((item.point_value || 0) * item.quantity)} PV</span></p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateQuantity(item.product_id, parseInt(e.target.value) || 0)}
                            className="w-16 h-8 bg-slate-700 border-slate-600 text-white text-center"
                          />
                          <button
                            onClick={() => removeProduct(item.product_id)}
                            className="text-red-400 hover:text-red-300 p-1"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-slate-400">Notes</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional transfer notes..."
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>

            {/* Total */}
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4 flex items-center justify-between">
              <span className="text-[#D4AF37] font-medium">Total Transfer Value:</span>
              <span className="text-2xl font-bold text-[#D4AF37]">₹{getTotalValue().toLocaleString()}</span>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1 border-slate-700 text-slate-300">
                Cancel
              </Button>
              <Button 
                onClick={handleCreate} 
                disabled={!selectedFranchise || transferProducts.length === 0}
                className="flex-1 bg-[#D4AF37] hover:bg-[#B5952F] text-black"
              >
                <Truck className="w-4 h-4 mr-2" /> Create Transfer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Settings
// Members by Plan — filter member IDs by plan A/B/C/D and change plan inline
const PLAN_META = {
  A: { label: "Plan A", amount: "₹4,000", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/40" },
  B: { label: "Plan B", amount: "₹2,000", color: "bg-purple-500/20 text-purple-400 border-purple-500/40" },
  C: { label: "Plan C", amount: "₹1,000", color: "bg-blue-500/20 text-blue-400 border-blue-500/40" },
  D: { label: "Plan D", amount: "₹500", color: "bg-green-500/20 text-green-400 border-green-500/40" },
};

function MembersByPlan() {
  const { token } = useAuth();
  const [activePlan, setActivePlan] = useState("A");
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const limit = 25;

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ plan: activePlan, limit: String(limit), skip: String(page * limit) });
      if (search.trim()) params.append("search", search.trim());
      const res = await axios.get(`${API}/admin/members?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembers(res.data.members || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line
  }, [activePlan, page]);

  const changePlan = async (member, newPlan) => {
    if (newPlan === member.plan_type) return;
    setSavingId(member.member_id);
    try {
      await axios.put(`${API}/admin/members/${member.member_id}`, { plan_type: newPlan }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${member.member_id} moved to Plan ${newPlan}`);
      // Member no longer belongs to current plan tab — remove from list
      setMembers((prev) => prev.filter((m) => m.member_id !== member.member_id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change plan");
    } finally {
      setSavingId(null);
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div data-testid="members-by-plan">
      <div className="flex items-center gap-3 mb-6">
        <Layers className="w-6 h-6 text-[#D4AF37]" />
        <h1 className="text-2xl font-bold text-white">Members by Plan</h1>
      </div>

      {/* Plan Tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {["A", "B", "C", "D"].map((p) => (
          <button
            key={p}
            onClick={() => { setActivePlan(p); setPage(0); }}
            data-testid={`plan-tab-${p}`}
            className={`px-5 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
              activePlan === p ? PLAN_META[p].color : "bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700"
            }`}
          >
            {PLAN_META[p].label} <span className="opacity-70">({PLAN_META[p].amount})</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex gap-2 mb-4 max-w-md">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setPage(0); fetchMembers(); } }}
          placeholder="Search by ID, name, phone..."
          className="bg-slate-800 border-slate-700 text-white"
          data-testid="plan-search-input"
        />
        <Button onClick={() => { setPage(0); fetchMembers(); }} className="bg-[#D4AF37] text-black hover:bg-[#B5952F]">
          <Search className="w-4 h-4" />
        </Button>
      </div>

      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-lg flex items-center justify-between">
            <span>{PLAN_META[activePlan].label} Members</span>
            <Badge className={PLAN_META[activePlan].color} data-testid="plan-total-count">{total} members</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-slate-400 text-center py-8">Loading...</p>
          ) : members.length === 0 ? (
            <p className="text-slate-400 text-center py-8" data-testid="no-members-msg">No members in this plan.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">Member ID</TableHead>
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">Phone</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Change Plan</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((m) => (
                    <TableRow key={m.member_id} className="border-slate-800" data-testid={`plan-member-row-${m.member_id}`}>
                      <TableCell className="text-[#D4AF37] font-mono font-bold">{m.member_id}</TableCell>
                      <TableCell className="text-white">{m.name}</TableCell>
                      <TableCell className="text-slate-300">{m.phone || "—"}</TableCell>
                      <TableCell>
                        {m.is_active
                          ? <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                          : <Badge className="bg-yellow-500/20 text-yellow-400">Pending</Badge>}
                      </TableCell>
                      <TableCell>
                        <Select value={m.plan_type} onValueChange={(v) => changePlan(m, v)} disabled={savingId === m.member_id}>
                          <SelectTrigger className="w-40 h-9 bg-slate-800 border-slate-700 text-white" data-testid={`plan-select-${m.member_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="A">Plan A - ₹4,000</SelectItem>
                            <SelectItem value="B">Plan B - ₹2,000</SelectItem>
                            <SelectItem value="C">Plan C - ₹1,000</SelectItem>
                            <SelectItem value="D">Plan D - ₹500</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-slate-400 text-sm">Page {page + 1} of {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="border-slate-600 text-slate-300" data-testid="plan-prev-page">
                  <ChevronLeft className="w-4 h-4" /> Prev
                </Button>
                <Button variant="outline" size="sm" disabled={(page + 1) >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="border-slate-600 text-slate-300" data-testid="plan-next-page">
                  Next <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SettingsPage() {
  const { token } = useAuth();
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [changing, setChanging] = useState(false);

  const createAdmin = async () => {
    try {
      await axios.post(`${API}/admin/create?email=admin@achraj.com&password=admin123&name=Admin`);
      toast.success("Admin created: admin@achraj.com / admin123");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const changePassword = async () => {
    if (!pwd.current_password || !pwd.new_password) { toast.error("Fill all password fields"); return; }
    if (pwd.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pwd.new_password !== pwd.confirm_password) { toast.error("New passwords do not match"); return; }
    setChanging(true);
    try {
      await axios.post(`${API}/admin/change-password`,
        { current_password: pwd.current_password, new_password: pwd.new_password },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Password changed successfully! Use it on your next login.");
      setPwd({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setChanging(false);
    }
  };

  return (
    <div data-testid="admin-settings">
      <h1 className="text-2xl font-bold text-white mb-6">Settings</h1>

      {/* Change Password */}
      <Card className="bg-slate-900/50 border-slate-800 mb-6 max-w-lg">
        <CardHeader>
          <CardTitle className="text-white text-lg">Change My Password</CardTitle>
          <CardDescription className="text-slate-400">Update the password for your admin account.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-400 text-xs">Current Password</Label>
            <Input type="password" value={pwd.current_password}
              onChange={e => setPwd({ ...pwd, current_password: e.target.value })}
              placeholder="Enter current password"
              className="bg-slate-800 border-slate-600 text-white" data-testid="current-password-input" />
          </div>
          <div>
            <Label className="text-slate-400 text-xs">New Password</Label>
            <Input type="password" value={pwd.new_password}
              onChange={e => setPwd({ ...pwd, new_password: e.target.value })}
              placeholder="Enter new password (min 6 chars)"
              className="bg-slate-800 border-slate-600 text-white" data-testid="new-password-input" />
          </div>
          <div>
            <Label className="text-slate-400 text-xs">Confirm New Password</Label>
            <Input type="password" value={pwd.confirm_password}
              onChange={e => setPwd({ ...pwd, confirm_password: e.target.value })}
              placeholder="Re-enter new password"
              className="bg-slate-800 border-slate-600 text-white" data-testid="confirm-password-input" />
          </div>
          <Button onClick={changePassword} disabled={changing}
            className="bg-[#D4AF37] hover:bg-[#B5952F] text-black w-full" data-testid="change-password-btn">
            {changing ? "Updating..." : "Update Password"}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/50 border-slate-800 max-w-lg">
        <CardContent className="p-6 space-y-4">
          <Button onClick={createAdmin} className="bg-slate-700 hover:bg-slate-600 text-white">
            Create Admin Account
          </Button>
          <p className="text-slate-400 text-sm">Click to create default admin account if not exists.</p>
        </CardContent>
      </Card>
    </div>
  );
}

// Main Admin Dashboard
export default function AdminDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#0F172A]" data-testid="admin-dashboard">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-64">
        <header className="sticky top-0 z-30 bg-[#0F172A]/80 backdrop-blur-lg border-b border-slate-800 px-4 py-4">
          <div className="flex items-center justify-between">
            <button className="lg:hidden p-2 text-slate-400 hover:text-white" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-4 ml-auto">
              <div className="text-right">
                <p className="text-white font-medium">{user?.name}</p>
                <p className="text-xs text-[#D4AF37]">Admin</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B5952F] flex items-center justify-center">
                <span className="text-white font-bold">{user?.name?.charAt(0)}</span>
              </div>
            </div>
          </div>
        </header>

        <main className="p-6">
          <Routes>
            <Route index element={<Overview />} />
            <Route path="registration-requests" element={<RegistrationRequests />} />
            <Route path="franchise-activations" element={<FranchiseActivations />} />
            <Route path="payments" element={<PaymentApprovals />} />
            <Route path="orders" element={<OrdersManagement />} />
            <Route path="billing" element={<BillingManagement />} />
            <Route path="members" element={<MembersManagement />} />
            <Route path="members-by-plan" element={<MembersByPlan />} />
            <Route path="members-report" element={<MembersReport />} />
            <Route path="member-gaps" element={<MemberGapsReport />} />
            <Route path="repurchase" element={<RepurchaseManagement />} />
            <Route path="kyc" element={<KYCManagement />} />
            <Route path="inventory" element={<InventoryManagement />} />
            <Route path="offline-purchases" element={<OfflinePurchasesManagement />} />
            <Route path="categories" element={<CategoriesManagement />} />
            <Route path="products" element={<ProductsManagement />} />
            <Route path="subscriptions" element={<SubscriptionsManagement />} />
            <Route path="payouts" element={<PayoutsManagement />} />
            <Route path="franchises" element={<FranchiseManagement />} />
            <Route path="franchise-billing" element={<FranchiseBillingManagement />} />
            <Route path="daily-reports" element={<DailyReportsPage />} />
            <Route path="stock-transfers" element={<StockTransfersManagement />} />
            <Route path="stock-orders" element={<StockOrdersManagement />} />
            <Route path="settlements" element={<FranchiseSettlementsManagement />} />
            <Route path="settings" element={<SettingsPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
